/**
 * auto-captions pipeline — wires transcribe / emoji / translate / speakers / burn / export
 * to the blade UI. The blade exposes state, elements and helpers on window.__ac*.
 *
 * Loaded with `defer` from resources/views/tools/auto-captions.blade.php.
 */
(function(){
    'use strict';

    if (!window.__acState) {
        console.warn('[auto-captions] blade state not ready, pipeline aborted');
        return;
    }

    var state = window.__acState;
    var els = window.__acEls;
    var I18N = window.__acI18n || {};
    var toast = window.__acToast || function(m, k){ console.log('[ac]', k||'', m); };
    var rebuildLines = window.__acRebuildLines;
    var setExportsEnabled = window.__acSetExportsEnabled;

    /* Часы сторожей «нет прогресса N секунд» — ВИДИМОЕ время (timbrica-core.js
       __visibleTimeout / __visibleNow). Свёрнутую вкладку Android замораживает
       вместе с воркерами: распознавание стоит не потому, что сломалось, а
       потому, что ему не дают процессор. Стенной таймер после разморозки
       срабатывал сразу и убивал исправный прогон (обращение 22.09.2026: 49 мин
       в фоне → «Распознавание зависло» на первой же секунде после возврата).
       Скрытое время не считается; без ядра — прежние стенные часы. */
    function visTimeout(fn, ms){
        if (typeof window.__visibleTimeout === 'function') return window.__visibleTimeout(fn, ms);
        var h = setTimeout(fn, ms);
        return function(){ clearTimeout(h); };
    }
    function visNow(){
        return (typeof window.__visibleNow === 'function') ? window.__visibleNow() : Date.now();
    }

    /* Длина, которую можно предъявить гейту тарифа, — до всякой работы.
       Превью (<video>) бывает не получает метаданных вовсе, поэтому блейд
       при выборе файла читает длину из заголовка контейнера
       (state.durationProbe → AudioDecoderRobust.probeDuration). Обычно она
       готова задолго до нажатия; если ещё нет (медленный источник файла) —
       ждём ограниченно. 0 = не знаем, и тогда судит страховка по длине
       извлечённого звука. Нечисловое (Infinity у WebM из MediaRecorder) —
       тоже «не знаем», а не бесконечная длина. */
    var DURATION_PROBE_WAIT_MS = 8000;
    function knownDuration(){
        var cur = function(){ return (state.duration > 0 && isFinite(state.duration)) ? state.duration : 0; };
        if (cur() > 0) return Promise.resolve(cur());
        var p = state.durationProbe;
        if (!p || typeof p.then !== 'function') return Promise.resolve(0);
        var timer = null;
        var cap = new Promise(function(res){ timer = setTimeout(res, DURATION_PROBE_WAIT_MS); });
        return Promise.race([p, cap]).then(function(){ clearTimeout(timer); return cur(); },
                                           function(){ clearTimeout(timer); return cur(); });
    }

    /* ════════════════════════════════════════════════════════════
       PHASE 2 — TRANSCRIBE (FFmpeg audio extract + Whisper worker)
       ════════════════════════════════════════════════════════════ */
    window.__acTranscribe = async function(){
        if (!state.file) { toast(I18N.file_error || 'No video', 'error'); reportBlocked('no_file'); return; }
        // Повторное нажатие во время прогона — не второй прогон (двойной тап на
        // телефоне, согласие на модель + кнопка): останавливает только «Отмена».
        if (state.busy) { reportBlocked('busy_' + state.busy); return; }
        // Предполётная часть (ожидание длины, диалог стены) идёт ДО setBusy:
        // второе касание в эти секунды иначе запускало бы второй прогон.
        if (state.preflight) { reportBlocked('busy_preflight'); return; }
        if (!window.FFmpegProcessor) { toast('FFmpeg not loaded', 'error'); reportToolError('ffmpeg_not_loaded', new Error('ffmpeg_missing')); return; }
        // Hard SIMD stop BEFORE the Whisper model download (39–244 MB): the
        // whole lane (FFmpeg extract + ORT inference) requires WASM SIMD, and
        // ORT ≥1.19 ships no non-SIMD binary — a pre-2010 CPU fails at
        // compile guaranteed. Localized message + typed telemetry come from
        // the shared gate (device-capability-gate.js), one source of truth.
        if (window.DeviceCapabilityGate && window.DeviceCapabilityGate.requireSimd) {
            var _g = window.DeviceCapabilityGate.requireSimd('auto-captions');
            if (!_g.ok) { toast(_g.message, 'error'); return; }
        }
        // Потолок длины файла по тиру (config/tiers.php → max_duration_s):
        // общий гейт рисует стену и предлагает Премиум с ЧИСЛОМ из конфига.
        // Судим ЗДЕСЬ — до скачивания модели и извлечения звука, чтобы отказ
        // ничего не стоил (Фарид 09.09.2026: «и длительность у файла»).
        // Длина — из заголовка контейнера (state.durationProbe, блейд), а не
        // только из превью: см. knownDuration(). Не узнали — судим позже, по
        // длине извлечённого звука (durationGated = false).
        var durationGated = false;
        state.preflight = true;
        try {
            var preDur = await knownDuration();
            // Платная модель OpenAI — не под стеной длины тира: её минуты
            // покупаются токенами, потолок держит сервер (240 мин).
            if (preDur > 0 && !openAiChosen() && window.__toolLimit && typeof window.__toolLimit.checkDuration === 'function') {
                durationGated = true;
                // file: даёт стене резать запись на части ЗДЕСЬ ЖЕ (audio-parts.js,
                // видео — с картинкой), вместо ухода на /video-trim: оттуда за
                // две недели вернулись с текстом 10 из 56, а части на месте у
                // аудио-тулов доводят до результата 88 из 113 (замер 26.09).
                var durOk = await window.__toolLimit.checkDuration('auto-captions', preDur, {
                    splitUrl: '/' + (window.__LOCALE || 'en') + '/video-trim',
                    file: state.file,
                });
                if (!durOk) return;
            }
        } finally { state.preflight = false; }
        // Confirm before discarding user edits on re-transcribe.
        if (state.captionsGenerated && state.words && state.words.length) {
            var msg = I18N.regenerate_confirm || 'Regenerate captions? Your current edits will be lost.';
            if (typeof window.confirm === 'function' && !window.confirm(msg)) return;
        }
        try { window.__ac_partials_seen = 0; } catch(_) {}
        reportFeature('transcribe_start', {
            // Что СТОИТ в списке и что РЕАЛЬНО поедет — разные вещи, пока
            // согласие на русскую модель не дано. До 2026-08-24 писалось только
            // первое, и в телеметрии было 231 «прогон на GigaAM» против 25
            // настоящих. Теперь пишем оба: engine — то, что запустится.
            model: els.model.value,
            engine: openAiChosen() ? 'openai' : (els.model.value === 'gigaam-ru' && state.gigaamRequested) ? 'gigaam' : ('whisper:' + whisperModelSize()),
            lang: els.lang.value,
            preprocDenoise:    !!(els.preprocDenoise && els.preprocDenoise.checked),
            preprocIsolate:    !!(els.preprocIsolate && els.preprocIsolate.checked),
            useVad:            !!(els.vadToggle && els.vadToggle.checked),
            glossaryTerms:     countGlossaryTerms(),
            // Match reportToolComplete payload so funnel can correlate start↔end.
            fastMode:          !!(els.fastMode && els.fastMode.checked),
            advancedModelsOn:  !!(els.advancedToggle && els.advancedToggle.checked),
            isRegeneration:    !!state.captionsGenerated,
            // Откуда гейт знал длину: video (превью) / header (контейнер) /
            // '' — не знал до извлечения звука (тогда судит страховка ниже).
            dur_s:             Math.round(state.duration || 0),
            dur_src:           durationGated ? (state.durationSrc || '') : '',
        });

        /* Honest run clock. `proc_sec` (telemetry-tier0) starts at
           workspace-activated, so it also counts the minutes a user spends
           choosing a model — useless for answering "how long does the work
           take". runStartedAt starts HERE, at the click.
           __runElapsed lets the shared abandon emitter record how far into the
           work the user left: "left at 0:20" and "left at 4:00" are different
           problems and were indistinguishable. */
        state.runStartedAt = Date.now();
        startProgClock();
        state.loadDoneAt = 0;
        state.firstPartialAt = 0;
        holdScreenAwake('transcribe');   // первое живое слово на экране — время до него и есть «зависло или нет»
        window.__runElapsed = function(){
            return state.runStartedAt ? Math.round((Date.now() - state.runStartedAt) / 1000) : 0;
        };

        showProgress(els.tProg, els.tFill, els.tText, els.tPct);
        setBusy('transcribe');

        /* Run-generation token. Cancel / workspace-close / a new transcribe
           bumps state.runGen; this run checks the token after every long
           await and bails silently when it lost ownership. Without it the
           async chain OUTLIVED the cancel: workers were terminated but the
           function kept going — extraction finished post-close and runVad
           spawned a fresh zombie vadWorker into the cleaned-up state (caught
           by auto-captions-cancel.mjs once streaming extraction got faster). */
        var myGen = state.runGen = (state.runGen || 0) + 1;
        var lostRun = function(){ return state.runGen !== myGen; };

        // Capture the file BEFORE the first await — a cancel/close during
        // FFmpeg init nulls state.file while this run is suspended, and the
        // resumed code would deref null (caught as a bogus transcribe error).
        var srcFile = state.file;

        try {
            // 1) Init FFmpeg + extract mono 16 kHz PCM (optionally cleaned up via afftdn + bandpass)
            stageProg(0, I18N.transcribe_extracting, 0);
            await window.FFmpegProcessor.init(function(p){
                stageProg(Math.min(0.08, p * 0.08), I18N.transcribe_extracting, Math.round(p * 8));
            });
            if (lostRun()) return;

            // Audio-cleanup filter chain — runs on the audio extraction stage so the
            // PCM that reaches Whisper is already de-noised. afftdn = adaptive
            // frequency-domain noise reduction (FFmpeg built-in, no extra model).
            // The bandpass + soft compand bias the spectrum toward speech.
            var preprocActive = false;
            var afilter = [];
            // Phase C-3: AI denoise mode resolution. When user picked 'ai'
            // we SKIP afftdn at extract time and run DeepFilterNet3 on raw
            // Float32 PCM right after s16→f32 conversion below. Falls back
            // to 'fast' when DeepFilter unavailable (set by __acAIDenoise
            // capability gate in the blade).
            var aiDenoise = window.__acAIDenoise || null;
            var denoiseEnabled = !!(els.preprocDenoise && els.preprocDenoise.checked);
            var denoiseMode = denoiseEnabled && aiDenoise && typeof aiDenoise.getMode === 'function' ? aiDenoise.getMode() : 'fast';
            if (denoiseEnabled && denoiseMode === 'fast') {
                afilter.push('afftdn=nf=-25');
                preprocActive = true;
            }
            if (els.preprocIsolate && els.preprocIsolate.checked) {
                // Vocal emphasis: cut sub-bass and air, soft-compress dynamics.
                afilter.push('highpass=f=80', 'lowpass=f=8000', 'compand=attacks=0:points=-90/-90|-30/-15|0/-3');
                preprocActive = true;
            }
            if (preprocActive) {
                stageProg(0.05, I18N.preproc_running || 'Cleaning audio', 5);
                reportFeature('preproc_active', { denoise: denoiseEnabled, denoiseMode: denoiseMode, isolate: els.preprocIsolate && els.preprocIsolate.checked });
            }

            // Audio-extract progress rides on `ffmpeg-progress` (raw 0..1 number),
            // NOT `ffmpeg-status` (init phases only, no fraction) — same wiring
            // trap that froze the burn bar. Extract is fast so the frozen bar was
            // less visible here, but the fix keeps both exec bars honest.
            var extractProg = function(e){
                var d = e.detail;
                var prog = (typeof d === 'number') ? d
                         : (d && typeof d.progress === 'number') ? d.progress
                         : (d && typeof d.percent === 'number') ? d.percent / 100
                         : null;
                if (prog === null) return;
                var pct = Math.min(0.18, 0.08 + (prog || 0) * 0.10);
                stageProg(pct, I18N.transcribe_extracting, Math.round(pct * 100));
            };
            window.addEventListener('ffmpeg-progress', extractProg);
            var pcmResult;
            try {
                var extractArgs = ['-vn', '-ac', '1', '-ar', '16000', '-f', 's16le', '-acodec', 'pcm_s16le'];
                if (afilter.length) extractArgs.unshift('-af', afilter.join(','));
                // WORKERFS streaming (ffStream): FFmpeg reads the input lazily
                // via File.slice() — RAM bounded by codec state, not file size.
                // Container hint mirrors process()'s _fmtHints so extension-
                // ambiguous audio (m4a/aac/3gp/caf) still demuxes.
                var _ext = (srcFile.name || '').split('.').pop().toLowerCase();
                var _hint = { m4a: 'mp4', aac: 'aac', '3gp': '3gp', caf: 'caf' }[_ext];
                pcmResult = await ffStream(srcFile, extractArgs, 'audio.pcm', _hint ? ['-f', _hint] : null);
            } finally {
                window.removeEventListener('ffmpeg-progress', extractProg);
            }
            if (lostRun()) return;
            var pcmBlob = pcmResult && pcmResult.blob ? pcmResult.blob : pcmResult;
            // The extracted PCM is an internal intermediate, NOT a deliverable.
            // FFmpegProcessor wrapped it in URL.createObjectURL, which trips the
            // layout's blob auto-complete heuristic (app.blade.php) → a phantom
            // tool_complete fires 2 s later with the PCM byte-count as "output
            // size" (prod telemetry: size=6666542 = 208 s × 16000 × 2, NOT a
            // captioned video). That inflates the success funnel and fires even
            // when the user later cancels mid-transcribe. Opt the intermediate
            // out via the layout's skip-blob registry; the REAL completion is
            // reported explicitly by reportToolComplete() after the burn. The
            // create-time check already passed (FFmpegProcessor called
            // createObjectURL inside processStreaming), but the auto-complete
            // re-checks the skip-list when its 2 s debounce fires — and we
            // register well inside that window, synchronously after the await.
            try { if (typeof window.__skipBlobAutoTrack === 'function' && pcmBlob) window.__skipBlobAutoTrack(pcmBlob); } catch(_){}

            // Convert s16le → Float32 in [-1, 1]
            var ab = await pcmBlob.arrayBuffer();
            var i16 = new Int16Array(ab);

            // Страховка гейта длительности. Заголовок молчит у части файлов
            // (WebM из MediaRecorder без Duration, экзотические контейнеры), и
            // тогда длину впервые знает извлечённый звук — это точная длина
            // дорожки, которую и будем распознавать. Судим ею ДО шумодава,
            // детектора речи и скачивания модели: извлечение уже заплачено,
            // остальное — нет.
            var pcmSec = i16.length / 16000;
            if (pcmSec > 0 && !(state.duration > 0)) {
                state.duration = pcmSec;
                state.durationSrc = 'pcm';
                try { if (typeof window.__acRenderFileMeta === 'function') window.__acRenderFileMeta(); } catch (_) {}
            }
            if (!durationGated && pcmSec > 0 && !openAiChosen() && window.__toolLimit && typeof window.__toolLimit.checkDuration === 'function') {
                durationGated = true;
                reportFeature('duration_gate_late', { dur_s: Math.round(pcmSec) });
                var lateOk = await window.__toolLimit.checkDuration('auto-captions', pcmSec, {
                    splitUrl: '/' + (window.__LOCALE || 'en') + '/video-trim',
                    file: srcFile,
                });
                if (lostRun()) return;
                if (!lateOk) {
                    var walled = new Error('duration walled after extraction');
                    walled.errorType = 'duration_walled';
                    walled.expected = true;
                    throw walled;
                }
            }

            var f32 = new Float32Array(i16.length);
            for (var i = 0; i < i16.length; i++) f32[i] = i16[i] / 32768;

            // Phase C-3: AI denoise (DeepFilterNet3) on raw 16 kHz PCM.
            // Runs only when checkbox on AND mode === 'ai' AND module ready.
            // On any failure we fall back silently (afftdn was skipped at
            // extract because we expected AI to run; surface a soft toast).
            if (denoiseEnabled && denoiseMode === 'ai' && aiDenoise && typeof aiDenoise.isAvailable === 'function' && aiDenoise.isAvailable()) {
                stageProg(0.13, I18N.preproc_running || 'Cleaning audio', 13);
                reportFeature('preproc_ai_start', { sampleRate: 16000, samples: f32.length });
                try {
                    var aiStart = Date.now();
                    var cleaned = await aiDenoise.process(f32, 16000, function(r){
                        var pct = 0.13 + (r || 0) * 0.05;
                        stageProg(pct, I18N.preproc_running || 'Cleaning audio', Math.round(pct * 100));
                    });
                    if (cleaned && cleaned.length) {
                        f32 = cleaned;
                        preprocActive = true;
                        reportFeature('preproc_ai_done', { ms: Date.now() - aiStart, samples: cleaned.length });
                    }
                } catch (aiErr) {
                    console.warn('[auto-captions] AI denoise failed, falling back to fast:', aiErr && aiErr.message);
                    reportFeature('preproc_ai_failed', { reason: (aiErr && aiErr.message) || 'unknown' });
                    toast(I18N.preproc_ai_denoise_failed || 'AI denoise failed — using fast denoise instead.', 'warning');
                }
            }

            // Silence guard — Whisper on a fully-silent / no-audio-stream input
            // hallucinates loops like "Thank you for watching" or "В этом году"
            // (highest-prior tokens). RMS over the whole signal in [-1,1]:
            //   • voiced speech ~ 0.05-0.20 (≈ -26 to -14 dBFS)
            //   • room tone     ~ 0.005-0.02
            //   • absolute silence < 0.0005
            // Threshold 0.001 (≈ -60 dBFS) catches stripped-audio video files
            // + truly-silent inputs without flagging quiet podcasts.
            (function(){
                var sum = 0;
                // Subsample every 16 samples — at 16 kHz that's a 1 ms grid,
                // plenty of resolution and >> 10x faster than a full pass on
                // 2-hour files (~2 hours × 16 kHz × 4 B = 460 MB → ~30 MB at /16).
                for (var s = 0; s < f32.length; s += 16) sum += f32[s] * f32[s];
                var rms = Math.sqrt(sum / Math.ceil(f32.length / 16));
                if (rms < 0.001 || !f32.length) {
                    hideProgress(els.tProg);
                    var noAudio = I18N.transcribe_no_audio_stream || 'No audible audio detected in the file — nothing to transcribe.';
                    toast(noAudio, 'error');
                    reportToolError('no_audio_stream', new Error('rms=' + rms.toFixed(6) + ' samples=' + f32.length));
                    var silentErr = new Error(noAudio);
                    silentErr.errorType = 'no_audio_stream';
                    silentErr.expected = true;
                    throw silentErr;
                }
            })();

            // 1a-bis) Выровнять ТИХУЮ запись ДО детектора речи и ДО GigaAM.
            //     Замер 09.09.2026 (FLEURS ru, p90 −40 dBFS, эталон известен):
            //     Silero на 0.5 оставил 47.7 с речи из ~98 — GigaAM отдал 98 слов
            //     из 170, WER 48.8 %; тот же файл без VAD — 170 слов, 5.9 %.
            //     Правило и числа — asr-level.js (общее с серверным лейном и с
            //     воркером whisper, у которого своё выравнивание: второй проход
            //     по уже нормальному уровню попадает в мёртвую зону и ничего не
            //     меняет). Нормальный файл остаётся байт-в-байт прежним.
            state.inputLevel = null;
            try {
                if (window.__asrLevel && typeof window.__asrLevel.level === 'function') {
                    var lv = window.__asrLevel.level(f32);
                    state.inputLevel = lv;
                    if (lv && lv.applied) {
                        reportFeature('input_leveled', { gain_db: lv.gainDb, level_db: lv.levelDb, peak_db: lv.peakDb, limited: +((lv.limitedFrac || 0).toFixed(5)) });
                    }
                }
            } catch (lvErr) {
                console.warn('[auto-captions] input leveling skipped:', lvErr && lvErr.message);
            }

            // 1b) Optional VAD pre-segmentation. Skips silent intros/outros and
            //     shrinks the audio fed to Whisper to actual speech regions only.
            //     Saves both bandwidth-of-decoder time AND improves accuracy on long files.
            var vadInfo = null;
            if (els.vadToggle && els.vadToggle.checked) {
                stageProg(0.16, I18N.vad_running || 'Detecting speech', 16);
                onVadProgress = function(p){
                    var vp = 0.16 + Math.min(1, Math.max(0, (p || 0) / 100)) * 0.03;
                    stageProg(vp, I18N.vad_running || 'Detecting speech', Math.round(vp * 100));
                };
                try {
                    vadInfo = await runVad(f32);
                    if (vadInfo && vadInfo.segments && vadInfo.segments.length) {
                        // Tight-pack speech segments into a smaller PCM buffer.
                        var packed = packPcmBySegments(f32, vadInfo.segments, 16000);
                        f32 = packed.pcm;
                        state.vadOffsetMap = packed.offsetMap; // [{newStart,newEnd,origStart}] for time remap
                        var msg = (I18N.vad_done || 'Found :count segments (:secs s)')
                            .replace(':count', vadInfo.segments.length)
                            .replace(':secs', Math.round(vadInfo.totalSpeechSec));
                        toast(msg, 'info');
                        // Keep the speech/total split — the plausibility guard
                        // needs it to explain a thin transcript honestly ("we
                        // only found 10 s of speech in 5:14").
                        state.vadUsed = true;
                        state.vadSpeechSec = vadInfo.totalSpeechSec;
                        reportFeature('vad_segments', { count: vadInfo.segments.length, totalSpeechSec: Math.round(vadInfo.totalSpeechSec), totalDurSec: Math.round(vadInfo.totalDurSec) });
                    } else {
                        // VAD found nothing → `f32` is untouched and Whisper gets
                        // the WHOLE file. So the run did NOT use VAD, and the
                        // plausibility verdict must not blame it: saying "speech
                        // search threw almost everything away" and offering
                        // "transcribe the whole file" would send the user into a
                        // second identical wait for the identical result.
                        // (Reproduced on prod 2026-07-23 with a 5-minute file.)
                        toast(I18N.vad_skipped || 'No speech detected', 'warning');
                        reportFeature('vad_no_speech', {});
                        state.vadOffsetMap = null;
                        state.vadUsed = false;
                        state.vadSpeechSec = null;
                    }
                } catch (vadErr) {
                    // Same reasoning, plus: these fields survive across re-runs,
                    // so leaving a previous run's values in place would attribute
                    // this run's thin result to a VAD pass that never happened.
                    if (!(vadErr && vadErr.errorType === 'cancelled')) {
                        console.warn('[auto-captions] VAD failed, continuing without segmentation', vadErr.message);
                        // phase/pct/hidden_ms: ЧТО стояло — загрузка ORT, модель или
                        // сам проход по файлу — и сколько вкладка была скрыта.
                        reportFeature('vad_failed', { reason: vadErr.message, phase: vadErr.vadPhase || '',
                            pct: vadErr.vadPct || 0, hidden_ms: vadErr.vadHiddenMs || 0,
                            audio_s: Math.round(f32.length / 16000) });
                    }
                    state.vadOffsetMap = null;
                    state.vadUsed = false;
                    state.vadSpeechSec = null;
                } finally {
                    onVadProgress = null;
                }
                if (lostRun()) return;
            } else {
                state.vadOffsetMap = null;
                state.vadUsed = false;
                state.vadSpeechSec = null;
            }

            // 1c) Live partial preview — clear ONLY the text container, never
            // the parent (parent.textContent='' would delete the static label
            // span the blade inserted, after which the label cache is detached
            // and the user stares at a blank box during the whole "Recognizing
            // speech" phase). The label stays put; only the streamText writes
            // get cleared between transcribes.
            if (els.streamPreview) {
                els.streamPreview.style.display = 'block';
                var streamTextEl = els.streamPreviewText || document.getElementById('ac-stream-text');
                if (streamTextEl) streamTextEl.textContent = '';
            }

            setProg(els.tFill, els.tText, els.tPct, 0.20, I18N.transcribe_downloading, 20);

            // 2) Spawn worker and run Whisper
            var wordLevelRequested = els.wordLevel.checked && !els.fastMode.checked;
            var wordLevelDegraded = false;
            /* Language selection.
             *
             * Earlier this code force-mapped "auto" → page locale because
             * transformers.js Whisper allegedly didn't auto-detect well.
             * That was wrong in practice: a Russian visitor on /ru/ who
             * uploaded an English video got pure gibberish ("В этом году.
             * В этом году мы будем…" ad nauseam) because Whisper was
             * locked into Russian on English audio and looped the
             * highest-prob filler tokens. Bug reported in 22:42 UTC.
             *
             * New behaviour: when the dropdown is "Auto-detect", we pass
             * the empty token (null in worker) so Whisper picks the
             * language from the first chunk's encoder output. v3.x DOES
             * detect — the prior comment was outdated. Page locale is
             * irrelevant to audio language. */
            var langChoice = els.lang.value;
            var chosenLang = langChoice;
            state.pendingTranslateTo = null;
            state.detectedLang = null;
            /* Язык речи — проверка ДО движка: от неё зависит, кто будет считать
               (русская модель только по-русски), и она стоит секунды против десятков
               минут каши. «Автоопределение» — всегда (иначе это английский перевод),
               явный язык — когда речи хватает, чтобы ошибка стоила времени. Платная
               модель OpenAI определяет язык сама. */
            var speechSecForLid = f32.length / 16000;
            if (!openAiChosen() && (langChoice === 'auto' || speechSecForLid >= LID_EXPLICIT_MIN_SEC)) {
                var willGigaam = !!(state.gigaamRequested && window.ACGigaam && ACGigaam.eligible(langChoice));
                var lidSize = whisperModelSize();
                var lidT0 = Date.now();
                stageProg(0.20, I18N.lang_detecting || 'Detecting the spoken language', 20);
                var lid = await detectSpokenLang(f32, lidSize, wordLevelRequested, function(kind, frac){
                    if (lostRun()) return;
                    if (kind === 'model') setProg(els.tFill, els.tText, els.tPct, 0.20 + Math.min(1, frac) * 0.02, I18N.transcribe_downloading, Math.round(frac * 100));
                    else stageProg(0.20 + Math.min(1, frac) * 0.02, I18N.lang_detecting || 'Detecting the spoken language', 20 + Math.round(frac * 2));
                }, langChoice === 'auto' ? null : langChoice);
                if (lostRun()) return;
                if (lid) {
                    var pChosen = probOf(lid.top, chosenLang);
                    reportFeature('lang_detect', { chosen: chosenLang, detected: lid.lang, p: lid.p, p_chosen: pChosen,
                        slices: lid.slices, model: lidSize, gigaam: willGigaam ? 1 : 0, speech_s: Math.round(speechSecForLid),
                        load_ms: lid.loadMs, compute_ms: lid.computeMs, ms: Date.now() - lidT0 });
                    if (langChoice === 'auto') {
                        langChoice = lid.lang;
                        state.detectedLang = lid.lang;
                        toast(fmt(I18N.lang_detected || 'Spoken language: :lang', { lang: langName(lid.lang) }), 'info');
                    } else if (lid.lang !== chosenLang && lid.p >= LID_ASK_MIN_P && pChosen < LID_CHOSEN_MAX_P) {
                        stageProg(0.22, I18N.lang_ask_title || 'Different language in the video?', 22);
                        var pick = await askLanguage(lid.lang, chosenLang);
                        if (lostRun() || pick === 'cancelled') return;
                        reportFeature('lang_ask', { chosen: chosenLang, detected: lid.lang, p: lid.p, pick: pick, gigaam: willGigaam ? 1 : 0 });
                        if (pick !== 'keep') {
                            langChoice = lid.lang;
                            state.detectedLang = lid.lang;
                            applyLangChoice(lid.lang);
                            if (pick === 'translate') state.pendingTranslateTo = chosenLang;
                        }
                    }
                }
                /* Определитель грузил whisper, а считать будет русская модель — веса
                   whisper освобождаются до неё (две модели разом — лишняя сотня МБ
                   памяти на слабом устройстве). */
                if (state.gigaamRequested && window.ACGigaam && ACGigaam.eligible(langChoice)) {
                    try { if (state.whisperWorker) state.whisperWorker.postMessage({ type: 'dispose' }); } catch(_) {}
                }
            }
            // Словарь «ё» (83 КБ сжатым) — с начала русского прогона, к концу он уже здесь.
            if (langChoice === 'ru' && window.RuYofication) { try { window.RuYofication.ready(); } catch (_) {} }
            // Keep 'auto' as-is — worker handles the null pass-through.
            // No more page-locale fallback.
            // Glossary → Whisper initial_prompt
            var glossaryText = collectGlossary();

            // Whisper download stall watchdog — mirror VAD's 45 s pattern, but
            // heartbeat-driven from download_progress events. If no progress
            // for 30 s the model fetch is stuck (DevTools Offline at 30%, RKN
            // throttle, CF Workers cold start), surface a clear error so the
            // user can retry instead of staring at a frozen bar forever.
            // Часы — видимые (visNow): замороженная свёрнутая вкладка не качает
            // ничего не потому, что сеть встала.
            var dlHeartbeatAt = visNow();
            var dlWatchdog = setInterval(function(){
                if (visNow() - dlHeartbeatAt > 30000) {
                    clearInterval(dlWatchdog);
                    dlWatchdog = null;
                    // Flag the state BEFORE terminating the worker so the
                    // outer catch can recognise WHY the worker died and
                    // surface the dedicated "stalled" toast instead of the
                    // generic "transcribe failed" one. The catch block at
                    // L500+ checks state.dlStalled and re-throws a typed
                    // `expected` error so the canonical Sentry beforeSend
                    // drops it (it's a predictable network condition, not
                    // an app bug).
                    state.dlStalled = true;
                    try { if (state.whisperWorker) state.whisperWorker.terminate(); } catch(_){}
                    state.whisperWorker = null;
                    state.whisperWorkerKey = null;
                    // Reject the dangling runWhisper await — terminate() alone
                    // never settles it, so pre-fix the "download stalled" toast
                    // was unreachable (the await hung; the catch never ran).
                    if (typeof state.whisperAbort === 'function') {
                        var de = new Error('whisper download stalled');
                        de.errorType = 'whisper_download_stalled';
                        de.expected = true;
                        state.whisperAbort(de);
                    }
                    // NOTE: we used to `throw new Error("Whisper download
                    // stalled …")` here. That throw was unreachable for any
                    // try/catch on the call stack — setInterval callbacks
                    // run on their own task, so the throw bypassed the
                    // surrounding runWhisper try block and became an
                    // unhandled window error (Sentry TIMBRICA-FRONTEND-7N,
                    // 2 users 2026-05-29). The user-facing message lived in
                    // an outer catch keyed off `err.errorType ===
                    // 'whisper_download_stalled'`, but that catch never saw
                    // the throw, so the user got either silent failure
                    // (terminate matched the `/terminate|aborted/` early-
                    // return at L503) or a generic toast. Now we signal
                    // via state.dlStalled and the outer catch maps it
                    // correctly — see the matching branch in L500+.
                }
            }, 5000);

            // The INFERENCE stall watchdog now lives INSIDE runWhisper (where it
            // can actually reject the promise — terminate() alone leaves the
            // await dangling). It bounds the "generates without stopping" class
            // (bug 2026-06-17, MKV music clip): once decoding starts, a finished
            // audio chunk (chunk_done) is the only forward-progress signal; if
            // none arrives for 150 s the decode is stuck (music loop / hang) and
            // runWhisper rejects with errorType 'transcribe_inference_stalled'.

            // Audio seconds actually fed to Whisper (post-VAD-pack) — drives the
            // chunk-count estimate for the progress bar + ETA. Captured BEFORE
            // runWhisper transfers f32's buffer to the worker.
            state.transcribeAudioSec = (f32 && f32.length) ? (f32.length / 16000) : (state.duration || 0);

            var result;
            try {
                /* Русская дорожка: GigaAM v3 вместо whisper, когда пользователь
                   ЯВНО её выбрал и подтвердил скачивание (см. auto-captions-gigaam.js).
                   84% прогонов этого тула на русском, и whisper `base` там ошибается
                   в 29.9% слов против 6.3% у GigaAM.

                   Откат — не украшение: вердикт 08-09 про клин воркера переоткрыт
                   замером, но на неизвестном устройстве клин всё ещё возможен.
                   Сторож внутри run() типизирует отказ, и мы молча продолжаем
                   whisper'ом на ЦЕЛОМ буфере — поэтому GigaAM его и не отсоединяет. */
                /* Платная модель OpenAI (intl, docs/why/asr-openai.md): звук —
                   тот же буфер, что получил бы whisper (уже ужатый VAD до речи:
                   меньше платить), отрезки приходят во времени ЭТОГО буфера и
                   переводятся обратно в время видео ниже (remapTimes), как у
                   whisper. Отказ — словами, без молчаливого отката на бесплатную
                   модель: человек выбрал платную. */
                if (openAiChosen()) {
                    stageProg(0.3, I18N.openai_asr_progress || I18N.transcribe_recognizing, 30);
                    try {
                        var oai = await window.OpenAiAsrLane.transcribe(f32, 16000, {
                            lang: langChoice, content: 'speech', prompt: glossaryText || '',
                            onProgress: function (frac) {
                                var p = 0.3 + Math.min(1, frac) * 0.65;
                                setProg(els.tFill, els.tText, els.tPct, p, I18N.openai_asr_progress || I18N.transcribe_recognizing, Math.round(p * 100));
                            },
                        });
                        result = { chunks: oai.segments.map(function (sg) { return { text: sg.text, timestamp: [sg.start, sg.end] }; }), wordLevel: false };
                        state.lastEngine = 'openai';
                        reportFeature('openai_ok', { segs: oai.segments.length, lang: oai.language || '' });
                    } catch (oErr) {
                        var code = (oErr && oErr.code) || 'failed';
                        var words = {
                            login: I18N.openai_asr_login, locked: I18N.openai_asr_locked, busy: I18N.openai_asr_busy,
                            lang: I18N.openai_asr_lang, unavailable: I18N.openai_asr_unavailable, running: I18N.openai_asr_running,
                            tokens: String(I18N.openai_asr_short || '').replace('{need}', (oErr && oErr.need) || 0).replace('{have}', (oErr && oErr.have) || 0),
                        }[code] || I18N.openai_asr_failed;
                        var shaped = new Error(words || 'OpenAI');
                        shaped.errorType = 'openai_asr';
                        shaped.code = code;
                        shaped.userMessage = words;
                        shaped.expected = true;
                        throw shaped;
                    }
                }
                if (!result && state.gigaamRequested && window.ACGigaam && ACGigaam.eligible(langChoice)) {
                    var gigaamCtl = (typeof AbortController === 'function') ? new AbortController() : null;
                    state.gigaamAbort = function(){ try { if (gigaamCtl) gigaamCtl.abort(); } catch(_) {} };
                    try {
                        stageProg(0.22, I18N.transcribe_downloading, 22);
                        var gigaamPct = 0.6;   // полоса распознавания только растёт: «chunk» следующего куска не откатывает её
                        result = await ACGigaam.run(f32, {
                            segments: ACGigaam.segmentsFromOffsetMap(state.vadOffsetMap),
                            signal: gigaamCtl ? gigaamCtl.signal : null,
                        }, function onGigaamEvt(m){
                            if (!m) return;
                            if (m.type === 'progress' && m.phase === 'model') {
                                // Прод отдаёт модель без Content-Length (замер 09.09:
                                // «Загрузка AI-модели 0 %» стояла 3+ минуты — ровно то,
                                // что люди зовут «зависает»). Знаменатель тогда — заявленный
                                // размер модели: он же напечатан в двери согласия.
                                var totalB = m.total || ((window.__acGigaamMb || window.__gigaamMb || 215) * 1048576);
                                var frac = m.cached ? 1 : Math.min(1, (m.loaded || 0) / totalB);
                                var pct = 0.22 + frac * 0.38;
                                setProg(els.tFill, els.tText, els.tPct, pct, I18N.transcribe_downloading, Math.round(pct * 100));
                            } else if (m.type === 'progress' && m.phase === 'decode' && m.totalSec > 0) {
                                // Доля распознанного звука, а не константа 72 %.
                                var dfrac = Math.min(1, (m.doneSec || 0) / m.totalSec);
                                gigaamPct = Math.max(gigaamPct, 0.6 + dfrac * 0.39);
                                setProg(els.tFill, els.tText, els.tPct, gigaamPct, I18N.transcribe_recognizing, Math.round(gigaamPct * 100));
                            } else if (m.type === 'progress' && (m.phase === 'chunk' || m.phase === 'decode')) {
                                setProg(els.tFill, els.tText, els.tPct, gigaamPct, I18N.transcribe_recognizing, Math.round(gigaamPct * 100));
                            } else if (m.type === 'partial_text') {
                                appendStreamPartial(m);
                            }
                        });
                        state.lastEngine = 'gigaam';
                        reportFeature('gigaam_ok', { segs: (result.chunks || []).length });
                    } catch (gErr) {
                        // Отменённый прогон не откатывается на whisper — он закончен.
                        if (lostRun() || (gErr && gErr.errorType === 'cancelled')) throw gErr;
                        state.lastEngine = 'whisper_after_gigaam';
                        // msg — сам текст отказа (до 26.09 писался только тип, и
                        // почему модель не идёт на iOS, прочесть было нечем);
                        // learned — записан ли вердикт устройству (не сеть).
                        reportFeature('gigaam_fallback', { reason: (gErr && (gErr.errorType || gErr.message) || '').toString().slice(0, 80),
                            msg: String((gErr && gErr.message) || '').slice(0, 120), learned: gErr && gErr.learned ? 1 : 0 });
                        result = null;
                    } finally {
                        state.gigaamAbort = null;
                    }
                }
                if (!result) result = await runWhisper(f32, {
                language: langChoice,
                modelSize: whisperModelSize(),
                // Окна по швам VAD (в упакованном таймлайне f32), ≤28 с — воркер
                // распознаёт их по одному вместо нарезки 29 с/шаг 5 с. Только когда
                // окон больше одного: короткий файл или сплошная речь идут как раньше.
                segments: whisperWindowsFromVad(f32),
                wordTimestamps: wordLevelRequested,
                initialPrompt: glossaryText,
                // `f32` may be VAD-packed down to seconds; the verdict needs the
                // duration of the file the user is actually looking at.
                mediaSec: state.duration || 0,
                // Stated, not inferred: this page can pack the audio down to the
                // speech regions, and only then does the speech ratio mean anything.
                vadUsed: !!state.vadUsed,
            }, function onStatus(m){
                // Capture the inference backend the worker actually used so
                // tool_complete can report webgpu-vs-wasm (telemetry gap: it was
                // never recorded, so we couldn't measure GPU adoption or confirm
                // the fp16/q8-fallback fix landed).
                if (m.type === 'backend' && m.backend) {
                    state.lastBackend = m.backend;
                    // Why WASM, when WASM. Without this the WASM share is a
                    // black box: 'no_adapter' (device truly has no GPU) and
                    // 'forced' (our own stored verdict) look identical in prod.
                    state.lastWasmReason = m.wasmReason || '';
                }
                if (m.type === 'status' && m.status === 'loading_model') {
                    setProg(els.tFill, els.tText, els.tPct, 0.22, I18N.transcribe_downloading, 22);
                    dlHeartbeatAt = visNow();
                } else if (m.type === 'download_progress' && m.data) {
                    dlHeartbeatAt = visNow();
                    var d = m.data;
                    if (d.progress != null) {
                        noteModelDownload(d.progress);
                        // Report the DOWNLOAD's own percentage, not a slice of
                        // the overall bar: "52%" that means "26% of the model"
                        // is the kind of number that makes a wait feel stuck.
                        var pct = 0.22 + (d.progress / 100) * 0.30;
                        setProg(els.tFill, els.tText, els.tPct, pct, I18N.transcribe_downloading, Math.round(d.progress));
                    }
                } else if (m.type === 'word_level_degraded') {
                    // The worker is actively retrying a different (segment-level)
                    // checkpoint — that's forward progress, not a stall.
                    dlHeartbeatAt = visNow();
                    wordLevelDegraded = true;
                } else if (m.type === 'model_host_failover' || m.type === 'model_load_stalled') {
                    // The worker is walking its host list: a weight fetch wedged
                    // or 404/403'd on one host and it is failing over to the next
                    // (mirror → proxy → direct HF). This is LIVENESS, not silence —
                    // bump the download watchdog so the pipeline doesn't preempt
                    // the worker's own per-host failover (the bug that left
                    // model_host_failover=0 in prod: the 30 s pipeline watchdog
                    // always fired before the worker's per-host guard could move on).
                    dlHeartbeatAt = visNow();
                    if (m.type === 'model_host_failover') {
                        // A later host served the model. Count so /pino shows when
                        // the primary (mirror) path degrades.
                        reportFeature('model_host_failover', { host: m.host, attempt: m.attempt, model: els.model.value });
                    }
                } else if (m.type === 'webgpu_fallback') {
                    // This device's WebGPU broke a run (thrown inference — the
                    // iPhone-Safari OOM class — or degenerate output). The worker
                    // already recovered on WASM; remember it so retries and future
                    // visits skip WebGPU instead of re-failing identically.
                    dlHeartbeatAt = visNow();   // recovery reload = liveness
                    rememberWebgpuBroken(m.reason || m.message || 'degenerate', {
                        detect: m.detect || '', tokensBefore: m.tokensBefore,
                    });
                } else if (m.type === 'asr_recovery') {
                    // Did the extra WASM pass actually rescue the transcript?
                    // Reported so the guard's threshold can be judged on results
                    // instead of on the reasoning that produced it.
                    dlHeartbeatAt = visNow();
                    reportFeature('asr_recovery', {
                        detect: m.detect || '', before: m.tokensBefore, after: m.tokensAfter,
                        audioDur: m.audioDur, improved: !!m.improved,
                    });
                } else if (m.type === 'model_downgraded') {
                    // Worker hit an out-of-memory on this model and transparently
                    // downgraded to Tiny. Tell the user honestly (accuracy drops)
                    // + count it so /pino can size the OOM population.
                    dlHeartbeatAt = visNow();
                    if (window.__toast) window.__toast(I18N.model_downgraded_oom || 'Not enough memory for this model — switched to the compact Tiny model.', 'warning');
                    reportFeature('model_downgraded', { from: m.from, to: m.to, stage: m.stage || '' });
                } else if (m.type === 'status' && m.status === 'transcribing') {
                    // Model load finished — the inference-stall watchdog inside
                    // runWhisper now owns progress. Retire the DOWNLOAD-stall
                    // watchdog so it can't false-fire mid-transcription (its
                    // heartbeat is only fed by download_progress, which stops once
                    // weights are in memory — a long decode would otherwise look
                    // "stalled" after 30 s and abort a working transcription).
                    if (dlWatchdog) { clearInterval(dlWatchdog); dlWatchdog = null; }
                    // Split the wait: everything before this point is fetching
                    // and initialising the model, everything after is actual
                    // decoding. On a phone the first half is suspected to
                    // dominate, and "make it faster" needs to know which half.
                    if (state.runStartedAt && !state.loadDoneAt) state.loadDoneAt = Date.now();
                    // Start the smooth bar + ETA ticker (replaces the old coarse
                    // per-chunk bumps that sat frozen between chunks with no ETA).
                    startTransProgress(state.transcribeAudioSec);
                    // Re-query the label fresh — a prior aggressive textContent
                    // clear elsewhere may have detached the cached reference.
                    var labelEl = els.streamPreviewLabel || document.getElementById('ac-stream-label');
                    if (labelEl && I18N.transcribe_streaming) labelEl.textContent = I18N.transcribe_streaming;
                } else if (m.type === 'chunk_done') {
                    // Advance the chunk counter; paintTransProgress (ticker) maps it
                    // to the bar + ETA. Immediate repaint so the boundary shows at once.
                    transChunksDone = m.chunkIndex || (transChunksDone + 1);
                    transLastChunkMs = Date.now();
                    paintTransProgress();
                } else if (m.type === 'heartbeat') {
                    // Per-token activity (worker posts every 2 s while decoding).
                    // Keep the bar visibly creeping so a slow-but-working decode
                    // doesn't read as frozen — but do NOT touch infChunkAt: a
                    // repetition loop also emits heartbeats, and only a finished
                    // chunk counts as progress for the stall watchdog.
                    paintTransProgress();
                } else if (m.type === 'partial_text' && els.streamPreview) {
                    appendStreamPartial(m);
                }
            });
            } finally {
                if (dlWatchdog) { clearInterval(dlWatchdog); dlWatchdog = null; }
                state.whisperAbort = null;   // runWhisper settled — drop the handle
                stopTransProgress();   // halt the bar/ETA ticker (success or throw)
            }
            if (lostRun()) return;
            if (wordLevelRequested && wordLevelDegraded) {
                toast(I18N.word_level_degraded || 'Word-level checkpoint unavailable — using approximate per-word timings.', 'warning');
                reportFeature('word_level_degraded', { model: els.model.value });
            }

            // 3) Normalize Whisper chunks into state.words / state.segments
            /* sourceLang must reflect the language ACTUALLY passed to Whisper
               (langChoice — auto resolved to page locale), not the raw dropdown
               value. Otherwise state.sourceLang stays null on "auto" and the
               emoji dictionary falls back to English on a Russian transcript,
               so no keywords match and zero emojis appear in preview. */
            // Read the language Whisper actually used. With "auto" the
            // worker's `detectedLang` carries the encoder-detected lang;
            // otherwise it echoes the dropdown choice. This drives the
            // emoji-dictionary pick + UI badges + future language-locked
            // re-runs (per-segment redo respects the original detection).
            state.sourceLang = (result && result.detectedLang) || (langChoice !== 'auto' ? langChoice : null) || 'en';
            // Cache the original (pre-VAD-pack) Float32 PCM so per-segment
            // re-transcribe can run Whisper on a slice without re-extracting
            // audio from FFmpeg. Stored at 16 kHz mono. Do NOT cache the
            // VAD-packed buffer — that loses the original timeline.
            //
            // We can't cache the post-VAD `f32` reference because pipeline.js
            // transferred its underlying ArrayBuffer to the worker. Re-extract
            // happened earlier (line 78ish via FFmpegProcessor.process) and
            // produced `pcmBlob`; we made a Float32 copy before transfer.
            // Stash a copy here while we still have `i16` / `pcmBlob` in scope.
            try {
                if (i16 && i16.length) {
                    var pcmCache = new Float32Array(i16.length);
                    for (var ic = 0; ic < i16.length; ic++) pcmCache[ic] = i16[ic] / 32768;
                    state.pcmCacheF32 = pcmCache;
                    state.pcmCacheSampleRate = 16000;
                }
            } catch(e){}
            var normalized = normalizeWhisperResult(result, state.duration);
            // Map VAD-packed timestamps back into original-video timeline so
            // captions sync against the user's video, not the packed audio buffer.
            if (state.vadOffsetMap) {
                // ⚠️ Слова и слова внутри сегментов — ОДНИ И ТЕ ЖЕ объекты
                // (normalizeWhisperResult кладёт их в оба списка). До 09.09.2026
                // два вызова пересчитывали каждое слово ДВАЖДЫ: t → orig(t) →
                // orig(orig(t)); для всего, что в исходнике позже конца
                // упакованного буфера, второй пересчёт упирался в «после
                // последнего отрезка» и сбивал слова в последнюю секунду ролика.
                // Замер 09.09 (106 с, VAD оставил 75 с): 62–64 слова из 165 стояли
                // на 105.8–105.9 с — последние 30 с субтитров были нечитаемы, и у
                // whisper, и у GigaAM. Один WeakSet на оба вызова = один пересчёт.
                var remapped = (typeof WeakSet === 'function') ? new WeakSet() : null;
                normalized.words = remapTimes(normalized.words, state.vadOffsetMap, remapped, true);
                normalized.segments = remapTimes(normalized.segments, state.vadOffsetMap, remapped);
            }
            // Sanitize the FINAL (real-video-clock) timeline: spread Whisper's
            // collapsed-timestamp dumps + cap marathon freezes so the burn is
            // watchable. No-op on clean transcripts. See sanitizeFinalTimings.
            normalized = sanitizeFinalTimings(normalized, state.duration);
            normalized = placeWordsOnSound(normalized, i16, result);
            normalized = await restoreYo(normalized, state.sourceLang, result);
            // Compute heuristic per-word confidence (no logprobs from transformers.js
            // pipeline — we infer from duration vs text-length and unicode-mix signals).
            annotateConfidence(normalized.words);
            // Phase C-3 sound-cues — YAMNet classifies non-speech windows
            // from VAD output into [applause] / [laughter] / [music] / etc.
            // Inserted as synthetic words with isSoundCue:true so prosody +
            // confidence skip them (they're not real word tokens).
            if (window.__acSoundCues && typeof window.__acSoundCues.isEnabled === 'function' && window.__acSoundCues.isEnabled()) {
                try {
                    var cues = await window.__acSoundCues.detect(state.pcmCacheF32 || f32, 16000, vadInfo && vadInfo.segments);
                    if (cues && cues.length) {
                        normalized.words = window.__acSoundCues.injectIntoTranscript(normalized.words, cues);
                        reportFeature('sound_cues_done', { count: cues.length, labels: cues.map(function(c){ return c.label; }).slice(0, 5) });
                    }
                } catch (cueErr) {
                    console.warn('[auto-captions] sound cues failed:', cueErr && cueErr.message);
                    reportFeature('sound_cues_failed', { reason: (cueErr && cueErr.message) || 'unknown' });
                }
            }
            // Compute prosody-based emphasis if (a) the toggle is on (default
            // true) and (b) we have the cached PCM. This is the differentiator
            // vs static-coloured captions — Submagic's killer-feature equivalent.
            if (state.emphasisOn !== false && state.pcmCacheF32 && typeof window.__acAnalyseProsody === 'function') {
                try {
                    window.__acAnalyseProsody(state.pcmCacheF32, state.pcmCacheSampleRate || 16000, normalized.words);
                    var hi = normalized.words.filter(function(w){ return w.emphasis === 'high'; }).length;
                    var med = normalized.words.filter(function(w){ return w.emphasis === 'med'; }).length;
                    reportFeature('prosody_emphasis', { high: hi, med: med, total: normalized.words.length });
                } catch (e) {
                    console.warn('[auto-captions] prosody analysis failed:', e.message);
                    toast(I18N.prosody_analysis_failed || 'Emphasis analysis failed — captions render without word-level emphasis.', 'warning');
                    reportToolError('prosody_failed', e);
                }
            }
            state.words = normalized.words;
            state.segments = normalized.segments;
            state.wordsOriginal = state.words.slice();
            state.cleanSnapshot = null; // any prior cleanup undo state stale

            // Apply emoji/speakers if toggles were pre-set
            if (state.emojiOn && typeof window.__acApplyEmoji === 'function') window.__acApplyEmoji();
            if (state.speakersOn && typeof window.__acApplySpeakers === 'function') window.__acApplySpeakers();

            rebuildLines();
            // Own-text alignment: if the user asked to align THEIR text, the
            // transcription above was only for timing anchors — overlay their
            // correct words onto that timeline now (see __acAlignOwnText).
            if (window.__acPendingAlign != null && typeof applyOwnTextAlignment === 'function') {
                var _pendAlign = window.__acPendingAlign; window.__acPendingAlign = null;
                try { applyOwnTextAlignment(_pendAlign); } catch (_) {}
            }
            // Phase D music-sync — snap caption-line starts to nearest beat
            // if the user enabled it AND the audio has a clear beat. Mutates
            // state.lines in place (not state.words — words keep real times).
            if (window.__acMusicSync && typeof window.__acMusicSync.isEnabled === 'function' && window.__acMusicSync.isEnabled()) {
                try {
                    var pcm = state.pcmCacheF32 || f32;
                    var sr = state.pcmCacheSampleRate || 16000;
                    var fakeBuf = { sampleRate: sr, length: pcm.length, numberOfChannels: 1, getChannelData: function(){ return pcm; } };
                    var beats = await window.__acMusicSync.analyzeBeats(fakeBuf);
                    if (beats && beats.length) {
                        var snapped = window.__acMusicSync.snapLinesToBeats(state.lines, beats);
                        if (snapped && snapped !== state.lines) state.lines = snapped;
                        reportFeature('music_sync_done', { beats: beats.length });
                    } else {
                        reportFeature('music_sync_no_beat', {});
                    }
                } catch (msErr) {
                    console.warn('[auto-captions] music-sync failed:', msErr && msErr.message);
                }
            }
            /* Работа стала результатом — сохраняем её ДО того, как человек
               уйдёт: расшифровка и есть самая дорогая часть прогона. */
            saveTranscriptCheckpoint('transcribe');
            setProg(els.tFill, els.tText, els.tPct, 1, I18N.transcribe_done, 100);
            setTimeout(function(){ hideProgress(els.tProg); }, 1200);
            // Hide live partial preview now that the editor has the full transcript
            setTimeout(clearStreamPreview, 1500);
            // Reveal cleanup card if it exists (gated until captions appear)
            var cleanCard = document.getElementById('ac-card-clean');
            if (cleanCard && cleanCard.classList.contains('ac-card-gated') && state.words.length) {
                cleanCard.classList.remove('ac-card-gated');
                cleanCard.classList.add('ac-card-reveal');
            }

            // Face-aware caption placement: if toggle is on and we have a
            // playable video element, run MediaPipe FaceDetector lazily to
            // build a face-position timeline. Audio-only files skip this.
            // The result is consumed by ASS-builder at burn time.
            if (els.faceAvoidToggle && els.faceAvoidToggle.checked &&
                els.video && els.video.videoWidth > 0 &&
                window.__acFace && typeof window.__acFace.detectAll === 'function') {
                (async function(){
                    try {
                        toast(I18N.face_detecting || 'Detecting speaker face…', 'info');
                        var samples = await window.__acFace.detectAll(els.video, { interval: 1.0, maxSamples: 60 });
                        state.faceSamples = samples;
                        var withFace = samples.filter(function(s){ return s.faces.length; }).length;
                        reportFeature('face_detection_done', { samples: samples.length, with_face: withFace });
                        if (!withFace) {
                            toast(I18N.face_no_speaker || 'No face detected — keeping default position', 'warning');
                        }
                    } catch (faceErr) {
                        console.warn('[auto-captions] face detection failed:', faceErr.message);
                        reportFeature('face_detection_failed', { reason: faceErr.message });
                        toast(I18N.face_detection_failed || 'Face-aware caption placement failed — falling back to default positions.', 'warning');
                    }
                })();
            }

            /* Mark model as cached so the "First run: model ~40-240 MB" hint
               doesn't show on subsequent visits — model is in transformers.js
               cache, runs are now instant. Read in init() (auto-captions.blade). */
            try { localStorage.setItem('ac_model_cached_v1', '1'); } catch(e){}
            try {
                var firstRunHint = document.getElementById('ac-first-run-hint');
                if (firstRunHint) firstRunHint.hidden = true;
            } catch(e){}

            /* ── Result-plausibility gate ───────────────────────────────────
               This used to be `if (!state.words.length)` — literally "zero
               words". A 314 s video that produced ONE word therefore counted
               as a full success: exports unlocked, no warning, and the user
               burned + downloaded an empty-looking video twice (~5.5 min each)
               before writing in (prod report 2026-07-23, reproduced). We
               already had every number needed to know better; we just never
               compared them. Now we do, and a thin result is surfaced honestly
               with a one-click fix instead of being sold as success. */
            var verdict = null;
            try {
                if (window.__acPlausibility) {
                    verdict = window.__acPlausibility.assess({
                        words: state.words.length,
                        durationSec: state.duration,
                        speechSec: state.vadSpeechSec,
                        hasVad: !!state.vadUsed,
                    });
                }
            } catch (_) {}
            state.lastVerdict = verdict;
            if (verdict && verdict.level !== 'ok') {
                reportFeature('transcript_implausible', {
                    level: verdict.level, reason: verdict.reason, words: verdict.words,
                    durationSec: Math.round(verdict.durationSec || 0),
                    speechSec: verdict.speechSec == null ? null : Math.round(verdict.speechSec),
                    model: els.model ? els.model.value : null,
                    denoise: !!(els.preprocDenoise && els.preprocDenoise.checked),
                });
            }

            /* A plausible transcript off WebGPU = the hardware works. Clear any
               stored strikes so two unrelated old failures can't add up to a
               verdict long after the device proved itself. */
            if (state.lastBackend === 'webgpu' && verdict && verdict.level === 'ok') rememberWebgpuOk();

            if (!state.words.length) {
                toast(I18N.transcribe_no_speech, 'warning');
                reportToolError('no_speech_detected', new Error('no_speech'));
                if (typeof window.__acShowThinResult === 'function') window.__acShowThinResult(verdict);
            }
            else {
                if (verdict && verdict.level === 'sparse' && typeof window.__acShowThinResult === 'function') {
                    window.__acShowThinResult(verdict);
                } else if (typeof window.__acHideThinResult === 'function') {
                    window.__acHideThinResult();
                }
                setExportsEnabled(true);
                // Flip the action button label to "Regenerate" — the original
                // "Generate captions" reads wrong once captions exist, and the
                // confirm hook prevents accidental overwrite of user edits.
                var btnLabel = document.getElementById('ac-transcribe-btn-label');
                if (btnLabel && I18N.regenerate_captions) btnLabel.textContent = I18N.regenerate_captions;
                state.captionsGenerated = true;
                /* Reveal gated cards (emoji / translate / speakers / export) — they
                   stay hidden until captions exist so the right column doesn't look
                   like a wall of dead controls during the long transcribe wait. */
                ['ac-card-emoji','ac-card-translate','ac-card-speakers','ac-card-export','ac-card-brand'].forEach(function(id){
                    var c = document.getElementById(id);
                    if (c && c.classList.contains('ac-card-gated')) {
                        c.classList.remove('ac-card-gated');
                        c.classList.add('ac-card-reveal');
                    }
                });
                /* Etalon telemetry — capture which optional features were used so
                   we can correlate adoption with retention and watch for regressions
                   per feature on the watch dashboard. Keep this denormalised: one
                   tool_complete row carries every flag for the run. */
                reportToolComplete({
                    action: 'transcribe',
                    backend: state.lastBackend || 'unknown',   // 'webgpu' | 'wasm' | 'unknown'
                    // run_sec = click → captions (proc_sec starts far earlier,
                    // at workspace activation, so it is not a work duration).
                    // load_sec = the model-fetch half of it.
                    run_sec: state.runStartedAt ? Math.round((Date.now() - state.runStartedAt) / 1000) : 0,
                    // click → первый живой фрагмент текста на экране (0 = не было ни одного)
                    first_partial_s: (state.runStartedAt && state.firstPartialAt) ? Math.round((state.firstPartialAt - state.runStartedAt) / 1000) : 0,
                    leveled_db: (state.inputLevel && state.inputLevel.applied) ? state.inputLevel.gainDb : 0,
                    load_sec: (state.runStartedAt && state.loadDoneAt)
                        ? Math.round((state.loadDoneAt - state.runStartedAt) / 1000) : 0,
                    // Same two counters the abandon payload carries, so finished
                    // and unfinished runs can finally be compared on equal terms.
                    hiddenMs: vis().hidden_ms, hiddenCount: vis().hidden_count,
                    // '' on webgpu; else forced | poisoned_session | no_adapter | webgpu_load_failed
                    wasmReason: state.lastWasmReason || '',
                    model: els.model.value,
                    lang: els.lang.value,
                    wordLevel: els.wordLevel.checked && !els.fastMode.checked,
                    words: state.words.length,
                    segments: state.segments.length,
                    durationSec: Math.round(state.duration || 0),
                    glossaryTerms: countGlossaryTerms(),
                    vadEnabled: !!(els.vadToggle && els.vadToggle.checked),
                    vadSegments: vadInfo ? vadInfo.segments.length : 0,
                    vadSpeechSec: vadInfo ? Math.round(vadInfo.totalSpeechSec) : 0,
                    preprocDenoise: !!(els.preprocDenoise && els.preprocDenoise.checked),
                    preprocIsolate: !!(els.preprocIsolate && els.preprocIsolate.checked),
                    advancedModelsOn: !!(els.advancedToggle && els.advancedToggle.checked),
                    streamingPartials: typeof window.__ac_partials_seen === 'number' ? window.__ac_partials_seen : 0,
                });
            }

            // 4) Dispose Whisper pipeline to free RAM (translate/burn may follow)
            try {
                if (state.whisperWorker) state.whisperWorker.postMessage({ type: 'dispose' });
            } catch(e){}
        } catch(err){
            hideProgress(els.tProg);
            // Clear live preview when transcribe errors mid-flight — otherwise
            // the partial text from before the failure sits orphaned below
            // the upload zone and reads as "captions" the user didn't get.
            clearStreamPreview();
            // Download-stall: dlWatchdog set state.dlStalled = true and
            // terminate()'d the worker, so we land here with a "terminated"
            // or "aborted" message that the cancellation branch below would
            // ALSO match — promote the stalled case first so the user
            // actually sees the actionable toast instead of a silent return.
            // Marks the re-thrown error `expected` so layout beforeSend drops
            // it from Sentry (predictable network condition, not app bug).
            if (state.dlStalled) {
                state.dlStalled = false;
                var stalled = new Error(I18N.transcribe_stalled || 'Whisper download stalled — no progress for 30 s. Check your connection and retry.');
                stalled.errorType = 'whisper_download_stalled';
                stalled.expected = true;
                stalled.userFacing = true;
                toast(stalled.message, 'error');
                reportToolError('transcribe', stalled);
                return;
            }
            // Inference-stall: runWhisper rejected because no chunk finalized
            // for 150 s (degenerate music loop / hung decode). Tell the user WHY
            // and WHAT to do — not a generic failure. It used to have to precede
            // the cancellation branch (the worker was terminate()'d, and that
            // branch matched /terminate|aborted/ in the message); since 28.09.2026
            // the branch asks lostRun(), so this is order by habit, not by need.
            if (err && err.errorType === 'transcribe_inference_stalled') {
                var infMsg = I18N.transcribe_inference_stalled || 'Recognition got stuck — this often happens on music or songs. Try a shorter clip, turn off word-level timing, or pick a smaller model.';
                toast(infMsg, 'error');
                reportToolError('transcribe_inference_stalled', err);
                return;
            }
            // Стена длительности уже сказала своё диалогом — второго тоста нет.
            if (err && err.errorType === 'duration_walled') return;
            // Платная модель OpenAI отказала: фраза уже названа, без «Ошибка: …».
            if (err && err.errorType === 'openai_asr') {
                toast(err.userMessage, err.code === 'failed' ? 'error' : 'warning');
                reportFeature('openai_refused', { code: err.code || '' });
                return;
            }
            /* Cancellation shouldn't surface as an error toast — and a cancel is
               THIS run losing its generation (__acCancelTranscribe bumps runGen
               before it terminates anything), not a message: «terminate» and
               «aborted» are also what a dying Whisper worker («Aborted(OOM)») and
               any engine reset print, and FFmpeg's errorType 'cancelled' means
               ANY terminate. Read so, a failed transcription ended in silence. */
            if (lostRun()) {
                return;
            }
            console.error('[auto-captions] transcribe failed', err);
            // Prefer FFmpegProcessor/whisper-worker classified message.
            var msg = (err && err.userMessage) || (err && err.message) || String(err);
            if (err && err.errorType === 'whisper_download_stalled') toast(I18N.transcribe_stalled || msg, 'error');
            else if (err && err.errorType === 'no_audio_stream') { /* already toasted at the throw site */ }
            else if (/out of memory|allocation/i.test(msg)) toast(I18N.oom_error, 'error');
            else toast(I18N.error + ': ' + msg, 'error');
            reportToolError('transcribe', err);
        } finally {
            setBusy(null);
            // No run in flight → __runElapsed reports 0, so a later abandon is
            // correctly recorded as "left while idle", not "left mid-work".
            state.runStartedAt = 0;
            stopProgClock();
        }
        /* Ответ «распознать на языке видео и перевести» — перевод идёт следом,
           тем же нажатием: человек просил субтитры на своём языке. */
        var trTo = state.pendingTranslateTo;
        state.pendingTranslateTo = null;
        if (trTo && !lostRun() && state.captionsGenerated && state.words.length && canTranslateTo(trTo)) {
            state.translateTarget = trTo;
            if (els.translateTarget) els.translateTarget.value = trTo;
            if (els.translateToggle && !els.translateToggle.checked) {
                els.translateToggle.checked = true;
                els.translateToggle.dispatchEvent(new Event('change', { bubbles: true }));
            }
            reportFeature('translate_after_lang_ask', { source: state.sourceLang || '', target: trTo });
            await window.__acRunTranslate();
        }
    };

    /* Worker spin-up. Reused across preload + transcribe so the model
       download isn't paid twice. Key = modelSize|wordLevel — when the user
       changes either, terminate and start fresh so the next preload/transcribe
       gets the right weights. */
    function whisperKey(modelSize, wordLevel){ return (modelSize || 'tiny') + '|' + (wordLevel ? 'w' : 's'); }
    /* Same-origin model mirror (redub parity, see whisper-worker.js HF_MIRROR).
       Models self-hosted under /vendor/whisper-hf/ never touch HF's Xet CDN —
       which the RU /cdn/hf/ proxy can only serve through a signed-redirect
       cache that intermittently 403s ("Forbidden access to file", prod
       2026-06-11). Mirrored: tiny + base, plain AND _timestamped (the tool's
       default is tiny + word-level → whisper-tiny_timestamped). small / turbo
       fall through to the proxy → direct HF chain, which the worker now
       walks per-host on weight failures. */
    var MIRRORED_MODELS = { tiny: 1, base: 1 };
    /* Worker URL with deploy cache-bust. `new Worker()` fetches bypass the
       Service Worker (destination='worker'), so the ONLY thing that forces a
       fresh worker after a deploy is the URL changing. The blade hands us a
       per-file ?v=<filemtime> base via window.__acWorkerBase (built with the
       AssetRouter::worker() helper); we append the runtime query. Fallback to
       the bare path keeps it working if the global is absent (older cached
       blade). Without this, a fix shipped to whisper-worker.js (e.g. the
       anti-repetition params, or the phase-1 host-failover) stays stale in the
       browser HTTP cache for returning users — the valeriitaglin 2026-06-20
       class on the worker side. */
    /* Значение селекта моделей может быть НЕ whisper'овским: русская дорожка
       добавляет туда `gigaam-ru`. Whisper такого размера не знает, а именно он
       принимает удар при откате — поэтому всюду, где значение уходит В WHISPER,
       оно проходит через этот перевод. Телеметрия, наоборот, пишет сырое
       значение: там важно, что человек выбрал на самом деле. */
    /* Выбрана ли платная модель OpenAI — и есть ли она на этом домене. */
    function openAiChosen(){
        return !!(els && els.model && els.model.value === 'openai'
            && window.OpenAiAsrLane && window.OpenAiAsrLane.available());
    }
    function whisperModelSize(raw){
        var v = raw || (els.model && els.model.value) || 'tiny';
        if (v !== 'gigaam-ru' && v !== 'openai') return v;
        // Русская модель выбрана, а считать будет whisper (согласие не дано или
        // откат). Раньше здесь стоял жёсткий 'base' — и слабый телефон, которому
        // по возможностям полагался `tiny` (40 МБ), качал 75 МБ. Спрашиваем тот
        // же источник правды, что и стартовый выбор.
        try {
            if (typeof window.__acCapabilityModel === 'function') return window.__acCapabilityModel();
        } catch(_) {}
        return 'base';
    }
    function acWorkerUrl(key, fallbackPath, params){
        var base = (window.__acWorkerBase && window.__acWorkerBase[key]) || fallbackPath;
        var sep = base.indexOf('?') === -1 ? '?' : '&';
        return base + (params ? sep + params : '');
    }
    function getOrCreateWhisperWorker(modelSize, wordLevel){
        var want = whisperKey(modelSize, wordLevel);
        if (state.whisperWorker && state.whisperWorkerKey === want) return state.whisperWorker;
        // Different weights incoming (model or word-level changed) → the old
        // download percentage no longer describes anything.
        if (state.whisperWorker) { try { state.whisperWorker.terminate(); } catch(e){} }
        state.modelDlPct = 0;
        var locale = (typeof window !== 'undefined' && window.__LOCALE) ? String(window.__LOCALE).toLowerCase() : 'en';
        var qs = acWorkerUrl('whisper', '/js/whisper-worker.js', 'locale=' + encodeURIComponent(locale));
        if (MIRRORED_MODELS[modelSize || 'tiny']) qs += '&hfmirror=' + encodeURIComponent('/vendor/whisper-hf/');
        var w = new Worker(qs);
        state.whisperWorker = w;
        state.whisperWorkerKey = want;
        return w;
    }

    /* Fire-and-forget warm of the currently selected model + word-level
       setting. Called after upload and on model/word-level dropdown changes
       so the heavy 30-200 MB weights download in parallel with the user's
       customization, not on the transcribe button click. Idempotent — the
       worker dedups via inflightLoad + lastModelKey internally. */
    function preloadWhisperModel(){
        if (!els || !els.model) return;
        // Выбрана русская дорожка → НЕ тянуть заранее веса whisper: это лишние
        // десятки мегабайт на том же соединении, куда сейчас пойдут 215 МБ
        // GigaAM. Whisper догрузится сам, если понадобится откат.
        if (els.model.value === 'gigaam-ru') return;
        // Платная модель считает на сервере — весам whisper тут делать нечего.
        if (els.model.value === 'openai') return;
        var modelSize = whisperModelSize();
        var wordLevel = els.wordLevel ? !!els.wordLevel.checked : true;
        var w = getOrCreateWhisperWorker(modelSize, wordLevel);
        var prev = w.onmessage;
        w.onmessage = function(ev){
            var m = ev.data;
            // The preload is where most of the weights actually arrive — it
            // starts when the upload zone is first touched. Its progress used
            // to be dropped on the floor (no transcribe is running yet, so
            // `prev` is null), which is why the bar could show 8% for a minute
            // while 39 MB streamed in unannounced.
                if (m && m.type === 'download_progress' && m.data) noteModelDownload(m.data.progress);
            if (m && (m.type === 'preload_done' || m.type === 'preload_failed')) {
                if (m.type === 'preload_done') state.modelDlPct = 100;
                console.log('[auto-captions] Whisper preload', m.type, m.backend || '', m.message || '');
                w.onmessage = prev || null;
                return;
            }
            if (typeof prev === 'function') try { prev(ev); } catch(e){}
        };
        try {
            w.postMessage({ type: 'preload', modelSize: modelSize, wordTimestamps: wordLevel, forceWasm: whisperForceWasm() });
        } catch(e) { console.warn('[auto-captions] Whisper preload post failed', e && e.message); }
    }
    window.__acPreloadWhisper = preloadWhisperModel;

    /* WebGPU skip-verdict for this device. Runs 3-5x faster than WASM, so the
       decision to abandon it must be earned, not assumed.

       v1 (2026-07-02) persisted `ac_whisper_wasm='1'` on ANY webgpu_fallback,
       forever. Prod (20 d) then showed 78 poisonings of which 72 were
       `degenerate` — the class whisper-worker.js itself documents as
       NON-deterministic ("same device gives good + bad runs"). So 92% of the
       verdicts condemned a working GPU to the slow path permanently.

       v2 delegates to whisper-gpu-policy.js: degenerate output recovers
       on WASM for THIS run only; hard capability failures (OOM / device lost /
       inference_error) need 2 strikes and expire after a week. The v1 key is
       read once for migration and dropped, freeing already-poisoned devices. */
    var _gpuVerdict = null;
    function gpuVerdict(){
        if (!_gpuVerdict && window.__whisperGpuPolicy) {
            _gpuVerdict = window.__whisperGpuPolicy.bind({
                key: 'ac_gpu_verdict', legacyKey: 'ac_whisper_wasm', onEvent: reportFeature,
            });
        }
        return _gpuVerdict;
    }
    function whisperForceWasm(){
        var v = gpuVerdict();
        return v ? v.isForced() : false;   // policy file missing → never block the fast path
    }
    /* Tab-hidden counters from the shared telemetry module; absent on a page
       where it failed to load, so never let it throw into a run. */
    function vis(){
        try { return (window.__visibilityStats && window.__visibilityStats()) || {}; }
        catch(_) { return {}; }
    }

    function rememberWebgpuBroken(reason, extra){
        var r = (reason || 'unknown').slice(0, 60);
        var v = gpuVerdict();
        var payload = { reason: r, persisted: v ? v.noteFailure(r) : false };
        if (extra) for (var k in extra) if (extra[k] !== undefined) payload[k] = extra[k];
        reportFeature('webgpu_fallback', payload);
    }
    /* A completed WebGPU run proves the hardware works — clear any strikes so a
       stale pair of failures can't accumulate into a verdict months later. */
    function rememberWebgpuOk(){
        var v = gpuVerdict();
        if (v) v.noteSuccess();
    }

    function runWhisper(pcmF32, opts, onEvt){
        return new Promise(function(resolve, reject){
            // CRITICAL: terminate() does NOT reject a pending worker await — the
            // promise just hangs and the outer try/catch never runs. The old
            // pipeline-side watchdogs learned this the hard way (a terminated
            // worker = an infinite "await runWhisper"). So every exit — success,
            // worker error, inference stall, download stall, user cancel — routes
            // through settle(), which clears the timer and is idempotent.
            // stallTimer — функция отмены видимого таймера (visTimeout).
            var settled = false, stallTimer = null;
            // Inference-stall budget. Forward progress = a FINISHED audio chunk
            // (chunk_done). Per-token heartbeats deliberately do NOT reset it: a
            // degenerate music-repetition loop keeps emitting tokens forever, so
            // "tokens flowing" is not progress — only a finalized chunk is.
            var STALL_MS = (typeof window.__AC_INFERENCE_STALL_MS === 'number' && window.__AC_INFERENCE_STALL_MS > 0)
                ? window.__AC_INFERENCE_STALL_MS : 150000;
            function settle(isErr, val){
                if (settled) return; settled = true;
                if (stallTimer) { stallTimer(); stallTimer = null; }
                state.whisperAbort = null;
                (isErr ? reject : resolve)(val);
            }
            function armStall(){
                if (stallTimer) stallTimer();
                stallTimer = visTimeout(function(){
                    try { if (state.whisperWorker) state.whisperWorker.terminate(); } catch(e){}
                    state.whisperWorker = null; state.whisperWorkerKey = null;
                    var e = new Error('inference stalled — no chunk finalized in ' + Math.round(STALL_MS/1000) + 's');
                    e.errorType = 'transcribe_inference_stalled';
                    e.expected = true;
                    settle(true, e);
                }, STALL_MS);
            }
            // External abort handle so the download-stall watchdog and user
            // cancel can REJECT this promise (not just terminate the worker and
            // leave the await dangling). Carries a typed errorType the catch maps.
            state.whisperAbort = function(err){ settle(true, err || new Error('aborted')); };
            try {
                var w = getOrCreateWhisperWorker(opts.modelSize, !!opts.wordTimestamps);

                w.onmessage = function(ev){
                    var m = ev.data;
                    if (onEvt) try { onEvt(m); } catch(e){}
                    if (m.type === 'status' && m.status === 'transcribing') armStall();   // inference began
                    else if (m.type === 'chunk_done') armStall();                          // real progress → reset
                    // preprocess_progress = the worker melling its way through the
                    // file BEFORE the first token (transformers.js preprocesses ALL
                    // windows up front — minutes of otherwise-total silence on long
                    // files, killed as "stalled" at 150 s; same class as profanity
                    // comment #852). It only fires pre-decode, so it can't mask a
                    // true decode stall.
                    else if (m.type === 'preprocess_progress') armStall();
                    // In-worker recovery (WebGPU→WASM reload, OOM→tiny downgrade)
                    // is LIVENESS: the reload re-emits download_progress with no
                    // chunk finalizing — without these resets a slow-network
                    // recovery would be killed by the inference watchdog mid-
                    // repair. download_progress only fires during model loads,
                    // never during decode, so this can't mask a true decode stall.
                    else if (m.type === 'webgpu_fallback' || m.type === 'model_downgraded' || m.type === 'download_progress' || m.type === 'asr_recovery') armStall();
                    if (m.type === 'result') { settle(false, m.result); }
                    else if (m.type === 'error') { settle(true, new Error(m.message || 'Whisper error')); }
                };
                w.onerror = function(e){ settle(true, new Error(e.message || 'Worker error')); };

                w.postMessage({
                    type: 'transcribe',
                    audioBuffer: pcmF32.buffer,
                    language: opts.language === 'auto' ? null : opts.language,
                    modelSize: opts.modelSize,
                    wordTimestamps: !!opts.wordTimestamps,
                    initialPrompt: opts.initialPrompt || '',
                    segments: (opts.segments && opts.segments.length > 1) ? opts.segments : null,
                    // The media duration the user is looking at. The buffer above is
                    // VAD-packed/mono 16k — its length is what the model HEARD, and
                    // judging only by that gets more forgiving as the input gets
                    // worse (asr-quality-guard.js → mediaVerdict).
                    mediaSec: +opts.mediaSec || 0,
                    vadUsed: !!opts.vadUsed,
                    // This device previously produced a broken WebGPU run (thrown
                    // inference / degenerate output) — skip WebGPU from the start.
                    forceWasm: whisperForceWasm(),
                }, [pcmF32.buffer]);
            } catch(err){ settle(true, err); }
        });
    }

    /* ════════════════════════════════════════════════════════════
       Язык речи — определяется, а не берётся на веру (чат 730, 29.10.2026)

       transformers.js язык не определяет: без `language` он подставляет
       английский и ПЕРЕВОДИТ (замер на этой странице, 74 с китайской речи:
       «Hello everyone, welcome to today's program…», sourceLang=en). Поэтому
       «Автоопределение» до этого дня было английским переводом чего угодно,
       а явный язык, не совпавший с речью, давал кашу: Whisper «ru» на китайской
       речи — иероглифы и петлю «В этом случае мы будем делать», GigaAM — русские
       «слова» из ничего (у неё: 221 слово на 70 минут речи за 45 минут работы).

       Определитель — прямой проход модели в воркере (`detect_language`, замер
       и правила — docs/why/lyrics.md): до четырёх отрывков по 30 с, середина
       первой, ранний выход, когда два первых согласны с p ≥ 0.6. Модель — та же,
       что будет распознавать, в том же воркере: второй загрузки нет. Отрывки
       берутся из буфера ПОСЛЕ детектора речи — там только речь.
       ════════════════════════════════════════════════════════════ */
    var LID_FRACS = [0.35, 0.55, 0.15, 0.75];
    var LID_SEC = 30;
    // Индонезийскую речь whisper метит яванским — замер /lyrics-transcription.
    var LID_ALIAS = { jw: 'id' };
    // Явный язык проверяем, когда речи хотя бы столько: короткий ролик в чужом
    // языке переделывается за секунды, а проверка стоит загрузки модели.
    var LID_EXPLICIT_MIN_SEC = 45;
    // «Другой язык» — только уверенно: язык отрывков ≥ 0.6 и у выбранного < 0.2.
    var LID_ASK_MIN_P = 0.6, LID_CHOSEN_MAX_P = 0.2;

    function langName(code){
        try {
            var loc = (typeof window.__intlLocale === 'function') ? window.__intlLocale() : (window.__LOCALE || 'en');
            var n = new Intl.DisplayNames([loc], { type: 'language' }).of(code);
            if (n && n !== code) return n;
        } catch(_) {}
        var opt = els.lang && els.lang.querySelector('option[value="' + code + '"]');
        return (opt && opt.textContent) || code;
    }
    function fmt(s, vars){
        s = String(s || '');
        for (var k in vars) s = s.split(':' + k).join(vars[k]);
        return s;
    }

    /* → { lang, p, top, perSlice, slices, loadMs, computeMs } либо null (не
       узнали: модель не загрузилась, воркер молчит 150 с, отмена). */
    function detectSpokenLang(pcmF32, modelSize, wordLevel, onProg, keepLang){
        return new Promise(function(resolve){
            var n = Math.min(pcmF32.length, LID_SEC * 16000);
            if (n < 16000 * 4) return resolve(null);
            var slices = [], seen = {};
            for (var i = 0; i < LID_FRACS.length; i++) {
                var at = Math.max(0, Math.min(pcmF32.length - n, Math.round(pcmF32.length * LID_FRACS[i])));
                if (seen[at]) continue;   // короткая запись: отрывок один
                seen[at] = 1;
                slices.push(pcmF32.slice(at, at + n).buffer);
            }
            var w;
            try { w = getOrCreateWhisperWorker(modelSize, wordLevel); } catch(_) { return resolve(null); }
            var done = false, stopWatch = null;
            function finish(v){
                if (done) return; done = true;
                if (stopWatch) { stopWatch(); stopWatch = null; }
                w.removeEventListener('message', onMsg);
                w.removeEventListener('error', onErr);
                state.lidAbort = null;
                resolve(v);
            }
            function watch(){
                if (stopWatch) stopWatch();
                stopWatch = visTimeout(function(){ reportFeature('lang_detect_silent', {}); finish(null); }, 150000);
            }
            function onMsg(ev){
                var m = ev.data || {};
                watch();
                if (m.type === 'language_detected') finish(m);
                else if (m.type === 'language_detect_failed') { reportFeature('lang_detect_failed', { msg: String(m.message || '').slice(0, 120) }); finish(null); }
                else if (m.type === 'download_progress' && m.data && m.data.progress != null) {
                    noteModelDownload(m.data.progress);
                    if (onProg) onProg('model', m.data.progress / 100);
                } else if (m.type === 'lid_progress' && m.total && onProg) onProg('lid', m.done / m.total);
            }
            function onErr(){ finish(null); }
            w.addEventListener('message', onMsg);
            w.addEventListener('error', onErr);
            state.lidAbort = function(){ finish(null); };
            watch();
            try {
                w.postMessage({ type: 'detect_language', modelSize: modelSize, wordTimestamps: !!wordLevel,
                                forceWasm: whisperForceWasm(), slices: slices,
                                earlyStop: { after: 2, minP: LID_ASK_MIN_P, keep: keepLang || null, keepMinP: 0.5 } }, slices);
            } catch(_) { finish(null); }
        }).then(function(m){
            if (!m || !m.lang) return null;
            var top = (m.top || []).map(function(x){ return { lang: LID_ALIAS[x.lang] || x.lang, p: x.p }; });
            return { lang: top.length ? top[0].lang : (LID_ALIAS[m.lang] || m.lang), p: top.length ? top[0].p : 0,
                     top: top, perSlice: m.perSlice || [], slices: m.slices || 0, loadMs: m.loadMs || 0, computeMs: m.computeMs || 0 };
        });
    }
    function probOf(top, code){
        for (var i = 0; i < (top || []).length; i++) if (top[i].lang === code) return top[i].p;
        return 0;
    }

    /* Вопрос «в видео другой язык?» — под полосой распознавания, где человек
       смотрит. Три ответа: распознать на языке видео и перевести на выбранный
       (так и звучала просьба в чате 730: «выбрала русский… как сделать, чтобы
       перевелось на русский»), только распознать, оставить выбранный. Отмена
       прогона закрывает вопрос (state.langAskAbort). */
    function askLanguage(detected, chosen){
        return new Promise(function(resolve){
            var box = document.getElementById('ac-lang-ask');
            if (!box) return resolve('only');
            var vars = { detected: langName(detected), chosen: langName(chosen) };
            var body = document.getElementById('ac-lang-ask-body');
            if (body) body.textContent = fmt(I18N.lang_ask_body, vars);
            var bT = document.getElementById('ac-lang-ask-translate');
            var bO = document.getElementById('ac-lang-ask-only');
            var bK = document.getElementById('ac-lang-ask-keep');
            if (bT) { bT.textContent = fmt(I18N.lang_ask_translate, vars); bT.hidden = !canTranslateTo(chosen); }
            if (bO) bO.textContent = fmt(I18N.lang_ask_only, vars);
            if (bK) bK.textContent = fmt(I18N.lang_ask_keep, vars);
            var done = false;
            function finish(pick){
                if (done) return; done = true;
                box.hidden = true;
                [bT, bO, bK].forEach(function(b){ if (b) b.onclick = null; });
                state.langAskAbort = null;
                resolve(pick);
            }
            if (bT) bT.onclick = function(){ finish('translate'); };
            if (bO) bO.onclick = function(){ finish('only'); };
            if (bK) bK.onclick = function(){ finish('keep'); };
            state.langAskAbort = function(){ finish('cancelled'); };
            box.hidden = false;
            try { box.scrollIntoView({ block: 'nearest' }); } catch(_) {}
            var first = (bT && !bT.hidden) ? bT : bO;
            try { if (first) first.focus({ preventScroll: true }); } catch(_) {}
        });
    }
    function canTranslateTo(code){
        return !!(els.translateTarget && els.translateTarget.querySelector('option[value="' + code + '"]'));
    }
    /* Ответ «на языке видео» переводит селект — тихо, без события change: оно
       перегрело бы веса посреди прогона. Русская модель на чужом языке не
       считает, поэтому селект модели уходит на модель по возможностям. */
    function applyLangChoice(code){
        if (!els.lang) return;
        if (!els.lang.querySelector('option[value="' + code + '"]')) {
            var o = document.createElement('option');
            o.value = code;
            o.textContent = langName(code);
            els.lang.appendChild(o);
        }
        if (els.model && els.model.value === 'gigaam-ru' && !(window.ACGigaam && ACGigaam.eligible(code))) {
            els.model.value = whisperModelSize();
        }
        els.lang.value = code;
        if (typeof window.__acSyncLangOptions === 'function') { try { window.__acSyncLangOptions(); } catch(_) {} }
    }

    /* ════════════════════════════════════════════════════════════
       VAD pre-segmentation (Silero VAD via worker)

       Deadline = 45 s WITHOUT A SIGN OF LIFE, counted in visible time — not
       45 s for the whole job. We saw Firefox WebWorker fetch hang silently on
       the HuggingFace 302 redirect during model download — without a deadline
       the whole transcribe pipeline stalls. But a single 45 s budget for the
       whole job also condemned working runs: the detector walks the file frame
       by frame (a long recording on a slow phone is minutes of honest work),
       and a frozen background tab fired the timer the instant it came back
       (prod 30 d: 91 «VAD timeout», 16 of them on files under 2 min). The
       worker reports status on every phase and progress every 5 %, so every
       message re-arms the watchdog; only silence means a hang. On timeout we
       terminate the worker and let the catch block in __acTranscribe fall
       back to whole-audio Whisper — and the error carries the phase it died
       in, so a hung download and a hung detector stop looking identical.
       ════════════════════════════════════════════════════════════ */
    function runVad(pcmF32){
        return new Promise(function(resolve, reject){
            var copy = new Float32Array(pcmF32.length);
            copy.set(pcmF32);
            var w;
            var settled = false;
            var SILENCE_MS = (typeof window.__AC_VAD_SILENCE_MS === 'number' && window.__AC_VAD_SILENCE_MS > 0)
                ? window.__AC_VAD_SILENCE_MS : 45000;
            var phase = 'start', pct = 0, cancelDeadline = null;
            var t0Wall = Date.now(), t0Vis = visNow();
            function arm(){
                if (cancelDeadline) cancelDeadline();
                cancelDeadline = visTimeout(function(){
                    if (settled) return;
                    var e = new Error('VAD timeout (' + Math.round(SILENCE_MS / 1000) + ' s) — model fetch may have stalled');
                    e.vadPhase = phase;
                    e.vadPct = Math.round(pct);
                    e.vadHiddenMs = Math.max(0, Math.round((Date.now() - t0Wall) - (visNow() - t0Vis)));
                    done(reject, e);
                }, SILENCE_MS);
            }
            // Cancel (__acCancelTranscribe) ends THIS run: stop the watchdog and
            // settle the promise. Without the settle the await in __acTranscribe
            // hung for good and kept the whole PCM buffer alive.
            state.vadDeadline = function(){ if (cancelDeadline) { cancelDeadline(); cancelDeadline = null; } };
            state.vadAbort = function(err){ done(reject, err || new Error('cancelled')); };
            function done(fn, payload){
                if (settled) return;
                settled = true;
                if (cancelDeadline) { cancelDeadline(); cancelDeadline = null; }
                state.vadDeadline = null;
                state.vadAbort = null;
                try { if (w) w.terminate(); } catch(e){}
                if (state.vadWorker === w) state.vadWorker = null;
                fn(payload);
            }
            try {
                if (state.vadWorker) { try { state.vadWorker.terminate(); } catch(e){} }
                var locale = (typeof window !== 'undefined' && window.__LOCALE) ? String(window.__LOCALE).toLowerCase() : 'en';
                w = new Worker(acWorkerUrl('vad', '/js/silero-vad-worker.js', 'locale=' + encodeURIComponent(locale)));
                state.vadWorker = w;
                arm();
                w.onmessage = function(ev){
                    var m = ev.data || {};
                    if (m.type === 'result') return done(resolve, m);
                    if (m.type === 'error') return done(reject, new Error(m.message));
                    if (m.type === 'status' && m.status) phase = m.status;
                    else if (m.type === 'progress') { phase = 'detecting'; pct = +m.progress || pct; }
                    if (typeof onVadProgress === 'function' && m.type === 'progress') { try { onVadProgress(pct); } catch(_) {} }
                    arm();
                };
                w.onerror = function(e){ done(reject, new Error(e.message || 'VAD worker error')); };
                // union: речь — то, что считает речью хоть одно из двух прочтений Silero
                // (без контекста и с ним): крик стримера и пение под музыку одновременно
                // (чат 640, 28.10.2026; замер — в шапке detect() воркера).
                w.postMessage({ type: 'detect', audioBuffer: copy.buffer, options: { union: true } }, [copy.buffer]);
            } catch (err) { done(reject, err); }
        });
    }
    // The page's VAD progress hook (set by __acTranscribe around its runVad
    // call) — the bar used to sit on «Detecting speech 16 %» for the whole
    // pass, however long.
    var onVadProgress = null;

    /* Concatenate speech-only PCM into a tighter Float32Array and remember the
       offset map so we can remap Whisper timestamps back to the original video
       timeline. */
    /* Живой текст распознавания — общая точка для ОБОИХ движков (whisper шлёт
       `partial_text` по токенам, GigaAM — по куску). Пишем в #ac-stream-text,
       а не в родителя, чтобы не снести подпись. Держим последние ~600 символов.
       Считаем ПРИШЕДШИЕ куски: `window.__ac_partials_seen` читался телеметрией
       с самого начала и до 08-24 не писался никогда. */
    /* Экран не гаснет, пока идёт работа. Половина сессий — телефоны, и на
       телефоне «получили текст» 49 % против 87 % на десктопе; свёрнутая или
       погасшая вкладка — главный убийца прогона (замер 07-24: с сокрытием
       вкладки 26 % успеха против 67 %). Держим экран через общий примитив
       window.__wakeLock (wake-lock.js): он сам проверяет, что замок реально
       взят, перебирает его после заморозки/bfcache и падает на видео-заглушку
       там, где Wake Lock API отказывает. Прямого обращения к API здесь нет
       намеренно — WakeLockPrimitiveTest. */
    var screenLockPhase = null;
    function holdScreenAwake(phase){
        screenLockPhase = phase || 'work';
        try { if (window.__wakeLock && typeof window.__wakeLock.request === 'function') window.__wakeLock.request(); } catch (_) {}
    }
    function releaseScreenAwake(){
        screenLockPhase = null;
        try { if (window.__wakeLock && typeof window.__wakeLock.release === 'function') window.__wakeLock.release(); } catch (_) {}
    }

    /* Окна для whisper из карты VAD: те же швы, что у GigaAM, но склеенные до
       28 с (у модели окно 30 с). null, когда VAD не работал, окон меньше двух
       или буфер короче одного окна модели — тогда нарезка остаётся за воркером. */
    function whisperWindowsFromVad(pcm){
        try {
            if (!state.vadOffsetMap || !window.ACGigaam || typeof window.ACGigaam.segmentsFromOffsetMap !== 'function') return null;
            if (!pcm || pcm.length / 16000 <= 29) return null;
            var w = window.ACGigaam.segmentsFromOffsetMap(state.vadOffsetMap, 28);
            if (!w || w.length < 2) return null;
            reportFeature('whisper_vad_windows', { n: w.length, sec: Math.round(pcm.length / 16000) });
            return w;
        } catch (_) { return null; }
    }

    function appendStreamPartial(m){
        window.__ac_partials_seen = (window.__ac_partials_seen || 0) + 1;
        if (!state.firstPartialAt) state.firstPartialAt = Date.now();
        var el = els.streamPreviewText || document.getElementById('ac-stream-text');
        if (!el) return;
        var existing = el.textContent || '';
        var combined = m.final ? (existing + ' ' + (m.text || '')).trim() : (existing.replace(/[^.!?]*$/, '') + ' ' + (m.text || '')).trim();
        if (combined.length > 600) combined = '… ' + combined.slice(combined.length - 580);
        el.textContent = combined;
    }

    function packPcmBySegments(pcm, segments, sampleRate){
        var totalSamples = 0;
        for (var i = 0; i < segments.length; i++){
            var ss = Math.max(0, Math.floor(segments[i].start * sampleRate));
            var ee = Math.min(pcm.length, Math.ceil(segments[i].end * sampleRate));
            if (ee > ss) totalSamples += ee - ss;
        }
        var out = new Float32Array(totalSamples);
        var offsetMap = []; // [{newStartSec, newEndSec, origStartSec}]
        var cursor = 0;
        for (var j = 0; j < segments.length; j++){
            var sJ = Math.max(0, Math.floor(segments[j].start * sampleRate));
            var eJ = Math.min(pcm.length, Math.ceil(segments[j].end * sampleRate));
            if (eJ <= sJ) continue;
            out.set(pcm.subarray(sJ, eJ), cursor);
            offsetMap.push({
                newStartSec: cursor / sampleRate,
                newEndSec:   (cursor + (eJ - sJ)) / sampleRate,
                origStartSec: sJ / sampleRate,
            });
            cursor += eJ - sJ;
        }
        return { pcm: out, offsetMap: offsetMap };
    }

    /* Remap a list of {start, end, ...} entries from packed timeline to original.
       The rule lives in auto-captions-timings.js (`remapPacked`: a word stays inside
       one VAD piece); below — the old ends-apart mapping, only if that module failed
       to load. */
    function remapTimes(items, offsetMap, seen, asWords){
        if (window.__acTimings && typeof window.__acTimings.remapPacked === 'function') {
            return window.__acTimings.remapPacked(items, offsetMap, seen, asWords);
        }
        if (!Array.isArray(items) || !offsetMap || !offsetMap.length) return items;
        // `seen` — объекты, уже пересчитанные в ЭТОМ проходе (слова разделяются
        // между списком слов и сегментами); без него пересчёт применялся дважды.
        if (!seen && typeof WeakSet === 'function') seen = new WeakSet();
        function mapOne(o){
            if (!o || typeof o !== 'object') return;
            if (seen) { if (seen.has(o)) return; seen.add(o); }
            o.start = remap(o.start);
            o.end = remap(o.end);
        }
        function remap(t){
            for (var i = 0; i < offsetMap.length; i++){
                var seg = offsetMap[i];
                if (t >= seg.newStartSec && t <= seg.newEndSec + 0.01) {
                    return seg.origStartSec + (t - seg.newStartSec);
                }
            }
            // After last segment — clamp to last segment end
            var last = offsetMap[offsetMap.length - 1];
            return last.origStartSec + (last.newEndSec - last.newStartSec);
        }
        for (var k = 0; k < items.length; k++){
            var it = items[k];
            mapOne(it);
            if (Array.isArray(it.words)) {
                for (var w = 0; w < it.words.length; w++) mapOne(it.words[w]);
            }
        }
        return items;
    }

    /* ════════════════════════════════════════════════════════════
       PROMPT TEMPLATES — pre-fill glossary with category-specific terms.

       Click a template, glossary textarea gets prepended with the term list.
       User can still add their own. We deliberately don't overwrite the
       existing glossary — append+dedupe so users don't lose their own
       terms when switching templates mid-edit.
       ════════════════════════════════════════════════════════════ */
    var PROMPT_TEMPLATES = {
        tech_podcast: ['Anthropic','Claude','OpenAI','GPT','ChatGPT','Llama','Mistral','transformer','LLM','RAG','API','SDK','GitHub','TypeScript','Rust','WebAssembly','WASM','Kubernetes','Docker','PostgreSQL'],
        lecture: ['hypothesis','methodology','correlation','statistical','peer-reviewed','dissertation','citation','curriculum','seminar','syllabus'],
        conference: ['keynote','panelist','breakout','roadmap','retrospective','OKRs','KPIs','quarterly','enterprise','SaaS'],
        interview: ['perspective','viewpoint','retrospect','reminisce','candidly','frankly'],
        movie: ['protagonist','antagonist','cinematography','dialogue','screenplay','soundtrack'],
        marketing: ['conversion','funnel','retention','churn','UGC','CTR','CPC','impressions','engagement','ROI'],
    };
    window.__acApplyPromptTemplate = function(key){
        var ta = els.glossary;
        if (!ta) return;
        var add = PROMPT_TEMPLATES[key];
        if (!add) return;
        var existing = (ta.value || '').split(/\r?\n/).map(function(s){ return s.trim(); }).filter(Boolean);
        var seen = Object.create(null);
        existing.forEach(function(s){ seen[s.toLowerCase()] = true; });
        var merged = existing.slice();
        for (var i = 0; i < add.length; i++) {
            if (!seen[add[i].toLowerCase()]) {
                merged.push(add[i]);
                seen[add[i].toLowerCase()] = true;
            }
        }
        ta.value = merged.join('\n');
        try { localStorage.setItem('ac_glossary_v1', ta.value); } catch(e){}
        // Trigger 'input' so the count badge updates
        ta.dispatchEvent(new Event('input', { bubbles: true }));
        // Also open the <details> wrapper so the user sees what just happened
        var det = ta.closest('details');
        if (det && !det.open) det.open = true;
        toast((I18N.prompt_template_applied || 'Template applied') + ' · +' + add.length, 'success');
        reportFeature('prompt_template_applied', { template: key, addedTerms: add.length });
    };

    /* ════════════════════════════════════════════════════════════
       STYLE FAVOURITES — save/load named style presets in localStorage.
       ════════════════════════════════════════════════════════════ */
    var STYLE_KEY = 'ac_style_favs_v1';
    function readFavs(){
        try { return JSON.parse(localStorage.getItem(STYLE_KEY) || '[]') || []; }
        catch(e){ return []; }
    }
    function writeFavs(list){
        try { localStorage.setItem(STYLE_KEY, JSON.stringify(list.slice(0, 12))); } catch(e){}
    }
    window.__acListStyleFavourites = function(){ return readFavs(); };
    window.__acSaveStyleFavourite = function(name){
        if (!name) return false;
        name = String(name).trim().slice(0, 40);
        if (!name) return false;
        var list = readFavs();
        var snapshot = {
            name: name,
            preset:    state.preset,
            font:      state.font,
            size:      state.size,
            color:     state.color,
            position:  state.position,
            maxWords:  state.maxWords,
            stroke:    state.stroke,
            shadow:    state.shadow,
            savedAt:   Date.now(),
        };
        // Replace existing same-name fav if any
        list = list.filter(function(f){ return f.name !== name; });
        list.unshift(snapshot);
        writeFavs(list);
        reportFeature('style_fav_saved', { name: name });
        return true;
    };
    window.__acApplyStyleFavourite = function(name){
        var list = readFavs();
        var fav = list.find(function(f){ return f.name === name; });
        if (!fav) return false;
        // Apply each setting, refresh DOM controls, redraw overlay
        if (fav.preset)    state.preset    = fav.preset;
        if (fav.font)      state.font      = fav.font;
        if (fav.size)      state.size      = fav.size;
        if (fav.color)     state.color     = fav.color;
        if (fav.position)  state.position  = fav.position;
        if (typeof fav.maxWords === 'number') state.maxWords = fav.maxWords;
        if (typeof fav.stroke === 'boolean') state.stroke = fav.stroke;
        if (typeof fav.shadow === 'boolean') state.shadow = fav.shadow;
        // Sync controls (best-effort — some controls may be hidden if we don't expose them all)
        if (els.font)      els.font.value     = state.font;
        if (els.size)      els.size.value     = state.size;
        if (els.position)  els.position.value = state.position;
        if (els.maxwords)  els.maxwords.value = state.maxWords;
        if (els.stroke)    els.stroke.checked = state.stroke;
        if (els.shadow)    els.shadow.checked = state.shadow;
        // Highlight active preset + colour swatch
        var presetBtn = document.querySelector('.ac-preset[data-preset="' + state.preset + '"]');
        if (presetBtn) {
            els.presets.querySelectorAll('.ac-preset').forEach(function(b){ b.classList.remove('active'); });
            presetBtn.classList.add('active');
        }
        var colorBtn = document.querySelector('.ac-color-btn[data-color="' + state.color + '"]');
        if (colorBtn) {
            els.colorRow.querySelectorAll('.ac-color-btn').forEach(function(b){ b.classList.remove('active'); });
            colorBtn.classList.add('active');
        }
        if (els.overlay) els.overlay.className = 'ac-overlay pos-' + state.position;
        if (typeof window.__acRebuildLines === 'function') window.__acRebuildLines();
        if (window.__acState && window.__acState.activeLineIdx >= 0) {
            // Force overlay re-render
            window.__acState.activeLineIdx = -1;
        }
        // The blade exposes updateOverlayStyle internally — call it via window if present.
        if (typeof window.__acUpdateOverlayStyle === 'function') window.__acUpdateOverlayStyle();
        reportFeature('style_fav_applied', { name: name });
        return true;
    };
    window.__acDeleteStyleFavourite = function(name){
        var list = readFavs().filter(function(f){ return f.name !== name; });
        writeFavs(list);
        reportFeature('style_fav_deleted', { name: name });
    };

    /* ════════════════════════════════════════════════════════════
       GLOSSARY → Whisper initial_prompt
       ════════════════════════════════════════════════════════════ */
    // Split on newlines OR commas — users naturally type "Anthropic, Claude,
    // OpenAI" on one line and the prior per-line-only split treated that as
    // a single 30-char "term" that Whisper effectively ignored (it became a
    // junk prompt token rather than three brand-name hints).
    function splitGlossary(raw){
        return String(raw || '').split(/[\r\n,]+/).map(function(s){ return s.trim(); }).filter(Boolean);
    }
    function collectGlossary(){
        var ta = document.getElementById('ac-glossary');
        if (!ta) return '';
        var terms = splitGlossary(ta.value);
        if (!terms.length) return '';
        return terms.slice(0, 30).join(', ');
    }
    function countGlossaryTerms(){
        var ta = document.getElementById('ac-glossary');
        if (!ta) return 0;
        return splitGlossary(ta.value).length;
    }

    /* ════════════════════════════════════════════════════════════
       CONFIDENCE annotation (heuristic — see comments inside)
       ════════════════════════════════════════════════════════════ */
    function annotateConfidence(words){
        if (!Array.isArray(words)) return;
        for (var i = 0; i < words.length; i++){
            var w = words[i];
            if (!w || w.isEmoji) continue;
            var text = String(w.text || '').trim();
            var dur = Math.max(0.001, (w.end || 0) - (w.start || 0));
            var charsPerSec = text.length / dur;
            // Heuristics — combined into a 0..1 confidence value (1 = trusted).
            var conf = 1.0;
            // Very fast or very slow words are suspicious
            if (charsPerSec > 35 || charsPerSec < 1.2) conf -= 0.35;
            // Mixed scripts within one token (Latin + Cyrillic etc.) — almost always a Whisper miss
            if (hasMixedScripts(text)) conf -= 0.40;
            // Tokens that are all punctuation or single non-letter character
            if (/^[\W_]+$/.test(text)) conf -= 0.25;
            // Very long durations on short words (>2s for ≤3 chars) — likely spurious
            if (text.length <= 3 && dur > 2.0) conf -= 0.20;
            // Repeated character runs ("aaaaa", "....") — Whisper hallucination signal
            if (/(.)\1{4,}/.test(text)) conf -= 0.45;
            w.confidence = Math.max(0, Math.min(1, conf));
        }
    }
    function hasMixedScripts(s){
        var hasLatin    = /[A-Za-z]/.test(s);
        var hasCyrillic = /[А-Яа-яЁё]/.test(s);
        var hasCJK      = /[぀-ヿ㐀-鿿가-힯]/.test(s);
        var hasArabic   = /[؀-ۿ]/.test(s);
        var n = (hasLatin?1:0) + (hasCyrillic?1:0) + (hasCJK?1:0) + (hasArabic?1:0);
        return n >= 2;
    }

    /* ════════════════════════════════════════════════════════════
       PER-SEGMENT RE-TRANSCRIBE (line-level redo)

       User can redo a single transcript line if it came out wrong, without
       re-uploading or re-running Whisper on the whole video. We slice the
       cached PCM by the line's [start, end] in seconds, send to a fresh
       Whisper worker, then splice the result back into state.words.

       Optional `modelSize` parameter lets the user redo just this line
       with a more accurate (slower) model — common pattern: "Tiny ran on
       the whole file, this 5-second slice is wrong, redo with Small".
       ════════════════════════════════════════════════════════════ */
    window.__acRedoSegment = async function(lineIdx, opts){
        opts = opts || {};
        if (state.busy) { toast('Busy, try again', 'warning'); return; }
        if (!state.pcmCacheF32 || !state.pcmCacheSampleRate) {
            toast(I18N.redo_no_cache || 'Cached audio missing — generate captions first', 'warning');
            return;
        }
        var ln = state.lines[lineIdx];
        if (!ln || !ln.words.length) return;

        // Slice the cached PCM at this line's time-bounds. Add a small
        // pre/post pad so Whisper has context to lock onto.
        var pad = 0.4;
        var sr = state.pcmCacheSampleRate;
        var s0 = Math.max(0, Math.floor((ln.start - pad) * sr));
        var s1 = Math.min(state.pcmCacheF32.length, Math.ceil((ln.end + pad) * sr));
        if (s1 <= s0 || (s1 - s0) < sr * 0.3) {
            toast('Segment too short to redo', 'warning');
            return;
        }
        var slice = new Float32Array(s1 - s0);
        slice.set(state.pcmCacheF32.subarray(s0, s1));
        var sliceStartSec = s0 / sr;

        setBusy('transcribe');
        showProgress(els.tProg, els.tFill, els.tText, els.tPct);
        setProg(els.tFill, els.tText, els.tPct, 0.20, I18N.transcribe_recognizing || 'Recognizing speech', 20);

        try {
            // Переспрашивание одного сегмента идёт whisper'ом всегда: GigaAM без
            // word-level таймингов, а тут они и нужны (wordTimestamps: true).
            var modelSize = whisperModelSize(opts.modelSize);
            var langChoice = state.sourceLang || 'en';
            var glossaryText = collectGlossary();

            var redoResult = await runWhisper(slice, {
                language: langChoice,
                modelSize: modelSize,
                wordTimestamps: true,
                initialPrompt: glossaryText,
            }, function onStatus(m){
                if (m.type === 'chunk_done') setProg(els.tFill, els.tText, els.tPct, 0.85, I18N.transcribe_recognizing, 85);
                else if (m.type === 'download_progress' && m.data && m.data.progress != null) {
                    var pct = 0.20 + (m.data.progress / 100) * 0.40;
                    setProg(els.tFill, els.tText, els.tPct, pct, I18N.transcribe_downloading, Math.round(pct * 100));
                }
            });

            // Normalize and remap the slice's timeline back to the original video.
            var normalized = normalizeWhisperResult(redoResult, ln.end - ln.start + 2 * pad);
            for (var ni = 0; ni < normalized.words.length; ni++){
                normalized.words[ni].start = (normalized.words[ni].start || 0) + sliceStartSec;
                normalized.words[ni].end   = (normalized.words[ni].end   || 0) + sliceStartSec;
            }
            // Clamp to original line range so re-transcribed words don't leak
            // into adjacent lines on the timeline.
            normalized.words = normalized.words.filter(function(w){
                return w.end >= ln.start - 0.2 && w.start <= ln.end + 0.2;
            });
            for (var nj = 0; nj < normalized.words.length; nj++){
                normalized.words[nj].start = Math.max(ln.start, normalized.words[nj].start);
                normalized.words[nj].end   = Math.min(ln.end,   normalized.words[nj].end);
            }
            annotateConfidence(normalized.words);

            if (!normalized.words.length){
                toast(I18N.redo_no_words || 'Whisper returned no words for this segment', 'warning');
                return;
            }

            // Splice back into state.words: replace the words belonging to this line.
            var firstIdx = state.words.indexOf(ln.words[0]);
            if (firstIdx < 0) firstIdx = state.words.findIndex(function(w){ return w.start >= ln.start - 0.05; });
            var deleteCount = ln.words.length;
            state.words.splice(firstIdx, deleteCount, ...normalized.words);
            state.wordsOriginal = state.words.slice();
            rebuildLines();

            setProg(els.tFill, els.tText, els.tPct, 1, I18N.transcribe_done, 100);
            setTimeout(function(){ hideProgress(els.tProg); }, 800);
            reportFeature('redo_segment', { line: lineIdx, words: normalized.words.length, model: modelSize });
        } catch(err){
            hideProgress(els.tProg);
            console.error('[auto-captions] redo segment failed', err);
            toast(I18N.error + ': ' + (err.message || err), 'error');
        } finally {
            setBusy(null);
        }
    };

    /* ════════════════════════════════════════════════════════════
       CANCEL — terminate the in-flight whisper / VAD / translate / burn
       worker, hide progress UI, clear busy state. Workers are
       fire-and-forget terminations: the worker thread dies + its allocated
       WASM heap is reclaimed by the browser. State.words is left intact
       (if the cancel happened mid-transcribe with no words yet, the editor
       just shows the empty state — no half-rendered partial captions).
       ════════════════════════════════════════════════════════════ */
    window.__acCancelTranscribe = function(opts){
        // In-flight = an ACTIVE phase (state.busy is set for the whole
        // transcribe/translate/burn span) or a running burn. A warm IDLE
        // whisper worker is NOT in-flight: it just holds the downloaded model
        // between runs. Pre-2026-06-13 this checked worker existence, so
        // every post-completion cancel click / file switch terminated the
        // cached model, showed a bogus "Cancelled" toast and logged
        // phase:"unknown" (10/10 prod cancels in the 48 h window were these).
        if (!state.busy && !state.burning) return;

        var phase = state.busy;
        // reason: 'user' (cancel button / Esc) vs 'new_file' (loadFile's
        // double-upload guard) vs 'workspace_closed' (close button) — keeps
        // user-intent telemetry honest.
        var reason = (opts && opts.reason) || 'user';
        // Invalidate the in-flight async run. Terminating workers alone is
        // not enough: the transcribe function keeps executing past its
        // current await and would respawn workers (VAD) into the cancelled
        // state. The generation check (lostRun) makes it bail at the next
        // stage boundary.
        state.runGen = (state.runGen || 0) + 1;
        // Reject any dangling runWhisper await — terminate() below kills the
        // worker but never settles the promise, so without this the awaiting
        // __acTranscribe would hang (its finally + lostRun guard never run).
        // The catch maps errorType 'cancelled' to a silent return.
        if (typeof state.whisperAbort === 'function') {
            var ce = new Error('cancelled'); ce.errorType = 'cancelled'; ce.expected = true;
            try { state.whisperAbort(ce); } catch(e){}
        }
        // Русская модель живёт в своём воркере: без этого «Отмена» оставляла её
        // распознавать дальше — у 92-минутного видео ещё час (чат 730).
        if (typeof state.gigaamAbort === 'function') { try { state.gigaamAbort(); } catch(e){} }
        if (typeof state.lidAbort === 'function') { try { state.lidAbort(); } catch(e){} }
        if (typeof state.langAskAbort === 'function') { try { state.langAskAbort(); } catch(e){} }
        if (typeof state.translateAbort === 'function') { try { state.translateAbort(); } catch(e){} }
        try { if (state.whisperWorker) state.whisperWorker.terminate(); } catch(e){}
        try { if (state.vadWorker) state.vadWorker.terminate(); } catch(e){}
        try { if (state.translateWorker) state.translateWorker.terminate(); } catch(e){}
        try { if (typeof state.vadDeadline === 'function') state.vadDeadline(); } catch(e){}
        if (typeof state.vadAbort === 'function') {
            var ve = new Error('cancelled'); ve.errorType = 'cancelled'; ve.expected = true;
            try { state.vadAbort(ve); } catch(e){}
        }
        state.whisperWorker = null;
        state.whisperWorkerKey = null;
        state.vadWorker = null;
        state.translateWorker = null;
        state.vadDeadline = null;

        // Cancel during burn OR transcribe-extraction — FFmpeg WASM is the
        // long-running synchronous pipeline in both. Without this call the
        // user sees "Cancelled" then FFmpeg keeps grinding (30-90 s burn, or
        // minutes of audio-extract on a multi-hundred-MB upload) burning CPU
        // for a result nobody wants. False-promise cancel is a trust killer.
        if (phase === 'burn' || phase === 'transcribe' || state.burning) {
            try { if (state.hwAbort) state.hwAbort.abort(); } catch(_) {}
            try { if (window.FFmpegProcessor && typeof window.FFmpegProcessor.abort === 'function') window.FFmpegProcessor.abort(); } catch(e){}
            state.burning = false;
            if (els.burnProg) hideProgress(els.burnProg);
        }

        if (els.tProg) hideProgress(els.tProg);
        if (els.translateProg) hideProgress(els.translateProg);
        clearStreamPreview();
        state.busy = null;
        setBusy(null);
        // The toast is a response to the USER's cancel. The new_file path
        // already gives feedback (workspace remounts with the new file) —
        // a "Cancelled" toast there reads as an error.
        if (reason === 'user') toast(I18N.cancelled || 'Cancelled', 'info');
        reportFeature('transcribe_cancelled', { phase: phase || 'unknown', reason: reason });
    };

    /* ════════════════════════════════════════════════════════════
       PROSODY EMPHASIS — toggle handler.
       Re-analyses or strips emphasis on existing words without re-transcribing.
       ════════════════════════════════════════════════════════════ */
    window.__acToggleEmphasis = function(on){
        state.emphasisOn = !!on;
        if (!state.words || !state.words.length) return;
        if (on && state.pcmCacheF32 && typeof window.__acAnalyseProsody === 'function') {
            window.__acAnalyseProsody(state.pcmCacheF32, state.pcmCacheSampleRate || 16000, state.words);
            state.wordsOriginal = state.words.slice();
            rebuildLines();
            reportFeature('emphasis_toggled', { on: true });
        } else if (!on) {
            if (typeof window.__acClearProsody === 'function') window.__acClearProsody(state.words);
            state.wordsOriginal = state.words.slice();
            rebuildLines();
            reportFeature('emphasis_toggled', { on: false });
        }
    };

    /* ════════════════════════════════════════════════════════════
       SUBTITLE QUALITY SCORE — mean of confidence values

       Surfaced in the editor card head as a small badge so the user
       can decide at a glance whether to re-transcribe with a bigger
       model. Re-computed on every state.words change via rebuildLines.
       ════════════════════════════════════════════════════════════ */
    window.__acComputeQualityScore = function(){
        if (!state.words || !state.words.length) return null;
        var nonEmoji = state.words.filter(function(w){ return !w.isEmoji; });
        if (!nonEmoji.length) return null;
        var sum = 0;
        for (var i = 0; i < nonEmoji.length; i++){
            var c = (typeof nonEmoji[i].confidence === 'number') ? nonEmoji[i].confidence : 1;
            sum += c;
        }
        return Math.round((sum / nonEmoji.length) * 100);
    };

    /* ════════════════════════════════════════════════════════════
       AUTO-CLEAN transcript (filler + repeats + silences)
       ════════════════════════════════════════════════════════════ */
    window.__acApplyCleanup = function(){
        if (!window.__acCleaner) { toast('Cleaner not loaded', 'error'); return; }
        if (!state.words || !state.words.length) { toast('Generate captions first', 'warning'); return; }
        // Snapshot so undo can restore exactly. We snapshot wordsOriginal too because
        // translate uses it as the source for re-translation.
        state.cleanSnapshot = {
            words:         state.words.slice(),
            wordsOriginal: state.wordsOriginal.slice(),
        };
        var rmFillers   = !!(els.cleanFillers   && els.cleanFillers.checked);
        var rmRepeated  = !!(els.cleanRepeated  && els.cleanRepeated.checked);
        var trimSil     = !!(els.cleanSilences  && els.cleanSilences.checked);
        // User-supplied filler additions, one per line in the textarea.
        var customFillers = [];
        if (els.customFillers && els.customFillers.value) {
            customFillers = els.customFillers.value.split(/\r?\n/).map(function(s){ return s.trim(); }).filter(Boolean);
        }
        var res = window.__acCleaner.clean(state.words, {
            lang: state.sourceLang,
            removeFillers:    rmFillers,
            collapseRepeats:  rmRepeated,
            trimSilences:     trimSil,
            customFillers:    customFillers,
        });
        state.words = res.words;
        state.wordsOriginal = state.words.slice();
        rebuildLines();
        var msg = (I18N.clean_done || 'Removed :words words and :secs s of silence')
            .replace(':words', res.removedWords)
            .replace(':secs', res.secsCut);
        toast(msg, 'success');
        reportFeature('cleanup_applied', { removedWords: res.removedWords, secsCut: res.secsCut, fillers: rmFillers, repeated: rmRepeated, silences: trimSil });
    };
    window.__acUndoCleanup = function(){
        if (!state.cleanSnapshot) { toast('Nothing to undo', 'info'); return; }
        state.words = state.cleanSnapshot.words.slice();
        state.wordsOriginal = state.cleanSnapshot.wordsOriginal.slice();
        state.cleanSnapshot = null;
        rebuildLines();
        reportFeature('cleanup_undone', {});
    };

    /**
     * Transform Whisper chunks (segment- or word-level) into
     *   { words: [{text, start, end}], segments: [{start, end, text, words: [...]}] }
     */
    function normalizeWhisperResult(res, totalDur){
        var chunks = (res && res.chunks) || [];
        var words = [];
        var segments = [];

        if (!chunks.length) return { words: words, segments: segments };

        if (res.wordLevel) {
            // Each chunk is a word
            var prevEnd = 0;
            for (var i = 0; i < chunks.length; i++){
                var c = chunks[i];
                var text = (c.text || '').trim();
                if (!text) continue;
                var s = (c.timestamp && c.timestamp[0] != null) ? c.timestamp[0] : prevEnd;
                var e = (c.timestamp && c.timestamp[1] != null) ? c.timestamp[1] : s + 0.3;
                if (!isFinite(s)) s = prevEnd;
                if (!isFinite(e) || e <= s) e = s + 0.3;
                var wd = { text: text, start: s, end: e };
                if (c.ls) wd.ls = 1;           // GigaAM: the word's opening was written before it sounds
                words.push(wd);
                prevEnd = e;
            }
            // Rebuild segments from sentence-end boundaries
            var seg = { start: words.length ? words[0].start : 0, end: 0, text: '', words: [] };
            for (var j = 0; j < words.length; j++){
                seg.words.push(words[j]);
                if (/[\.\?\!…]$/.test(words[j].text) || j === words.length - 1) {
                    seg.end = words[j].end;
                    seg.text = seg.words.map(function(w){ return w.text; }).join(' ');
                    segments.push(seg);
                    if (j < words.length - 1) seg = { start: words[j + 1].start, end: 0, text: '', words: [] };
                }
            }
        } else {
            // Segment-level: distribute words linearly by character length within each segment
            for (var k = 0; k < chunks.length; k++){
                var cc = chunks[k];
                var txt = (cc.text || '').trim();
                if (!txt) continue;
                var ss = (cc.timestamp && cc.timestamp[0] != null) ? cc.timestamp[0] : 0;
                var ee = (cc.timestamp && cc.timestamp[1] != null) ? cc.timestamp[1] : ss + 1;
                if (!isFinite(ee) || ee <= ss) ee = Math.min((totalDur || ss + 1), ss + Math.max(1, txt.length * 0.06));
                var tokens = txt.split(/\s+/).filter(Boolean);
                var segDur = Math.max(0.1, ee - ss);
                var totalChars = tokens.reduce(function(a, t){ return a + t.length + 1; }, 0) || 1;
                var cursor = ss;
                var segWords = [];
                for (var t = 0; t < tokens.length; t++){
                    var share = (tokens[t].length + 1) / totalChars;
                    var wEnd = cursor + segDur * share;
                    var w = { text: tokens[t], start: cursor, end: wEnd };
                    segWords.push(w);
                    words.push(w);
                    cursor = wEnd;
                }
                segments.push({ start: ss, end: ee, text: txt, words: segWords });
            }
        }

        /* Anti-repetition guard. Whisper Tiny + q8 (the WASM fallback path) loops
           a filler token over noisy/music audio ("что-то что-то что-то …"). This
           is a hallucination, not real speech. collapseRepeats is a pure post-
           process: a clean transcript trips nothing (collapsed === 0 → untouched),
           so this can't degrade normal output. Both branches feed it the flat
           `words` array, so word-level (consecutive identical words) AND segment-
           level (repeats inside one chunk string, now split into words) are caught.
           When it fires we rebuild segments from the surviving words so SRT cues
           and the editor don't reference dropped tokens. */
        try {
            if (window.__acDedup && typeof window.__acDedup.collapseRepeats === 'function') {
                var dd = window.__acDedup.collapseRepeats(words);
                if (dd && dd.collapsed > 0) {
                    words = dd.words;
                    segments = rebuildSegmentsFromWords(words);
                    try { reportFeature('repeat_collapsed', { removed: dd.collapsed, kept: words.length }); } catch (_) {}
                }
            }
        } catch (ddErr) { /* never let the dedup guard break transcription */ }

        return { words: words, segments: segments };
    }

    /* Degenerate-timestamp sanitizer, applied to the FINAL (post VAD-remap)
       word timeline. Whisper loses temporal alignment on singing / long music
       and — once VAD-packed timestamps are remapped back to the real video
       clock — leaves many words pinned to one instant (invisible/stacked
       captions) or one word frozen across a long instrumental gap (a 50-60s
       held caption). sanitize() spreads collapsed runs + caps marathon holds
       so the burn is always watchable. A clean transcript passes through
       unchanged (changed === false), so normal speech is never degraded.
       MUST run after remapTimes — pre-remap the timeline is in packed-audio
       time where the gaps are still collapsed and the freeze isn't visible. */
    function sanitizeFinalTimings(normalized, totalDur){
        try {
            if (window.__acTimings && typeof window.__acTimings.sanitize === 'function') {
                var sz = window.__acTimings.sanitize(normalized.words, totalDur);
                if (sz && sz.changed) {
                    normalized.words = sz.words;
                    normalized.segments = rebuildSegmentsFromWords(normalized.words);
                    try { reportFeature('timings_sanitized', { collapsedRuns: sz.collapsedRuns, capped: sz.capped, words: normalized.words.length }); } catch (_) {}
                }
            }
        } catch (tzErr) { /* never let the timing guard break transcription */ }
        return normalized;
    }

    /* Where each GigaAM word SOUNDS. The model's time is when it WROTE the word:
       on the stand (her «Шортс» text, 2 voices × 2 tempos × clean / game bed,
       878 words) a cue started more than 50 ms after the word in 54 % of words
       on that time, in 38 % once the start is looked for in the sound (the
       censor's `ProfanityRegions.hitSpan`, same model, same failure), and in 5 %
       with the 80 ms lead the cue builder adds. The rule lives in
       `CaptionCues.placeOnSound` (start never later than the written start plus
       one CTC frame — on her real recording later moves landed quiet words on
       the next word's onset; dashes are not neighbours). Runs on the ORIGINAL
       timeline (`i16`), after the VAD remap. Whisper's word times come from
       another mechanism and are left alone. */
    function placeWordsOnSound(normalized, i16, res) {
        try {
            var PR = window.ProfanityRegions, CC = window.CaptionCues;
            var words = normalized && normalized.words;
            if (!PR || !CC || !res || !res.wordLevel || res.engine !== 'gigaam' || !i16 || !i16.length || !words || !words.length) return normalized;
            var moved = CC.placeOnSound(words, PR, i16, state.duration || (i16.length / 16000));
            normalized.segments = rebuildSegmentsFromWords(words);
            reportFeature('word_times_sound', { words: words.length, moved: moved });
        } catch (e) {
            try { reportFeature('word_times_sound_failed', { reason: String((e && e.message) || e).slice(0, 80) }); } catch (_) {}
        }
        return normalized;
    }

    /* «ё» в русской расшифровке — общий примитив RuYofication (словарь eyo SAFE: только
       однозначные замены, «все/всё» и «че/чё» он не трогает). Замер 22.10 на её записи (3 мин,
       та же речь): Whisper base написал «ё» в 3 словах из 289, словарь добавил 4 — «ее → её»,
       «нашел → нашёл» ×2, «пошел → пошёл»; GigaAM пишет «ё» сам (30 из 352, словарю нечего
       добавить; на 41 мин — 3 из 4630). Словарь грузится с начала прогона; не пришёл за 5 с —
       текст как был. «Писать «е» вместо «ё»» при выгрузке снимает всё обратно. */
    async function restoreYo(normalized, lang, res) {
        var Y = window.RuYofication;
        var words = normalized && normalized.words;
        if (String(lang || '').slice(0, 2).toLowerCase() !== 'ru' || !Y || typeof Y.restoreWords !== 'function' || !words || !words.length) return normalized;
        try {
            var ok = window.__bounded ? await window.__bounded(Y.ready(), 5000, 'yo_dict') : await Y.ready();
            if (!ok) { reportFeature('yo_restore_failed', { reason: 'dict' }); return normalized; }
            var changed = Y.restoreWords(words);
            (normalized.segments || []).forEach(function (sg) {
                if (sg && typeof sg.text === 'string') sg.text = Y.restore(sg.text).text;
            });
            reportFeature('yo_restored', { words: words.length, changed: changed, engine: (res && res.engine) || (openAiChosen() ? 'openai' : 'whisper') });
        } catch (e) {
            reportFeature('yo_restore_failed', { reason: String((e && (e.code || e.message)) || e).slice(0, 60) });
        }
        return normalized;
    }

    /* Rebuild segments from a flat word array on sentence-end boundaries.
       Used after collapseRepeats drops hallucinated words so segments stay
       consistent with the surviving word list (same logic as the word-level
       branch of normalizeWhisperResult). */
    function rebuildSegmentsFromWords(words){
        var segments = [];
        if (!words || !words.length) return segments;
        var seg = { start: words[0].start, end: 0, text: '', words: [] };
        for (var j = 0; j < words.length; j++){
            seg.words.push(words[j]);
            if (/[\.\?\!…]$/.test(words[j].text) || j === words.length - 1){
                seg.end = words[j].end;
                seg.text = seg.words.map(function(w){ return w.text; }).join(' ');
                segments.push(seg);
                if (j < words.length - 1) seg = { start: words[j + 1].start, end: 0, text: '', words: [] };
            }
        }
        return segments;
    }

    /* ════════════════════════════════════════════════════════════
       PHASE 5 — AUTO-EMOJI
       ════════════════════════════════════════════════════════════ */
    var emojiCache = null;
    async function loadEmojiDict(){
        if (emojiCache) return emojiCache;
        try {
            var r = await fetch('/js/emoji-keywords.json', { cache: 'force-cache' });
            if (!r.ok) throw new Error('emoji dict HTTP ' + r.status);
            emojiCache = await r.json();
            return emojiCache;
        } catch(e){
            console.warn('[auto-captions] emoji dict load failed', e);
            emojiCache = { en: {}, ru: {} };
            return emojiCache;
        }
    }

    window.__acApplyEmoji = async function(){
        if (!state.words || !state.words.length) return;
        var dict = await loadEmojiDict();
        // Start from current (post-translation) words, strip prior synthetic emojis first
        state.words = state.words.filter(function(w){ return !w.isEmoji; });

        if (!state.emojiOn) { rebuildLines(); return; }

        // Pick dictionary by source/target lang, fall back to en
        var lang = (state.translateOn ? state.translateTarget : (state.sourceLang || 'en'));
        var map = dict[lang] || dict.en || {};
        var injections = []; // { index, emoji }
        for (var i = 0; i < state.words.length; i++){
            var raw = (state.words[i].text || '').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
            if (!raw) continue;
            // Try exact match then stem (first 5+ chars)
            var em = map[raw] || map[raw.slice(0, 5)] || map[raw.slice(0, 4)];
            if (em) injections.push({ index: i, emoji: em });
        }
        // Inject from the end to keep indices valid
        for (var j = injections.length - 1; j >= 0; j--){
            var k = injections[j].index;
            var src = state.words[k];
            state.words.splice(k + 1, 0, {
                text: injections[j].emoji,
                start: src.end,
                end: src.end + 0.001,
                isEmoji: true,
                speaker: src.speaker,
            });
        }
        rebuildLines();
    };

    /* ════════════════════════════════════════════════════════════
       PHASE 6 — TRANSLATE

       Через общий движок `TranslatorEngine.translateTexts` — весь текст одним
       вызовом, пачками (docs/why/primitives/translate-texts.md). До 29.10.2026
       здесь был свой воркер transformers.js: Opus-MT пары или запасной
       NLLB-600M, а это ~900 МБ (энкодер 419 + декодер 476) на каждой паре без
       своей модели — zh→ru, ja→ru, ko→ru… — без единого слова человеку об
       объёме, и перевод фраза за фразой в WASM. Пару zh→ru за 45 дней не
       запускал никто; первым её искал чат 730 (китайское видео, 92 мин).
       ════════════════════════════════════════════════════════════ */
    function loadTranslatorEngine(){
        if (window.TranslatorEngine) return Promise.resolve(window.TranslatorEngine);
        if (loadTranslatorEngine.p) return loadTranslatorEngine.p;
        loadTranslatorEngine.p = new Promise(function(resolve, reject){
            var s = document.createElement('script');
            s.src = (typeof window.__jsSrc === 'function') ? window.__jsSrc('/js/translator-engine.js') : '/js/translator-engine.js';
            s.async = true;
            s.onload = function(){
                if (window.TranslatorEngine) return resolve(window.TranslatorEngine);
                loadTranslatorEngine.p = null;
                reject(new Error('TranslatorEngine missing after load'));
            };
            s.onerror = function(){ loadTranslatorEngine.p = null; reject(new Error('translator-engine.js failed to load')); };
            document.head.appendChild(s);
        });
        return loadTranslatorEngine.p;
    }

    window.__acRunTranslate = async function(){
        if (!state.words.length) { toast(I18N.translate_need_captions || 'Generate captions first', 'warning'); return; }
        if (!state.wordsOriginal.length) state.wordsOriginal = state.words.slice().filter(function(w){ return !w.isEmoji; });

        var target = state.translateTarget;
        // Неизвестный язык источника — «auto»: переводчик определит сам. Прежний
        // запасной 'en' переводил, скажем, немецкий текст «с английского».
        var source = state.sourceLang || 'auto';
        if (source === target) return;   // nothing to do

        showProgress(els.translateProg, els.translateFill, els.translateText, els.translatePct);
        setProg(els.translateFill, els.translateText, els.translatePct, 0.03, I18N.translate_translating, 3);
        setBusy('translate');
        var ctl = (typeof AbortController === 'function') ? new AbortController() : null;
        state.translateAbort = function(){ try { if (ctl) ctl.abort(); } catch(_) {} };
        var t0 = Date.now();

        try {
            var Engine = await loadTranslatorEngine();
            var sentences = groupForTranslation(state.wordsOriginal, source);
            var texts = sentences.map(function(s){ return s.text; });
            var stats = {};
            var answers = await Engine.translateTexts(texts, source, target, ctl ? ctl.signal : null, {
                _stats: stats,
                onProgress: function(p){
                    var f = 0.03 + Math.min(1, (p && p.frac) || 0) * 0.92;
                    setProg(els.translateFill, els.translateText, els.translatePct, f, I18N.translate_translating, Math.round(f * 100));
                },
                // Сервис отказал и движок ЖДЁТ повтора — сказать словами, а не стоять.
                onStatus: function(st){
                    if (st && st.state === 'retrying' && els.translateText) els.translateText.textContent = I18N.translate_retrying || I18N.translate_translating;
                },
            });

            /* `reason` у ответа — фраза НЕ переведена, в `text` оригинал. Такая
               фраза остаётся словами оригинала со своим временем и считается; не
               перевелось ничего — это не результат. */
            var failed = 0;
            var newWords = [];
            for (var i = 0; i < sentences.length; i++){
                var src = sentences[i];
                var a = answers[i];
                if (!a || a.reason || typeof a.text !== 'string' || !a.text.trim()) {
                    failed++;
                    for (var k = 0; k < src.words.length; k++) newWords.push(src.words[k]);
                    continue;
                }
                var tr = a.text.trim();
                var tokens = splitTargetTokens(tr, target);
                if (!tokens.length) continue;
                var segDur = Math.max(0.2, src.end - src.start);
                var totalChars = tokens.reduce(function(a, t){ return a + t.length + 1; }, 0) || 1;
                var cursor = src.start;
                for (var t = 0; t < tokens.length; t++){
                    var share = (tokens[t].length + 1) / totalChars;
                    var wEnd = cursor + segDur * share;
                    newWords.push({
                        text: tokens[t],
                        start: cursor,
                        end: wEnd,
                        translated: true,
                        speaker: src.speaker,
                    });
                    cursor = wEnd;
                }
                if (state.translateKeepOriginal && src.text) {
                    // Attach original as a secondary line via marker on first word of this sentence
                    if (newWords.length) newWords[newWords.length - tokens.length]._original = src.text;
                }
            }
            if (sentences.length && failed >= sentences.length) {
                var none = new Error(I18N.translate_failed || 'Translation failed — the translation service did not answer. Try again in a minute.');
                none.userMessage = none.message;
                none.errorType = 'translate_none';
                none.expected = true;
                throw none;
            }
            state.words = newWords;
            if (state.emojiOn) await window.__acApplyEmoji();
            rebuildLines();

            els.translateNote.textContent = failed
                ? fmt(I18N.translate_partial || ':n of :total phrases were not translated — press «Translate» again.', { n: failed, total: sentences.length })
                : (I18N.translate_approx || '');
            setProg(els.translateFill, els.translateText, els.translatePct, 1, '', 100);
            setTimeout(function(){ hideProgress(els.translateProg); }, 900);
            reportFeature('translate', { source: source, target: target, sentences: sentences.length, failed: failed,
                lane: stats.lane || '', requests: stats.requests || 0, ms: Date.now() - t0, keepOriginal: state.translateKeepOriginal });
        } catch(err){
            hideProgress(els.translateProg);
            var cancelled = (typeof window.__isUserCancel === 'function') ? window.__isUserCancel(err, ctl ? ctl.signal : null) : !!(ctl && ctl.signal.aborted);
            if (!cancelled) {
                console.error('[auto-captions] translate failed', err);
                toast((err && err.userMessage) || (I18N.error + ': ' + ((err && err.message) || err)), 'error');
                reportToolError('translate', err);
            }
            // Revert state.words AND state.translateOn together — leaving the
            // toggle on while the words are reverted breaks emoji map, ASS
            // keepOriginal output, the tool_complete `translated` payload, and
            // the UI label. The toggle is the single source of truth.
            state.words = state.wordsOriginal.slice();
            state.translateOn = false;
            if (els.translateToggle) els.translateToggle.checked = false;
            rebuildLines();
        } finally {
            state.translateAbort = null;
            setBusy(null);
        }
    };

    /* Фразы для переводчика. Письмо без пробелов (китайский, японский, тайский)
       склеивается без пробелов — Whisper отдаёт его кусками, и «你 好 世 界»
       переводчик читает хуже связного текста; пауза в речи ≥ 0.35 с внутри
       такой фразы — запятая. Конец фразы — знак конца (и полноширинные
       。！？, которых прежняя разбивка не знала и резала китайскую речь по
       14 иероглифов), пауза ≥ 1.2 с (перевод не размазывается по молчанию)
       или потолок длины. У фразы — её слова: непереведённая остаётся ими. */
    var NO_SPACE_LANG = /^(zh|ja|th|yue|lo|my|km|bo)$/;
    var CJK_RE = /[぀-ヿ㐀-鿿豈-﫿฀-๿]/;
    function groupForTranslation(words, lang){
        var out = [];
        var noSpace = NO_SPACE_LANG.test(String(lang || '').split('-')[0]);
        var bucket = [], text = '';
        function flush(){
            if (!bucket.length) return;
            out.push({ start: bucket[0].start, end: bucket[bucket.length - 1].end, text: text.trim(),
                       speaker: bucket[0].speaker, words: bucket });
            bucket = []; text = '';
        }
        for (var i = 0; i < words.length; i++){
            var w = words[i], t = String(w.text || '').trim();
            if (!t || w.isEmoji) continue;
            var prev = bucket.length ? bucket[bucket.length - 1] : null;
            var gap = prev ? (w.start - prev.end) : 0;
            if (prev && gap >= 1.2) flush();
            if (!text) text = t;
            else if (noSpace && CJK_RE.test(text.slice(-1)) && CJK_RE.test(t.charAt(0))) text += (gap >= 0.35 ? '，' : '') + t;
            else text += ' ' + t;
            bucket.push(w);
            var ends = /[.?!…。！？]$/.test(t);
            var full = noSpace ? text.length >= 28 : bucket.length >= 14;
            if (ends || full) flush();
        }
        flush();
        return out;
    }
    /* Перевод → «слова» для строк субтитров. Язык без пробелов (на китайский,
       японский) прежде становился ОДНИМ словом на всю фразу — строкой в 40
       знаков; теперь кусками до 6 знаков, знаки препинания — к своему куску. */
    function splitTargetTokens(text, lang){
        if (!NO_SPACE_LANG.test(String(lang || '').split('-')[0])) return text.split(/\s+/).filter(Boolean);
        return (text.match(/[^\s，。！？、；：,.!?]{1,6}[，。！？、；：,.!?]*|[，。！？、；：,.!?]+/g) || []).filter(Boolean);
    }

    function groupIntoSentences(words){
        var out = [];
        if (!words.length) return out;
        var bucket = [];
        for (var i = 0; i < words.length; i++){
            bucket.push(words[i]);
            var last = /[\.\?\!…]$/.test(words[i].text || '');
            if (last || i === words.length - 1 || bucket.length >= 14){
                out.push({
                    start: bucket[0].start,
                    end: bucket[bucket.length - 1].end,
                    text: bucket.map(function(w){ return w.text; }).join(' '),
                    speaker: bucket[0].speaker,
                });
                bucket = [];
            }
        }
        return out;
    }

    /* ════════════════════════════════════════════════════════════
       PHASE 7 — SPEAKER DIARIZATION (heuristic)
       ════════════════════════════════════════════════════════════ */
    window.__acApplySpeakers = function(){
        if (!state.words.length) return;
        if (!state.speakersOn) {
            for (var i = 0; i < state.words.length; i++) { delete state.words[i].speaker; delete state.words[i]._speakerColor; }
            if (window.__acSpeakerPaletteUI && typeof window.__acSpeakerPaletteUI.render === 'function') {
                try { window.__acSpeakerPaletteUI.render(); } catch(_) {}
            }
            rebuildLines();
            return;
        }
        // Group to pseudo-sentences first, then assign speakers
        var sentences = groupIntoSentences(state.words);
        var labels = diarize(sentences);
        // Propagate back to words
        for (var s = 0, wi = 0; s < sentences.length; s++){
            for (var k = 0; k < sentences[s].wordsCount; k++){
                if (state.words[wi]) state.words[wi].speaker = labels[s];
                wi++;
            }
        }
        // The groupIntoSentences helper doesn't emit wordsCount — patch by re-walking
        wi = 0;
        var senIdx = 0;
        for (var ii = 0; ii < state.words.length && senIdx < labels.length; ii++){
            state.words[ii].speaker = labels[senIdx];
            var last = /[\.\?\!…]$/.test(state.words[ii].text || '');
            if (last) senIdx++;
        }
        // Phase C-2 speaker-color palette — assign distinct color per speaker.
        if (window.__acSpeakerColors && typeof window.__acSpeakerColors.apply === 'function') {
            try { window.__acSpeakerColors.apply(state.words); } catch(_) {}
        }
        if (window.__acSpeakerPaletteUI && typeof window.__acSpeakerPaletteUI.render === 'function') {
            try { window.__acSpeakerPaletteUI.render(); } catch(_) {}
        }
        rebuildLines();
    };

    function diarize(sentences){
        var labels = [];
        var fingerprints = []; // [{ label, fp }]
        var nextCode = 0;
        var alphabet = ['A', 'B', 'C'];
        for (var i = 0; i < sentences.length; i++){
            var s = sentences[i];
            var fp = charBigramFingerprint(s.text);
            var pause = i > 0 ? (s.start - sentences[i - 1].end) : 0;
            var label;
            if (!fingerprints.length){
                label = alphabet[0];
                fingerprints.push({ label: label, fp: fp });
                nextCode = 1;
            } else {
                // Check similarity vs last speaker
                var lastLabel = labels[i - 1];
                var lastFp = fingerprints.filter(function(f){ return f.label === lastLabel; })[0];
                var sim = lastFp ? cosineSim(lastFp.fp, fp) : 0;
                if (pause > 1.5 && sim < 0.22) {
                    // Candidate switch — pick nearest existing cluster or create new (cap 3)
                    var best = null; var bestSim = -1;
                    for (var f = 0; f < fingerprints.length; f++){
                        var cs = cosineSim(fingerprints[f].fp, fp);
                        if (cs > bestSim) { bestSim = cs; best = fingerprints[f]; }
                    }
                    if (best && best.label !== lastLabel && bestSim > 0.25) label = best.label;
                    else if (fingerprints.length < alphabet.length) {
                        label = alphabet[fingerprints.length];
                        fingerprints.push({ label: label, fp: fp });
                    } else {
                        label = best ? best.label : lastLabel;
                    }
                } else {
                    label = lastLabel;
                }
            }
            labels.push(label);
        }
        return labels;
    }

    function charBigramFingerprint(text){
        var s = (text || '').toLowerCase().replace(/\s+/g, ' ').trim();
        var map = {};
        for (var i = 0; i < s.length - 1; i++) {
            var bg = s.substr(i, 2);
            map[bg] = (map[bg] || 0) + 1;
        }
        return map;
    }
    function cosineSim(a, b){
        var keys = {}; for (var k in a) keys[k] = 1; for (var k2 in b) keys[k2] = 1;
        var dot = 0, na = 0, nb = 0;
        for (var kk in keys){
            var va = a[kk] || 0, vb = b[kk] || 0;
            dot += va * vb; na += va * va; nb += vb * vb;
        }
        if (!na || !nb) return 0;
        return dot / Math.sqrt(na * nb);
    }

    /* ════════════════════════════════════════════════════════════
       PHASE 8 — BURN-IN MP4 (libass)
       ════════════════════════════════════════════════════════════ */
    // Phase C-2 multi-format fork — when the user picks 2+ platforms in
    // the multi-format card, we loop through each format and burn one MP4
    // per platform. Sequential by design: FFmpeg WASM is single-threaded
    // so parallel runs would queue inside FFmpegProcessor and lose per-
    // format progress. The original burn body lives in __acBurnSingle so
    // the loop just calls it with a formatOverride.
    window.__acBurnExport = async function(){
        /* Never let a degenerate transcript be burned UNKNOWINGLY. The reported
           failure was two ~5.5-minute burns of a 314 s video holding one word;
           the cost of a silent bad burn is minutes of the user's life, so a
           thin result asks once, states the numbers, and still lets them
           proceed if that is genuinely what they want. */
        try {
            var v = state.lastVerdict;
            if (v && v.level !== 'ok' && !state.thinBurnConfirmed) {
                var q = (I18N.thin_burn_confirm || 'Only :words word(s) were recognised for this video — burn anyway?')
                    .replace(':words', String(v.words))
                    .replace(':dur', (function(t){ t=Math.round(t||0); var m=Math.floor(t/60), ss=t%60; return m+':'+(ss<10?'0':'')+ss; })(v.durationSec));
                if (!window.confirm(q)) {
                    reportFeature('thin_burn_cancelled', { words: v.words, level: v.level });
                    return;
                }
                state.thinBurnConfirmed = true;
                reportFeature('thin_burn_confirmed', { words: v.words, level: v.level });
            }
        } catch (_) {}
        if (window.__acMultiFormat && typeof window.__acMultiFormat.shouldUseMultiFormat === 'function' && window.__acMultiFormat.shouldUseMultiFormat()) {
            try {
                var summary = await window.__acMultiFormat.forEachFormat(async function(format){
                    var formatPlan = window.__acMultiFormat.planForFormat(format);
                    await __acBurnSingle(formatPlan);
                });
                if (summary && summary.done > 0) {
                    var doneMsg = (I18N.multi_format_done || ':count files saved').replace(':count', String(summary.done));
                    toast(doneMsg, 'success');
                }
            } catch (mfErr) {
                console.error('[auto-captions] multi-format burn failed', mfErr);
                toast(I18N.error + ': ' + (mfErr.message || mfErr), 'error');
            }
            return;
        }
        return __acBurnSingle(null);
    };

    async function __acBurnSingle(formatOverride){
        if (state.burning) return;
        if (!state.file) { toast('No video', 'error'); return; }
        if (!state.words.length) { toast('Generate captions first', 'warning'); return; }
        if (!window.ASSBuilder) { toast('ASS builder not loaded', 'error'); return; }
        // Defensive: burn-in pipeline drops mid-flight on audio-only inputs.
        // setExportsEnabled normally hides the button outright; this catches
        // the rare path where the toggle hasn't applied yet (e.g. tests).
        if (window.__acWaveform && window.__acWaveform.isAudioOnly && window.__acWaveform.isAudioOnly(state.file)) {
            toast(I18N.burn_audio_only_hint || 'Burn-in needs a video file — export SRT/VTT/ASS to use captions with audio.', 'warning');
            return;
        }
        /* Soft daily cap — the BURN is the metered run. Transcribe and the SRT/VTT/ASS
           text exports are free: measured over 14 days, the abusive tail lives entirely
           in burn (peak 36/day), and metering transcribe too would have blocked only 2
           more runs while charging 116 user-days instead of 20 — i.e. it would tax the
           normal transcribe→burn workflow twice for almost no protection.
           The unit is the SOURCE VIDEO, not the click (Farid 2026-08-20, when the cap
           went 5 → 2). Passing the file fingerprint makes every later burn of the same
           file today free, which is what the label "2 видео в день" actually promises:
           changing the style and re-exporting, or picking five platforms in "Экспорт под
           несколько платформ", must not eat five days' worth of allowance from one
           video. Until 2026-08-20 this called begin() with no key — one token per
           PRODUCED file — which at a cap of 5 was merely rough and at a cap of 2 would
           have made multi-format unusable on the first press.
           Gated below all four cheap bails (already burning, no file, no captions,
           audio-only) so a refused burn costs nothing, and above state.burning/setBusy
           so a wall-hit can't wedge the UI.
           commit() when the encoded blob exists — the render is the work, the save is
           not. abort() in the finally covers the catch, including the cancel branch. */
        var myRun = window.__toolLimit
            ? window.__toolLimit.begin('auto-captions',
                (window.__toolLimit.fileKey && state.file) ? window.__toolLimit.fileKey(state.file) : null)
            : null;
        if (window.__toolLimit && !myRun) return;   // wall hit — note already shown

        state.burning = true;
        var burnGen = state.runGen;   // __acCancelTranscribe bumps it: THE burn's cancel signal
        saveTranscriptCheckpoint('burn');   // прожиг длинный: слепок с правками — до него, а не после
        setBusy('burn');
        showProgress(els.burnProg, els.burnFill, els.burnText, els.burnPct);
        setProg(els.burnFill, els.burnText, els.burnPct, 0, I18N.export_burn_progress, 0);
        // Etalon: honest ETA for the burn stage. The label-derived key MUST
        // match setProg()'s own (String(label).slice(0,24)) so update() finds
        // this begun stage and appends "~Xm left". Ended in the finally below.
        var burnEtaKey = String(I18N.export_burn_progress || 'burn').slice(0, 24);
        try { if (window.__acETA && window.__acETA.beginStage) window.__acETA.beginStage(burnEtaKey); } catch(_){}

        try {
            // Extract emoji events BEFORE stripping from ASS. ASS burns only the text;
            // emojis are composited on top via FFmpeg overlay filter with Twemoji PNGs.
            var emojiEvents = collectEmojiEvents(state.words);
            var hasEmoji = state.emojiOn && emojiEvents.length > 0;

            // Edit-by-transcript word cuts — when the user deleted any words
            // in the editor, snapshot the cut plan + remap state.lines so the
            // ASS file is built against the shorter post-cut timeline.
            // Restored on the way out (finally) so the editor still shows
            // original timestamps after the burn finishes.
            var ebt = window.__acEditByTranscript;
            var ebtKeep = null, ebtSavedLines = null, ebtSavedDuration = null;
            if (ebt && typeof ebt.hasCuts === 'function' && ebt.hasCuts()) {
                ebtKeep = ebt.getKeepList(state.duration || 0);
                ebtSavedLines = state.lines;
                ebtSavedDuration = state.duration;
                var remappedWords = (typeof ebt.mapWordsToCutTimeline === 'function')
                    ? ebt.mapWordsToCutTimeline(state.words)
                    : state.words;
                // Rebuild lines on the cut timeline so libass renders against
                // the same seconds the cut MP4 will play. groupWordsIntoLines
                // is a top-level helper exposed via rebuildLines closure —
                // here we cheat and call rebuildLines() against the remapped
                // words by temporarily swapping state.words.
                var savedWords = state.words;
                state.words = remappedWords;
                rebuildLines();
                state.words = savedWords;
                state.duration = ebtKeep.totalDuration;
                reportFeature('edit_by_transcript_burn', { cuts: Math.max(0, ebtKeep.ranges.length - 1), cutSec: +(ebtKeep.cutTotal || 0).toFixed(2) });
            }

            // Shared face+brand assembly with __acDownloadAss so the two
            // export paths never drift. stripEmoji=true because the burn
            // pipeline composites Twemoji PNGs via FFmpeg overlay separately
            // (libass color-emoji is unreliable).
            var assBuilt = buildDecoratedAss({ stripEmoji: true });
            var ass = assBuilt.ass;
            var brandPlan = assBuilt.brandPlan;
            if (brandPlan && brandPlan.assDialogue) {
                var brandKitForTelemetry = window.__acBrandKitState || (window.__acBrandKit && window.__acBrandKit.read()) || {};
                reportFeature('brand_watermark_burned', { length: (brandKitForTelemetry.watermarkText || '').length, position: brandKitForTelemetry.watermarkPosition });
            }

            /* ── Аппаратный лейн (WebCodecs + 2D-канва) ─────────────────────
               Замер 09.09.2026, 106 с 1080×1920: FFmpeg-WASM (libass+libx264,
               один поток) — 991 с; WebCodecsPipeline с наложением — 47.7 с.
               Прод: медиана прожига 9.1× длины ролика, на телефоне 18.2×.
               Берём его для «обычного» прожига: только надписи (11 пресетов),
               без эмодзи-картинок, вырезов, кадрирования, зума, бренд-кита,
               заглушения и учёта лица — всё это остаётся у FFmpeg-лейна.
               Любой отказ лейна (нет WebCodecs, кодек не поднялся, ошибка
               на середине) → тихий откат на прежний путь; отмена — нет. */
            var hwFlags = {
                hasEmoji: !!hasEmoji,
                ebtCuts: !!ebtKeep,
                magicCut: !!(window.__acMagicCut && typeof window.__acMagicCut.isActive === 'function' && window.__acMagicCut.isActive()),
                reframe: !!(window.__acAutoReframe && typeof window.__acAutoReframe.isEnabled === 'function' && window.__acAutoReframe.isEnabled()),
                autoZoom: !!(window.__acAutoZoom && typeof window.__acAutoZoom.isEnabled === 'function' && window.__acAutoZoom.isEnabled()),
                brandKit: !!(brandPlan && (brandPlan.assDialogue || (brandPlan.extraInputs && brandPlan.extraInputs.length))),
                muteWord: !!(window.__acMuteWord && typeof window.__acMuteWord.hasMutes === 'function' && window.__acMuteWord.hasMutes()),
                translateKeep: !!(state.translateOn && state.translateKeepOriginal),
                faceAvoid: !!(els.faceAvoidToggle && els.faceAvoidToggle.checked),
            };
            /* Прежний лейн (FFmpeg-WASM: libass + x264 в один поток, выход в MEMFS)
               берёт только то, что брал, пока зона пускала файлы до кида 'video'.
               С 01.10.2026 зона принимает видео до 16 ГиБ: распознавание и текст
               субтитров работают на таком файле целиком, а прожечь его умеет
               только аппаратный лейн. Решаем ДО работы: иначе 3-ГБ ролик с
               эмодзи или бренд-китом часами кодировался бы в WASM и умер бы на
               стене MEMFS, а аппаратный сбой на нём молча уводил бы туда же. */
            var ffFits = !(state.file.size > ffBurnCeiling());
            var refuseBigBurn = function(why, err){
                if (ebtSavedLines) {
                    state.lines = ebtSavedLines;
                    state.duration = ebtSavedDuration;
                    rebuildLines();
                    ebtSavedLines = null;
                }
                hideProgress(els.burnProg);
                var tpl = why === 'features' ? I18N.burn_big_features : I18N.burn_big_hw_failed;
                toast(String(tpl || 'This video is too large to burn with these settings.')
                    .replace(':limit', fmtBytesShort(ffBurnCeiling()))
                    .replace(':features', burnFeatureNames(hwFlags).join(', ')), 'warning');
                reportFeature('burn_refused_big', {
                    why: why,
                    mb: Math.round(state.file.size / 1048576),
                    flags: Object.keys(hwFlags).filter(function(k){ return hwFlags[k]; }).join(','),
                    reason: err ? String((err && err.message) || err).slice(0, 100) : '',
                });
            };
            var hwResult = null;
            if (window.__acCanvasCaptions && window.WebCodecsPipeline && window.__acCanvasCaptions.eligible(state, hwFlags)) {
                try {
                    hwResult = await burnHardware(formatOverride);
                } catch (hwErr) {
                    if (hwErr && hwErr.name === 'AbortError') throw hwErr;
                    console.warn('[auto-captions] hardware burn failed, falling back to FFmpeg:', hwErr && hwErr.message);
                    reportFeature('hw_burn_fallback', { reason: String((hwErr && hwErr.message) || hwErr).slice(0, 120), refused: ffFits ? 0 : 1 });
                    if (!ffFits) { refuseBigBurn('hw_failed', hwErr); return; }
                    hwResult = null;
                }
            } else if (!ffFits) {
                refuseBigBurn('features');
                return;
            } else {
                var skipWhy = !window.WebCodecsPipeline ? 'no_pipeline' : !window.__acCanvasCaptions ? 'no_renderer'
                    : Object.keys(hwFlags).filter(function(k){ return hwFlags[k]; }).join(',') || 'preset';
                reportFeature('hw_burn_skipped', { why: skipWhy });
            }

            if (!hwResult) {
            await window.FFmpegProcessor.init();
            await window.FFmpegProcessor.writeFile('subs.ass', new TextEncoder().encode(ass));

            // Font embed: write fonts from /vendor/fonts/ into FS /fonts/ — script-aware (skip
            // CJK / Arabic packs when captions don't need them).
            var fullCaptionText = state.words.map(function(w){ return w.text; }).join(' ');
            await ensureFontsInFfmpeg(fullCaptionText);
            }

            // Real encode progress rides on `ffmpeg-progress` (detail = a raw
            // 0..1 number, emitted from FFmpeg's stderr time= via e.on('progress')
            // in ffmpeg-processor.js). It does NOT ride on `ffmpeg-status`, which
            // only carries init/download PHASES (downloading/instantiating/ready)
            // and never a fraction — listening to it left the burn bar frozen at
            // 0 % for the entire (single-thread, slower-than-realtime) encode and
            // then snapped to 100 %, which is exactly the "прожигание пошло, всё
            // на 0, ничего не двигается" bug report. Reader tolerates both a raw
            // number and the legacy {progress}/{percent} object shapes.
            var burnProg = function(e){
                var d = e.detail;
                var p = (typeof d === 'number') ? d
                      : (d && typeof d.progress === 'number') ? d.progress
                      : (d && typeof d.percent === 'number') ? d.percent / 100
                      : null;
                if (p === null) return;
                if (p < 0) p = 0; else if (p > 1) p = 1;
                setProg(els.burnFill, els.burnText, els.burnPct, p, I18N.export_burn_progress, Math.round(p * 100));
            };
            window.addEventListener('ffmpeg-progress', burnProg);

            var outResult;
            var exporter = state.exporter;
            try {
                if (hwResult) {
                    outResult = hwResult;
                } else if (hasEmoji) {
                    /* Emoji overlay path passes a multi-input filter_complex
                       graph to the exporter via its extraFilterComplex slot.
                       The exporter generates the codec/quality/output args
                       (matching user's format + quality selection); the tool
                       contributes the filter graph and -map directives that
                       reference the multi-input stream layout. */
                    outResult = await burnWithEmojiOverlay(emojiEvents, ass, state.position, state.file, exporter);
                } else if (exporter) {
                    /* Standard burn path — let VideoExporter generate the args.
                       extraFilters supplies the libass burn-in filter, captionsAware
                       in the exporter ensures it runs AFTER any resize.

                       Brand-kit logo: when present, route through multi-input
                       graph (logo as extra input → scale + alpha → overlay →
                       subtitles). Watermark text was already appended to ASS
                       above, so it rides through libass. */
                    var brandLogoPresent = brandPlan && brandPlan.extraInputs && brandPlan.extraInputs.length;
                    if (brandLogoPresent) {
                        var brandFilter = brandPlan.filterChainSegment + ';' + brandPlan.outputTag + 'subtitles=subs.ass:fontsdir=.[vout]';
                        var brandPlanArgs = exporter.plan({
                            extraFilterComplex: brandFilter,
                            extraOutputMap:     ['[vout]', '0:a?'],
                            audioCopy:          true,
                            outputBase:         baseName(state.file.name) + '-captioned',
                        });
                        outResult = await ffStreamMulti(
                            [state.file].concat(brandPlan.extraInputs),
                            brandPlanArgs.args,
                            brandPlanArgs.outputName
                        );
                        var brandKitForLogo = window.__acBrandKitState || (window.__acBrandKit && window.__acBrandKit.read()) || {};
                        reportFeature('brand_logo_burned', { position: brandKitForLogo.logoPosition, size: brandKitForLogo.logoSize });
                    } else {
                        // ─── Phase C top-4 burn composition ───
                        // Order: auto-reframe (crop) → auto-zoom (zoompan) → cut filters → libass.
                        // Each module returns a filter string or null; we chain them defensively.

                        // Auto-reframe — re-frames to target aspect, tracks speaker via face samples.
                        var reframePlan = null;
                        if (window.__acAutoReframe && typeof window.__acAutoReframe.planForBurn === 'function') {
                            try {
                                reframePlan = window.__acAutoReframe.planForBurn({
                                    faceSamples: state.faceSamples,
                                    videoWidth:  els.video && els.video.videoWidth,
                                    videoHeight: els.video && els.video.videoHeight,
                                    videoEl:     els.video,
                                });
                                if (reframePlan && reframePlan.filter) {
                                    reportFeature('auto_reframe_burned', {
                                        aspect:  (typeof window.__acAutoReframe.getAspectRatio === 'function') ? window.__acAutoReframe.getAspectRatio() : null,
                                        targetW: reframePlan.targetW, targetH: reframePlan.targetH,
                                        tracked: !!reframePlan.tracked,
                                    });
                                }
                            } catch (rfErr) {
                                console.warn('[auto-captions] auto-reframe plan failed:', rfErr.message);
                                reportFeature('auto_reframe_failed', { reason: rfErr.message });
                            }
                        }

                        // Auto-zoom — punches in around prosody-emphasis peaks.
                        var azFilter = '';
                        try {
                            if (window.__acAutoZoom && typeof window.__acAutoZoom.isEnabled === 'function' && window.__acAutoZoom.isEnabled()) {
                                var vw = (els.video && els.video.videoWidth)  || 1920;
                                var vh = (els.video && els.video.videoHeight) || 1080;
                                var vf = 30;
                                azFilter = window.__acAutoZoom.buildFilterArgs(state.words, vw, vh, vf) || '';
                            }
                        } catch (zErr) { try { console.warn('[auto-captions] auto-zoom build failed', zErr); } catch(_){} }

                        // Magic Cut — cleaner-driven select/aselect to remove fillers + long silences from VIDEO not just captions.
                        var magicFilter = null;
                        try {
                            if (window.__acMagicCut && typeof window.__acMagicCut.isActive === 'function' && window.__acMagicCut.isActive()) {
                                magicFilter = window.__acMagicCut.buildFFmpegSelectFilter(state.duration);
                            }
                        } catch (mcErr) { try { console.warn('[auto-captions] magic-cut build failed', mcErr); } catch(_){} }

                        var plan;
                        if (ebtKeep) {
                            // Edit-by-transcript word-cut path (user-deleted ranges take priority over magic-cut in v1).
                            var cutFilter = ebt.buildFFmpegSelectFilter(ebtKeep, state.duration || 0);
                            var videoChain = '[0:v]' + cutFilter.video;
                            if (reframePlan && reframePlan.filter) videoChain += ',' + reframePlan.filter;
                            if (azFilter) videoChain += ',' + azFilter;
                            videoChain += ',ass=subs.ass:fontsdir=.[vout]';
                            plan = exporter.plan({
                                extraFilterComplex: videoChain + ';[0:a]' + cutFilter.audio + '[aout]',
                                extraOutputMap:     ['[vout]', '[aout]'],
                                audioCopy:          false,    // cuts mid-stream — must re-encode
                                outputBase:         baseName(state.file.name) + '-captioned-cut',
                            });
                        } else if (magicFilter) {
                            // Magic-cut path (no per-word user cut; cleaner-driven trimming of fillers/silences).
                            var mcVideo = '[0:v]' + magicFilter.vf;
                            if (reframePlan && reframePlan.filter) mcVideo += ',' + reframePlan.filter;
                            if (azFilter) mcVideo += ',' + azFilter;
                            mcVideo += ',ass=subs.ass:fontsdir=.[vout]';
                            plan = exporter.plan({
                                extraFilterComplex: mcVideo + ';[0:a]' + magicFilter.af + '[aout]',
                                extraOutputMap:     ['[vout]', '[aout]'],
                                audioCopy:          false,
                                outputBase:         baseName(state.file.name) + '-captioned',
                            });
                            reportFeature('cleanup_burned_video', {
                                ranges:         (magicFilter.ranges || []).length,
                                total_secs_cut: (typeof window.__acMagicCut.previewSecondsCut === 'function') ? window.__acMagicCut.previewSecondsCut() : 0,
                            });
                        } else {
                            // Phase C-3 mute-word — when user marked words to
                            // mute, prepend volume=0:enable=between(t,a,b) to
                            // the AUDIO branch. Forces audio re-encode (cannot
                            // -c:a copy when applying a filter). Composes with
                            // every other path: cuts strip first → mute filter
                            // runs on the already-shortened audio.
                            var muteAudioFilter = (window.__acMuteWord && typeof window.__acMuteWord.hasMutes === 'function' && window.__acMuteWord.hasMutes())
                                ? window.__acMuteWord.buildFFmpegVolumeFilter()
                                : null;
                            // No cuts — simple -vf chain (still composes reframe + zoom on top of libass).
                            var burnFilters = '';
                            // Multi-format: prepend scale+pad for the platform preset.
                            if (formatOverride && formatOverride.extraFilters) burnFilters += formatOverride.extraFilters + ',';
                            if (reframePlan && reframePlan.filter) burnFilters += reframePlan.filter + ',';
                            if (azFilter) burnFilters += azFilter + ',';
                            burnFilters += 'ass=subs.ass:fontsdir=.';
                            if (muteAudioFilter) {
                                // Route through filter_complex so we can patch
                                // the audio chain. Video keeps its simple path.
                                plan = exporter.plan({
                                    extraFilterComplex: '[0:v]' + burnFilters + '[vout];[0:a]' + muteAudioFilter + '[aout]',
                                    extraOutputMap:     ['[vout]', '[aout]'],
                                    audioCopy:          false,
                                    outputBase:         baseName(state.file.name) + '-captioned' + (formatOverride && formatOverride.outputBase ? formatOverride.outputBase : ''),
                                });
                                reportFeature('mute_word_burned', { ranges: (window.__acMuteWord.getMuteList() || []).length });
                            } else {
                                plan = exporter.plan({
                                    extraFilters:    burnFilters,
                                    audioCopy:       true,         // caption-only burn: never re-encode audio
                                    outputBase:      baseName(state.file.name) + '-captioned' + (formatOverride && formatOverride.outputBase ? formatOverride.outputBase : ''),
                                });
                            }
                        }
                        outResult = await ffStream(state.file, plan.args, plan.outputName);
                        if (azFilter) reportFeature('auto_zoom_burned', {
                            intensity: (typeof window.__acAutoZoom.getIntensity === 'function') ? window.__acAutoZoom.getIntensity() : 'unknown',
                            events:    (azFilter.match(/between\(t/g) || []).length,
                        });
                    }
                } else {
                    /* Fallback if exporter didn't mount (older deployment / SW cache). */
                    var args = [
                        '-vf', 'ass=subs.ass:fontsdir=.',
                        '-c:a', 'copy',
                        '-c:v', 'libx264',
                        '-preset', 'ultrafast',
                        '-crf', '23',
                        '-pix_fmt', 'yuv420p',
                    ];
                    outResult = await ffStream(
                        state.file,
                        args,
                        baseName(state.file.name) + '-captioned.mp4'
                    );
                }
            } finally {
                // Restore original (uncut) state.lines + duration so the editor
                // keeps showing real source timestamps after the burn.
                if (ebtSavedLines) {
                    state.lines = ebtSavedLines;
                    state.duration = ebtSavedDuration;
                    rebuildLines();
                }
                window.removeEventListener('ffmpeg-progress', burnProg);
            }
            var outBlob = outResult && outResult.blob ? outResult.blob : outResult;

            /* Суффикс площадки («-tiktok») — в имени СОХРАНЯЕМОГО файла. Он был
               только в имени выхода внутри FFmpeg, а скачивалось «…-captioned.mp4»
               для каждой площадки: за 60 дней на RU нет ни одного имени с
               суффиксом, браузер нумеровал копии (1), (2). */
            var fmtSuffix = (formatOverride && formatOverride.outputBase) ? formatOverride.outputBase : '';
            var outName = (exporter && !hasEmoji && !hwResult)
                ? exporter.plan({ outputBase: baseName(state.file.name) + '-captioned' + fmtSuffix }).outputName
                : (baseName(state.file.name) + '-captioned' + fmtSuffix + '.mp4');
            if (myRun) myRun.commit();   // captioned file rendered → bill one run (not the save)
            window.FFmpegProcessor.download(outBlob, outName);
            setProg(els.burnFill, els.burnText, els.burnPct, 1, '', 100);
            setTimeout(function(){ hideProgress(els.burnProg); }, 1200);
            reportToolComplete({
                action: 'burn',
                preset: state.preset,
                words: state.words.length,
                durationSec: Math.round(state.duration || 0),
                speakers: state.speakersOn,
                translated: state.translateOn,
                emoji: state.emojiOn,
                emojiBurned: hasEmoji ? emojiEvents.length : 0,
                outputMB: Math.round((outBlob.size || 0) / (1024 * 1024)),
                lane: hwResult ? 'webcodecs' : 'ffmpeg',
                hw_ms: hwResult && hwResult.hwMs ? Math.round(hwResult.hwMs) : 0,
                hw_frames: hwResult && hwResult.frames ? hwResult.frames : 0,
            });
        } catch(err){
            hideProgress(els.burnProg);
            // Cancel via FFmpegProcessor.abort() throws 'terminate' / aborted —
            // suppress the error toast so the cancel UI doesn't look broken.
            /* …but the cancel is known by the run generation the handler bumps,
               not by the text: a crash-driven terminate and a dying runtime
               say the same words, and the burn then failed in silence. */
            if (state.runGen !== burnGen) {
                // AbortError/«cancelled» — отмена аппаратного лейна (WebCodecsPipeline
                // бросает DOMException('cancelled','AbortError')), не ошибка.
                state.burning = false;
                setBusy(null);
                return;
            }
            console.error('[auto-captions] burn failed', err);
            // Prefer the classified user-facing message that FFmpegProcessor
            // annotates onto its errors (errorType + userMessage) — passing
            // the raw English "moov atom not found" through 9 locales is the
            // exact pattern Phase A is meant to retire.
            var burnMsg = (err && err.userMessage) || (err && err.message) || String(err);
            toast(I18N.error + ': ' + burnMsg, 'error');
            reportToolError('burn', err);
        } finally {
            if (myRun) myRun.abort();   // idempotent after commit; cancel / failure → never bills
            state.burning = false;
            try { if (window.__acETA && window.__acETA.endStage) window.__acETA.endStage(burnEtaKey); } catch(_){}
            setBusy(null);
        }
    };

    /* ─── Twemoji PNG burn-in (color emoji composite) ───────────────────────
       libass does not render bitmap color-emoji fonts reliably, so we composite
       PNGs from the Twemoji CDN via FFmpeg -filter_complex overlay.
       Each emoji word becomes an overlay enabled for [start-0.1, end+0.8] with
       a small bounce scale to mirror TikTok-style timing. */

    /* Аппаратный прожиг: декод → 2D-наложение надписей → VideoEncoder (GPU).
       Возвращает { blob, hwMs, frames } или бросает (AbortError — отмена,
       остальное — повод для отката на FFmpeg-лейн у вызывающего). */
    async function burnHardware(formatOverride){
        var WCP = window.WebCodecsPipeline;
        var cap = await WCP.isSupported({});
        if (!cap || !cap.ok) { var e0 = new Error('webcodecs: ' + ((cap && cap.reason) || 'unsupported')); e0.hwReason = (cap && cap.reason) || 'unsupported'; throw e0; }
        if (state.speakersOn && window.__acSpeakerColors && typeof window.__acSpeakerColors.apply === 'function') {
            try { window.__acSpeakerColors.apply(state.words); } catch(_) {}
        }
        var renderer = await window.__acCanvasCaptions.create(state.lines, {
            preset: state.preset, color: state.color, font: state.font, size: state.size,
            position: state.position, stroke: state.stroke, shadow: state.shadow, speakers: state.speakersOn,
        });
        if (renderer.substituted && typeof window.__ae === 'function') {
            try { window.__ae('ac_font_substituted', { from: renderer.substituted.from, to: renderer.substituted.to, preset: state.preset, lane: 'webcodecs' }); } catch(_) {}
        }
        state.hwAbort = (typeof AbortController !== 'undefined') ? new AbortController() : null;
        var t0 = performance.now();
        try {
            var runOpts = {
                file: state.file,
                slug: 'auto-captions',
                params: {},
                crf: 23,
                overlay: renderer.draw,
                onProgress: function(p){
                    if (!(p >= 0)) return;
                    if (p > 1) p = 1;
                    setProg(els.burnFill, els.burnText, els.burnPct, p, I18N.export_burn_progress, Math.round(p * 100));
                },
                abortSignal: state.hwAbort ? state.hwAbort.signal : undefined,
            };
            /* «Экспорт под несколько платформ»: кадр вписывается в формат
               площадки с чёрными полями, как у FFmpeg-лейна (scale…
               force_original_aspect_ratio=decrease + pad), а надписи ложатся
               поверх кадра ФОРМАТА (overlay рисуется над холстом geometry2d).
               С 09.09 до 01.10.2026 этот лейн формат молча пропускал: N
               площадок давали N одинаковых копий исходной геометрии. */
            var fw = formatOverride && +formatOverride.width, fh = formatOverride && +formatOverride.height;
            if (fw > 0 && fh > 0) {
                runOpts.geometry2d = {
                    width: fw,
                    height: fh,
                    draw: function(ctx, video, W, H, _i, _t, _frame, meta){
                        var sw = (meta && meta.dispW) || W, sh = (meta && meta.dispH) || H;
                        var k = Math.min(W / sw, H / sh);
                        var dw = Math.round(sw * k), dh = Math.round(sh * k);
                        ctx.fillStyle = '#000';
                        ctx.fillRect(0, 0, W, H);
                        video(0, 0, sw, sh, Math.round((W - dw) / 2), Math.round((H - dh) / 2), dw, dh);
                    },
                };
            }
            var res = await WCP.run(runOpts);
            if (!res || !res.blob || !res.blob.size) throw new Error('webcodecs: empty output');
            return { blob: res.blob, hwMs: performance.now() - t0, frames: res.frames || 0, encoder: cap.encoderHardware ? 'hw' : 'sw' };
        } finally {
            state.hwAbort = null;
        }
    }

    function collectEmojiEvents(words){
        var out = [];
        for (var i = 0; i < words.length; i++){
            var w = words[i];
            if (!w.isEmoji) continue;
            var cps = [];
            var txt = w.text || '';
            for (var j = 0; j < txt.length;){
                var cp = txt.codePointAt(j);
                if (cp) cps.push(cp.toString(16));
                j += (cp > 0xFFFF) ? 2 : 1;
            }
            if (!cps.length) continue;
            // Twemoji drops U+FE0F variation selector in file names
            var filename = cps.filter(function(c){ return c !== 'fe0f'; }).join('-');
            out.push({
                text: txt,
                cp: filename,
                start: Math.max(0, w.start - 0.1),
                end: (w.end || (w.start + 0.001)) + 0.8,
            });
        }
        return out;
    }

    var twemojiCache = {}; // cp → File
    async function fetchEmojiPng(cp){
        if (twemojiCache[cp]) return twemojiCache[cp];
        var proxyUrl = '/cdn/jsdelivr/gh/twitter/twemoji@14.0.2/assets/72x72/' + cp + '.png';
        var directUrl = 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/72x72/' + cp + '.png';
        var lastErr = null;
        for (var url of [proxyUrl, directUrl]){
            try {
                var r = await fetch(url, { cache: 'force-cache' });
                if (!r.ok) throw new Error('HTTP ' + r.status);
                var blob = await r.blob();
                var f = new File([blob], cp + '.png', { type: 'image/png' });
                twemojiCache[cp] = f;
                return f;
            } catch(e){ lastErr = e; }
        }
        throw new Error('twemoji fetch failed for ' + cp + ': ' + (lastErr && lastErr.message));
    }

    async function burnWithEmojiOverlay(events, ass, position, videoFile, exporter){
        // Dedup by codepoint — same emoji reused gets loaded once
        var uniq = {};
        events.forEach(function(e){ uniq[e.cp] = true; });
        var cps = Object.keys(uniq);
        var files = [videoFile];
        var cpToIndex = {};
        for (var i = 0; i < cps.length; i++){
            try {
                var f = await fetchEmojiPng(cps[i]);
                cpToIndex[cps[i]] = files.length;  // 1-based in FFmpeg input streams
                files.push(f);
            } catch(e){
                console.warn('[auto-captions] skip emoji', cps[i], (e && e.message) || e);
            }
        }

        // Filter graph: scale each emoji once, then chain overlays by time window.
        // Emoji sits above the caption line (not overlapping text). Its width scales
        // with video width so 1080p videos get a proportionally larger sticker than 360p.
        //   - position=bottom  → emoji ~58% of frame height (captions are ~75%)
        //   - position=center  → emoji ~28% (captions at ~40%)
        //   - position=top     → emoji below caption at ~24%
        var y = position === 'top' ? 'main_h*0.24' : (position === 'center' ? 'main_h*0.28' : 'main_h*0.58');
        // Emoji width ~10% of frame width (but min 64 px for readability on phones),
        // centred horizontally. scale=-2:-2 keeps square aspect since PNG is 72x72.
        var filter = '';
        for (var k2 = 0; k2 < cps.length; k2++){
            if (cpToIndex[cps[k2]] == null) continue;
            var ii = cpToIndex[cps[k2]];
            // Fixed 80 px square — proportional at 360p (22%), 720p (11%), 1080p (7%).
            // scale expressions can't reference main_w here, so we pick a size that
            // reads well across mobile and desktop without being obnoxious.
            filter += '[' + ii + ':v]scale=80:80:flags=lanczos[e' + ii + '];';
        }
        var chainIn = '[0:v]';
        var chainCounter = 0;
        var chunks = [];
        events.forEach(function(ev, evIdx){
            if (cpToIndex[ev.cp] == null) return;
            var label = evIdx < events.length - 1 ? '[v' + (++chainCounter) + ']' : '[vemo]';
            chunks.push(chainIn + '[e' + cpToIndex[ev.cp] + ']overlay=x=(main_w-overlay_w)/2:y=' + y + ':enable=\'between(t,' + ev.start.toFixed(3) + ',' + ev.end.toFixed(3) + ')\'' + label);
            chainIn = label;
        });
        filter += chunks.join(';');
        // Finally: render subtitles on top of emoji-overlaid video
        filter += (chunks.length ? ';' : '') + chainIn + 'subtitles=subs.ass:fontsdir=.[vout]';
        // If no emoji actually loaded, simplify:
        if (!chunks.length) {
            filter = '[0:v]subtitles=subs.ass:fontsdir=.[vout]';
        }

        /* Use VideoExporter to source the codec / quality / output args so that
           emoji burns honor the user's format + quality selection (mp4/mkv,
           high/medium/low) just like the regular burn path. The filter_complex
           graph and -map directives stay tool-specific because they reference
           the multi-input stream layout that only this path knows about. */
        var args, outputName;
        if (exporter) {
            var plan = exporter.plan({
                extraFilterComplex: filter,
                extraOutputMap:     ['[vout]', '0:a?'],
                audioCopy:          true,
                outputBase:         baseName(videoFile.name) + '-captioned',
            });
            args = plan.args;
            outputName = plan.outputName;
        } else {
            /* Fallback for older deployments without VideoExporter loaded. */
            args = [
                '-filter_complex', filter,
                '-map', '[vout]',
                '-map', '0:a?',
                '-c:a', 'copy',
                '-c:v', 'libx264',
                '-preset', 'ultrafast',
                '-crf', '23',
                '-pix_fmt', 'yuv420p',
            ];
            outputName = baseName(videoFile.name) + '-captioned.mp4';
        }
        return await ffStreamMulti(files, args, outputName);
    }

    /* Embed all premium TTFs into FFmpeg FS at /fonts/ so libass can resolve them via fontsdir.
       Language-aware: always load core Latin/Cyrillic fonts; load CJK/Arabic only if
       the caption text contains those scripts (saves ~8MB network per burn).

       Font paths match download-fonts.sh + VPS rsync. In dev they must be present locally. */
    var fontsEmbedded = false;
    var embeddedCore = false, embeddedCJK = false, embeddedArabic = false, embeddedCyrillic = false;

    function scriptsInText(text){
        return {
            cjk: /[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uac00-\ud7af\uff00-\uffef]/.test(text),
            arabic: /[\u0600-\u06ff\u0750-\u077f\ufb50-\ufdff\ufe70-\ufeff]/.test(text),
            cyrillic: /[\u0400-\u04ff\u0500-\u052f]/.test(text),
        };
    }

    async function fetchAndWrite(url, fsPath){
        try {
            var r = await fetch(url, { cache: 'force-cache' });
            if (!r.ok) { console.warn('[auto-captions] font HTTP ' + r.status + ': ' + url); return false; }
            var ab = await r.arrayBuffer();
            await window.FFmpegProcessor.writeFile(fsPath, new Uint8Array(ab));
            return true;
        } catch(e){
            console.warn('[auto-captions] font skipped: ' + url + ' — ' + ((e && e.message) || e));
            return false;
        }
    }

    async function ensureFontsInFfmpeg(text){
        var scripts = scriptsInText(text || '');
        // Fonts are written flat into MEMFS root (no mkdir needed).
        // libass is told via `fontsdir=.` that the current directory holds them.
        if (!embeddedCore) {
            // Curated picker faces only. Roboto/Montserrat/Poppins were pruned
            // from the picker (visually redundant with Inter) so they are no
            // longer fetched. Inter-Bold + Oswald-Bold are re-shipped with
            // Cyrillic, so they cover RU captions natively.
            await fetchAndWrite('/vendor/fonts/Inter-Bold.ttf',           'Inter-Bold.ttf');
            await fetchAndWrite('/vendor/fonts/Oswald-Bold.ttf',          'Oswald-Bold.ttf');
            await fetchAndWrite('/vendor/fonts/BebasNeue-Regular.ttf',    'BebasNeue-Regular.ttf');
            await fetchAndWrite('/vendor/fonts/Anton-Regular.ttf',        'Anton-Regular.ttf');
            await fetchAndWrite('/vendor/fonts/PermanentMarker-Regular.ttf', 'PermanentMarker-Regular.ttf');
            await fetchAndWrite('/vendor/fonts/NotoSans-Bold.ttf',        'NotoSans-Bold.ttf');
            embeddedCore = true;
        }
        if (scripts.cyrillic && !embeddedCyrillic) {
            // Cyrillic-capable display faces offered on Cyrillic locales —
            // only fetched when the captions actually contain Cyrillic.
            await fetchAndWrite('/vendor/fonts/RussoOne-Regular.ttf', 'RussoOne-Regular.ttf');
            await fetchAndWrite('/vendor/fonts/Rubik-Black.ttf',      'Rubik-Black.ttf');
            await fetchAndWrite('/vendor/fonts/Caveat-Bold.ttf',      'Caveat-Bold.ttf');
            embeddedCyrillic = true;
        }
        if (scripts.cjk && !embeddedCJK) {
            await fetchAndWrite('/vendor/fonts/NotoSansJP-Bold.ttf', 'NotoSansJP-Bold.ttf');
            await fetchAndWrite('/vendor/fonts/NotoSansKR-Bold.ttf', 'NotoSansKR-Bold.ttf');
            await fetchAndWrite('/vendor/fonts/NotoSansSC-Bold.ttf', 'NotoSansSC-Bold.ttf');
            embeddedCJK = true;
        }
        if (scripts.arabic && !embeddedArabic) {
            await fetchAndWrite('/vendor/fonts/NotoSansArabic-Bold.ttf', 'NotoSansArabic-Bold.ttf');
            embeddedArabic = true;
        }
        fontsEmbedded = true;
    }

    function baseName(name){
        var i = (name || 'video').lastIndexOf('.');
        return i > 0 ? name.substring(0, i) : (name || 'video');
    }

    /* Потолок прежнего лейна прожига — то число, до которого его пускала зона
       с кидом 'video' (устройство решает, layout — __effectiveFileLimit), и
       никогда не выше стены MEMFS: выход x264 живёт в памяти воркера. Та же
       формула, что SoundtrackSplice.classicCeiling() у соседей цензора. */
    function ffBurnCeiling(){
        var f = window.__effectiveFileLimit;
        var dev = typeof f === 'function' ? +f('video') : 0;
        var wall = +window.__WASM_OUTPUT_MAX || 1600 * 1048576;
        return Math.min(wall, dev > 0 ? dev : 1073741824);
    }
    function fmtBytesShort(n){
        var mb = n / 1048576;
        return mb >= 1000 ? (Math.round(mb / 102.4) / 10) + ' ' + (I18N.unit_gb || 'GB')
                          : Math.round(mb) + ' ' + (I18N.unit_mb || 'MB');
    }
    /* Имена включённых настроек, которые умеет только FFmpeg-лейн, — теми же
       словами, что на их переключателях. */
    function burnFeatureNames(flags){
        var map = {
            hasEmoji: I18N.emoji_title, brandKit: I18N.brand_title, faceAvoid: I18N.face_avoid_label,
            reframe: I18N.auto_reframe_title, autoZoom: I18N.auto_zoom_title, magicCut: I18N.burn_feat_magic_cut,
            ebtCuts: I18N.burn_feat_word_cuts, muteWord: I18N.burn_feat_mute, translateKeep: I18N.translate_keep_original,
        };
        var out = [];
        Object.keys(map).forEach(function(k){ if (flags && flags[k] && map[k]) out.push(map[k]); });
        return out;
    }

    /* Build the filename stem for caption / transcript exports.
       Adds `-captions` (or the opts.stem you pass) plus the target-language
       suffix when the user has translated, so transcribing the same source
       in 5 languages produces 5 distinct files instead of overwriting on
       download. Burn-in MP4 already uses `-captioned` via its own path. */
    function downloadBaseName(opts){
        opts = opts || {};
        var stem = opts.stem || 'captions';
        /* Имя восстановленной работы принадлежит ИСХОДНОМУ файлу, даже когда
           самого файла на странице уже нет: иначе человек, вернувшийся за
           субтитрами к «лекции», получает «captions-captions.srt». */
        var base = state.file ? baseName(state.file.name)
                 : (state.ckptFileName ? baseName(state.ckptFileName) : stem);
        var suffix = '-' + stem;
        if (state.translateOn && state.translateTarget) {
            suffix += '-' + state.translateTarget;
        }
        return base + suffix;
    }

    /* ════════════════════════════════════════════════════════════
       PHASE 9 — EXPORT SRT / VTT / ASS
       ════════════════════════════════════════════════════════════ */
    window.__acDownloadSrt = function(){
        if (!state.lines.length) return;
        reportFeature('export', { format: 'srt', lines: state.lines.length });
        var out = '';
        for (var i = 0; i < state.lines.length; i++){
            var ln = state.lines[i];
            var text = ln.words.filter(function(w){ return !w.isEmoji; }).map(function(w){ return w.text; }).join(' ');
            if (state.speakersOn && ln.words[0] && ln.words[0].speaker) text = '[' + ln.words[0].speaker + '] ' + text;
            out += (i + 1) + '\n'
                 + srtTime(ln.start) + ' --> ' + srtTime(ln.end) + '\n'
                 + text + '\n\n';
        }
        downloadText(out, downloadBaseName() + '.srt', 'application/x-subrip');
    };
    /* Субтитры ДЛЯ МОНТАЖА (Premiere Pro, DaVinci Resolve): раскладка как в окне
       «Create captions» Premiere, служебные слова — со словом, мат — звёздочкой.
       Чат 640, 20.10.2026; правила и замеры — public/js/caption-cues.js. Слова — из
       state.words: правки руками и перевод уже в них. */
    window.__acDownloadEditSrt = function(opts){
        if (!state.words || !state.words.length || !window.CaptionCues) return;
        opts = opts || {};
        var lang = (state.translateOn && state.translateTarget) ? state.translateTarget : (state.sourceLang || 'ru');
        lang = String(lang).slice(0, 2).toLowerCase();
        var mask = null;
        if (opts.mask && window.ProfanityDetector) {
            try { mask = window.CaptionCues.maskFor(window.ProfanityDetector.create({ lang: lang }), lang); } catch (e) { mask = null; }
        }
        var cues = window.CaptionCues.build(state.words, {
            layout: opts.layout, maxChars: opts.maxChars, glue: opts.glue !== false, lang: lang,
            mask: mask, noPunct: !!opts.noPunct, noYo: !!opts.noYo
        });
        if (!cues.length) return;
        reportFeature('export', { format: 'srt_edit', layout: opts.layout || 'word', glue: opts.glue !== false ? 1 : 0,
            mask: mask ? 1 : 0, nopunct: opts.noPunct ? 1 : 0, noyo: opts.noYo ? 1 : 0, cues: cues.length, words: state.words.length,
            engine: state.lastEngine || '', lang: lang });
        downloadText(window.CaptionCues.toSrt(cues), downloadBaseName({ stem: 'edit' }) + '.srt', 'application/x-subrip');
    };
    window.__acDownloadVtt = function(){
        if (!state.lines.length) return;
        reportFeature('export', { format: 'vtt', lines: state.lines.length });
        var out = 'WEBVTT\n\n';
        for (var i = 0; i < state.lines.length; i++){
            var ln = state.lines[i];
            var text = ln.words.filter(function(w){ return !w.isEmoji; }).map(function(w){ return w.text; }).join(' ');
            if (state.speakersOn && ln.words[0] && ln.words[0].speaker) text = '<v Speaker ' + ln.words[0].speaker + '>' + text + '</v>';
            out += vttTime(ln.start) + ' --> ' + vttTime(ln.end) + '\n' + text + '\n\n';
        }
        downloadText(out, downloadBaseName() + '.vtt', 'text/vtt');
    };
    /* Build a fully-decorated ASS string with face-aware placement + brand
       watermark applied — shared between standalone-ASS export and the burn
       path. Users who pick .ass specifically (DaVinci/Aegisub/mpv consumers)
       were silently losing these two features which they had explicitly
       enabled in the UI. stripEmoji is the only burn-vs-standalone delta:
       libass color-emoji is unreliable so the burn pipeline stripts them
       and composites Twemoji PNGs on top via FFmpeg overlay; the standalone
       ASS keeps the emoji glyphs so external players can render them. */
    function buildDecoratedAss(opts) {
        opts = opts || {};
        // Phase C-2: assign per-speaker colors before ASS render so each line
        // inherits its speaker's palette color. ass-builder.js consumes the
        // _speakerColor field set by __acSpeakerColors.apply().
        if (state.speakersOn && window.__acSpeakerColors && typeof window.__acSpeakerColors.apply === 'function') {
            try { window.__acSpeakerColors.apply(state.words); } catch(_) {}
        }
        var ass = window.ASSBuilder.build(state.lines, {
            preset: state.preset,
            color: state.color,
            font: state.font,
            size: state.size,
            position: state.position,
            stroke: state.stroke,
            shadow: state.shadow,
            stripEmoji: !!opts.stripEmoji,
            speakers: state.speakersOn,
            keepOriginal: state.translateOn && state.translateKeepOriginal,
            faceSamples: (els.faceAvoidToggle && els.faceAvoidToggle.checked) ? state.faceSamples : null,
            // Размер кадра — чтобы PlayRes повторял его пропорцию (см. ass-builder.headerBlock).
            videoWidth: (els.video && els.video.videoWidth) || 0,
            videoHeight: (els.video && els.video.videoHeight) || 0,
        });
        // The chosen display font may be Latin-only while the captions are
        // Cyrillic / Arabic / CJK — ASSBuilder auto-swaps in a covering Noto
        // face so the burn isn't tofu. Record how often that fires so we can
        // see how many burns the fallback rescues (and whether a user-facing
        // "font swapped for script support" note is worth adding later).
        try {
            var sub = window.ASSBuilder && window.ASSBuilder.lastFontSubstitution;
            if (sub && typeof window.__ae === 'function') {
                window.__ae('ac_font_substituted', { from: sub.from, to: sub.to, preset: state.preset });
            }
        } catch (_) {}
        var brandKit = window.__acBrandKitState || (window.__acBrandKit && window.__acBrandKit.read()) || null;
        var brandPlan = brandKit && window.__acBrandKit ? window.__acBrandKit.planBurnIn(brandKit, '[0:v]') : null;
        if (brandPlan && brandPlan.assDialogue) {
            var endCs = Math.max(1, Math.round((state.duration || 60) * 100));
            var assEnd = (function(cs){
                var h = Math.floor(cs / 360000); cs -= h * 360000;
                var m = Math.floor(cs / 6000);   cs -= m * 6000;
                var s = Math.floor(cs / 100);    var c = cs - s * 100;
                return h + ':' + (m<10?'0':'') + m + ':' + (s<10?'0':'') + s + '.' + (c<10?'0':'') + c;
            })(endCs);
            ass += brandPlan.assDialogue.replace('{{ASS_END}}', assEnd);
        }
        return { ass: ass, brandPlan: brandPlan };
    }

    window.__acDownloadAss = function(){
        if (!state.lines.length || !window.ASSBuilder) return;
        reportFeature('export', { format: 'ass', preset: state.preset, lines: state.lines.length });
        var out = buildDecoratedAss({ stripEmoji: false });
        downloadText(out.ass, downloadBaseName() + '.ass', 'text/x-ssa');
    };
    /* Plain-text transcript export — PROSE, not a column of caption chunks.
       Until 2026-08-19 this wrote one caption line per output line. A caption
       line is ~3 words, so the .txt arrived as a column of fragments and was
       unusable for exactly what people download it for (lecture notes, an
       article, an interview transcript). A paragraph closes on sentence-end
       punctuation but not before ~180 chars, so one short reply does not
       become its own paragraph; a speaker change always starts a new one. */
    window.__acDownloadTxt = function(){
        if (!state.lines.length) return;
        reportFeature('export', { format: 'txt', lines: state.lines.length });
        var paras = [], buf = [], speaker = null;
        for (var i = 0; i < state.lines.length; i++){
            var ln = state.lines[i];
            var text = ln.words.filter(function(w){ return !w.isEmoji; }).map(function(w){ return w.text; }).join(' ').trim();
            if (!text) continue;
            var sp = (state.speakersOn && ln.words[0] && ln.words[0].speaker) ? ln.words[0].speaker : null;
            if (sp && sp !== speaker) {
                if (buf.length) { paras.push(buf.join(' ')); buf = []; }
                speaker = sp;
                text = '[' + sp + '] ' + text;
            }
            buf.push(text);
            if (/[.?!…]["»)]?$/.test(text) && buf.join(' ').length > 180) { paras.push(buf.join(' ')); buf = []; }
        }
        if (buf.length) paras.push(buf.join(' '));
        downloadText(paras.join('\n\n'), downloadBaseName({ stem: 'transcript' }) + '.txt', 'text/plain;charset=utf-8');
    };

    /* ════════════════════════════════════════════════════════════
       SRT / VTT IMPORT — skip AI transcribe, hydrate state from cues
       ════════════════════════════════════════════════════════════ */
    window.__acImportSubtitlesFromText = function(text, fileName){
        if (!window.SubtitleParser) {
            if (window.__toast) window.__toast(I18N.import_subtitles_failed || 'Subtitle parser not loaded', 'error');
            return;
        }
        var cues;
        try { cues = window.SubtitleParser.parse(text || ''); }
        catch(e) { cues = []; }
        if (!cues || !cues.length) {
            if (window.__toast) window.__toast(I18N.import_subtitles_failed || 'Could not parse subtitle file', 'error');
            reportToolError('import_subtitles', new Error('parse_failed_or_empty'));
            return;
        }
        /* Convert cues → state.words by linear time distribution within each cue.
           Mirrors the segment-level Whisper path so the rest of the pipeline
           (editor, ASS builder, burn) sees identical data shape. */
        var words = [];
        for (var i = 0; i < cues.length; i++){
            var c = cues[i];
            var s = +c.start || 0, e = +c.end || (s + 1.5);
            if (e <= s) e = s + Math.max(0.6, (c.text || '').length * 0.06);
            var tokens = String(c.text || '').split(/\s+/).filter(Boolean);
            if (!tokens.length) continue;
            var totalChars = tokens.reduce(function(a, t){ return a + t.length + 1; }, 0) || 1;
            var dur = e - s;
            var cursor = s;
            for (var t = 0; t < tokens.length; t++){
                var share = (tokens[t].length + 1) / totalChars;
                var wEnd = cursor + dur * share;
                words.push({ text: tokens[t], start: cursor, end: Math.min(e, wEnd) });
                cursor = wEnd;
            }
        }
        if (!words.length) {
            if (window.__toast) window.__toast(I18N.import_subtitles_failed || 'No usable cues', 'error');
            return;
        }
        annotateConfidence(words);
        state.words = words;
        state.wordsOriginal = state.words.slice();
        state.segments = []; /* not used after import; lines are rebuilt from words */
        state.cleanSnapshot = null;
        state.sourceLang = state.sourceLang || 'auto';
        rebuildLines();
        /* Reveal cleanup card + export card just like a finished transcribe does. */
        var cleanCard = document.getElementById('ac-card-clean');
        if (cleanCard && cleanCard.classList.contains('ac-card-gated')) {
            cleanCard.classList.remove('ac-card-gated');
            cleanCard.classList.add('ac-card-reveal');
        }
        var exportCard = document.getElementById('ac-card-export');
        if (exportCard && exportCard.classList.contains('ac-card-gated')) {
            exportCard.classList.remove('ac-card-gated');
            exportCard.classList.add('ac-card-reveal');
        }
        if (setExportsEnabled) setExportsEnabled(true);
        reportFeature('import_subtitles', {
            format: window.SubtitleParser.detect(text),
            cues: cues.length,
            words: words.length,
        });
        if (window.__toast) {
            /* One pass, longest name wins — NOT a .replace(':n').replace(':name')
               chain. ':n' is a prefix of ':name', so replacing it first eats the
               head of the other placeholder wherever the translation puts :name
               first. Russian reads "Загружено :n строк из :name" and survives;
               Korean reads ":name에서 :n줄 로드됨" and shipped as
               "42ame에서 :n줄 로드됨" — mangled filename, raw placeholder for the
               count. The locale that broke was the one nobody read. */
            var vals = { name: fileName || 'file', n: cues.length };
            var msg = (I18N.import_subtitles_loaded || 'Loaded :n lines from :name')
                .replace(/:(name|n)\b/g, function (_, key) { return String(vals[key]); });
            window.__toast(msg, 'success');
        }
    };

    /* Reveal the cleanup + export cards and enable exports — shared by the
       finished-transcribe path, subtitle import, and own-text alignment. */
    function revealCaptionCards(){
        var cleanCard = document.getElementById('ac-card-clean');
        if (cleanCard && cleanCard.classList.contains('ac-card-gated')) {
            cleanCard.classList.remove('ac-card-gated');
            cleanCard.classList.add('ac-card-reveal');
        }
        var exportCard = document.getElementById('ac-card-export');
        if (exportCard && exportCard.classList.contains('ac-card-gated')) {
            exportCard.classList.remove('ac-card-gated');
            exportCard.classList.add('ac-card-reveal');
        }
        if (setExportsEnabled) setExportsEnabled(true);
    }

    /* Build the timing skeleton for own-text alignment. Prefer the VAD speech
       timeline (state.vadOffsetMap covers EVERY vocal region across the whole
       clip in original-video time — `{newStart,newEnd,origStart}`, where a
       region spans [origStart, origStart + (newEnd-newStart)]). We sample each
       region into ~0.5s anchor points so longer speech gets proportionally
       more of the user's words and silent/instrumental gaps stay empty. Falls
       back to the Whisper word timeline (state.words) when VAD is unavailable. */
    function buildAlignAnchors(){
        var map = state.vadOffsetMap;
        if (map && map.length) {
            var anchors = [], STEP = 0.5;
            for (var i = 0; i < map.length; i++) {
                var oStart = +map[i].origStart || 0;
                var dur = Math.max(0.1, (+map[i].newEnd || 0) - (+map[i].newStart || 0));
                var n = Math.max(1, Math.round(dur / STEP));
                for (var k = 0; k < n; k++) {
                    anchors.push({ start: oStart + (k / n) * dur, end: oStart + ((k + 1) / n) * dur });
                }
            }
            if (anchors.length) return anchors;
        }
        return state.words || [];
    }

    /* Replace the transcript with the user's own text, time-aligned to the
       audio's vocal-activity skeleton (VAD speech regions, or the Whisper word
       timeline as a fallback). Used for songs/scripts where ASR mis-hears the
       words but the timing is usable. Sanitizes the result so durations stay
       watchable. */
    function applyOwnTextAlignment(text){
        if (!window.__acAlign || typeof window.__acAlign.alignToAnchors !== 'function') {
            if (window.__toast) window.__toast(I18N.own_text_failed || 'Alignment module not loaded', 'error');
            return false;
        }
        var anchors = buildAlignAnchors();
        var aligned = window.__acAlign.alignToAnchors(text, anchors, state.duration);
        if (!aligned || !aligned.length) {
            if (window.__toast) window.__toast(I18N.own_text_empty || 'Enter some text first', 'warning');
            return false;
        }
        if (window.__acTimings && typeof window.__acTimings.sanitize === 'function') {
            try { var sz = window.__acTimings.sanitize(aligned, state.duration); if (sz && sz.changed) aligned = sz.words; } catch (_) {}
        }
        annotateConfidence(aligned);
        state.words = aligned;
        state.wordsOriginal = state.words.slice();
        state.segments = [];
        state.cleanSnapshot = null;
        rebuildLines();
        revealCaptionCards();
        reportFeature('own_text_aligned', { words: aligned.length, anchors: (anchors || []).length });
        if (window.__toast) window.__toast(I18N.own_text_aligned_ok || 'Your text was aligned to the audio', 'success');
        return true;
    }

    /* PUBLIC: align the user's own text to the audio. If we already have a
       transcription (timing anchors), align immediately. Otherwise kick off a
       transcribe FOR TIMING ONLY and align once it finishes (see the pending-
       align hook in __acTranscribe). */
    window.__acAlignOwnText = function(text){
        var t = String(text || '').trim();
        if (!t) { if (window.__toast) window.__toast(I18N.own_text_empty || 'Enter some text first', 'warning'); return; }
        if (!state.file) { if (window.__toast) window.__toast(I18N.own_text_need_file || 'Upload your video or audio first', 'warning'); return; }
        if (state.words && state.words.length) { applyOwnTextAlignment(t); return; }
        window.__acPendingAlign = t;
        if (window.__toast) window.__toast(I18N.own_text_need_timing || 'Recognising the audio for timing, then placing your text…', 'info');
        if (typeof window.__acTranscribe === 'function') window.__acTranscribe();
        else { var btn = document.getElementById('ac-transcribe-btn'); if (btn) btn.click(); }
    };

    /* PUBLIC: use the user's own text AS-IS (even timing, no audio alignment).
       Routes through the subtitle importer, which already handles plain text. */
    window.__acUseOwnTextRaw = function(text){
        var t = String(text || '').trim();
        if (!t) { if (window.__toast) window.__toast(I18N.own_text_empty || 'Enter some text first', 'warning'); return; }
        window.__acImportSubtitlesFromText(t, 'text');
    };

    /* Hand off current captions to /video-subtitles for line-by-line fine-tuning. */
    window.__acHandoffToVideoSubtitles = function(){
        if (!state.lines.length) return;
        var srt = '';
        for (var i = 0; i < state.lines.length; i++){
            var ln = state.lines[i];
            var text = ln.words.filter(function(w){ return !w.isEmoji; }).map(function(w){ return w.text; }).join(' ').trim();
            if (state.speakersOn && ln.words[0] && ln.words[0].speaker) text = '[' + ln.words[0].speaker + '] ' + text;
            srt += (i + 1) + '\n' + srtTime(ln.start) + ' --> ' + srtTime(ln.end) + '\n' + text + '\n\n';
        }
        try {
            sessionStorage.setItem('__handoff_ac_to_vs_v1', JSON.stringify({
                srt: srt,
                videoName: state.file ? state.file.name : null,
                createdAt: Date.now(),
            }));
        } catch(e) { /* quota or disabled — bridge just navigates without carry */ }
        reportFeature('bridge_to_vs', { lines: state.lines.length, hasVideo: !!state.file });
    };

    /* ════════════════════════════════════════════════════════════
       RETIME LINE — adjust the first word's start time so the line
       appears earlier / later. Line END is preserved (we shrink/grow
       the leading word). Clamps:
         - lower:  previous line's end (no overlap with prior)
         - upper:  the next word's start minus 0.05 s (keep first word visible)
       This semantic lets nudge work even when lines abut each other,
       unlike a whole-line shift which would clamp to zero delta.
       ════════════════════════════════════════════════════════════ */
    window.__acRetimeLine = function(lineIdx, newStart){
        if (!state.lines || !state.lines[lineIdx]) return false;
        var line = state.lines[lineIdx];
        if (!line.words || !line.words.length) return false;
        var prev = state.lines[lineIdx - 1] || null;
        var firstWord = line.words[0];
        var minStart = prev ? prev.end : 0;
        /* Upper bound: don't let the first word start so late it disappears.
           Use the NEXT word's start (or the first word's own end) - 0.05 s. */
        var nextWordStart = line.words[1] ? line.words[1].start : firstWord.end;
        var maxStart = nextWordStart - 0.05;
        if (newStart < 0) newStart = 0;
        if (newStart < minStart) newStart = minStart;
        if (newStart > maxStart) newStart = maxStart;
        var delta = newStart - firstWord.start;
        if (Math.abs(delta) < 0.001) return false;
        firstWord.start = newStart;
        if (typeof window.__acRebuildLines === 'function') window.__acRebuildLines();
        reportFeature('retime_line', { line: lineIdx, delta: +delta.toFixed(3), newStart: +newStart.toFixed(3) });
        return true;
    };

    /* ════════════════════════════════════════════════════════════
       FIND-AND-REPLACE — operates on state.words (source of truth)
       so changes survive rebuildLines / preset switches / export
       ════════════════════════════════════════════════════════════ */
    window.__acReplaceInWords = function(query, replacement, all){
        if (!state.words || !state.words.length) return 0;
        if (!query) return 0;
        var qLow = String(query).toLowerCase();
        var repl = String(replacement == null ? '' : replacement);
        var qLen = query.length;
        var replaced = 0;
        for (var i = 0; i < state.words.length; i++){
            var w = state.words[i];
            if (!w || !w.text) continue;
            var txt = w.text;
            var low = txt.toLowerCase();
            var idx = low.indexOf(qLow);
            if (idx === -1) continue;
            if (!all) {
                w.text = txt.slice(0, idx) + repl + txt.slice(idx + qLen);
                replaced = 1;
                break;
            }
            var out = '', cursor = 0;
            while (true) {
                var pos = low.indexOf(qLow, cursor);
                if (pos === -1) { out += txt.slice(cursor); break; }
                out += txt.slice(cursor, pos) + repl;
                cursor = pos + qLen;
                replaced++;
            }
            w.text = out;
        }
        if (replaced > 0) {
            state.words = state.words.filter(function(w){ return w.text && w.text.trim().length > 0; });
            state.wordsOriginal = state.words.slice();
        }
        return replaced;
    };

    function srtTime(t){
        if (!isFinite(t)) t = 0;
        var total = Math.max(0, Math.round(t * 1000));   // whole ms first: 3.9996 s is 00:00:04.000, not 00:00:03.1000
        var h = Math.floor(total / 3600000), m = Math.floor(total % 3600000 / 60000), s = Math.floor(total % 60000 / 1000), ms = total % 1000;
        return pad(h, 2) + ':' + pad(m, 2) + ':' + pad(s, 2) + ',' + pad(ms, 3);
    }
    function vttTime(t){
        if (!isFinite(t)) t = 0;
        var total = Math.max(0, Math.round(t * 1000));   // whole ms first: 3.9996 s is 00:00:04.000, not 00:00:03.1000
        var h = Math.floor(total / 3600000), m = Math.floor(total % 3600000 / 60000), s = Math.floor(total % 60000 / 1000), ms = total % 1000;
        return pad(h, 2) + ':' + pad(m, 2) + ':' + pad(s, 2) + '.' + pad(ms, 3);
    }
    function pad(n, w){ var s = String(n); while (s.length < w) s = '0' + s; return s; }

    /* Через общую дверь `__downloadFile`: голый <a download> в WebView Android не
       скачивает ничего и выдачу не считает (CLAUDE.md, «File download»). */
    function downloadText(text, name, mime){
        var blob = new Blob([text], { type: mime || 'text/plain' });
        if (typeof window.__downloadFile === 'function') {
            Promise.resolve(window.__downloadFile(blob, name)).then(function(ok){
                if (ok !== false) markCheckpointDelivered();   // субтитры у человека — баннер своё отработал
            }, function(){});
            return;
        }
        markCheckpointDelivered();
        var a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = name;
        document.body.appendChild(a);
        a.click();
        setTimeout(function(){ URL.revokeObjectURL(a.href); a.remove(); }, 500);
    }

    /* ════════════════════════════════════════════════════════════
       UI HELPERS
       ════════════════════════════════════════════════════════════ */
    function showProgress(wrapEl){ if (wrapEl) wrapEl.style.display = 'block'; }
    function hideProgress(wrapEl){ if (wrapEl) wrapEl.style.display = 'none'; }
    /* Hide preview + clear ONLY the text container — never .textContent='' on
       the parent, that wipes the static label and breaks the next transcribe. */
    function clearStreamPreview(){
        if (!els.streamPreview) return;
        els.streamPreview.style.display = 'none';
        var t = els.streamPreviewText || document.getElementById('ac-stream-text');
        if (t) t.textContent = '';
    }
    window.__acClearStreamPreview = clearStreamPreview;
    /* ── Honest progress while the model is still downloading ──────────────
       Measured on an emulated phone (4x CPU, 4G, first visit, 2026-07-24):

         t=3s    "Извлечение звука"      8%   ← and there it sat for 56s
         t=59s   "Поиск участков речи"  16%   ← another 32s
         t=91s   "Загрузка AI-модели"   52%
         t=134s  done

       Audio extraction is not what takes 56 seconds — the ~39 MB of model
       weights are streaming in the background (preload starts when the upload
       zone is touched). The bar was reporting the pipeline STAGE while the real
       long pole was invisible, so the user watched 8% for a minute under a
       label that should take seconds. Prod telemetry says people leave between
       60 and 180 seconds; this is what they are staring at.

       Fix: while weights are still arriving, the early stages hand the bar to
       the download, which has real percentages. No new copy — the existing
       "downloading model" label just gains its own number. */
    function acProgress(){ return window.__acProgress || null; }
    function modelDownloadOwnsBar(){
        var P = acProgress();
        return !!P && P.ownsBar(state.modelDlPct, !!state.loadDoneAt);
    }
    function noteModelDownload(pct){
        if (typeof pct !== 'number' || !isFinite(pct)) return;
        // Monotonic: a second worker starting its own load must not rewind it.
        if (pct > (state.modelDlPct || 0)) state.modelDlPct = pct;
        // PUSH the repaint. The first attempt at this fix only redirected
        // stageProg() and changed nothing, because during the frozen stretch
        // nothing calls it: audio extraction is itself waiting on ffmpeg's wasm
        // to arrive over the same connection, so no stage callback fires for a
        // minute and a half. The download is the only thing making progress, so
        // it has to drive the bar itself.
        if (!state.runStartedAt || state.loadDoneAt) return;   // no run, or decoding already
        paintModelDownload();
    }
    function paintModelDownload(){
        if (!els || !els.tFill) return;
        stageProg(0, I18N.transcribe_downloading, 0);   // decide() overrides with the real %
    }
    /* Early stages (extract / denoise / VAD) call this instead of setProg. The
       decision — stage vs download — lives in auto-captions-progress.js so it
       can be unit-tested without a browser. */
    function stageProg(frac, label, pct){
        var P = acProgress();
        var out = P
            ? P.decide({ frac: frac, label: label, pct: pct },
                       { modelDlPct: state.modelDlPct, loadDone: !!state.loadDoneAt,
                         downloadingLabel: I18N.transcribe_downloading })
            : { frac: frac, label: label, pct: pct };
        setProg(els.tFill, els.tText, els.tPct, out.frac, out.label, out.pct);
    }

    function setProg(fillEl, textEl, pctEl, frac, label, pct){
        if (fillEl) fillEl.style.width = Math.max(0, Math.min(100, frac * 100)).toFixed(1) + '%';
        // Phase D per-stage ETA — append " · ~Xs left" suffix when ETA module
        // is loaded and we have enough progress to project. ETA key derived
        // from the label so transcribe/burn each get their own running stat.
        var labelOut = label;
        try {
            if (label != null && window.__acETA && typeof window.__acETA.update === 'function') {
                var etaKey = String(label).slice(0, 24);
                var etaStr = window.__acETA.update(etaKey, typeof frac === 'number' ? frac : 0);
                if (etaStr) {
                    // __acETA.update() already returns the fully-formatted, localized
                    // suffix via applySuffix() (e.g. " · осталось ~33s"). Append it
                    // as-is — re-wrapping it in eta_label_suffix a second time
                    // double-printed the phrase ("осталось ~ · осталось ~33s").
                    labelOut = String(label) + etaStr;
                }
            }
        } catch (_) {}
        if (textEl && labelOut != null) textEl.textContent = labelOut;
        if (pctEl && pct != null) pctEl.textContent = Math.round(pct) + '%';
        // Часы считают, когда ЭКРАН последний раз изменился, а не когда сюда
        // последний раз зашли: setProg зовут и с теми же значениями, и такой
        // «пульс» сбрасывал таймер, пока пользователь смотрел в застывшую
        // картинку. Подпись = то, что человек реально видит.
        if (textEl) {
            var sig = String(labelOut == null ? textEl.textContent : labelOut)
                    + '|' + (pct == null ? '' : Math.round(pct));
            if (sig !== _clock.sig) { _clock.sig = sig; _clock.at = Date.now(); }
            _clock.el = textEl;
            _clock.base = labelOut == null ? _clock.base : labelOut;
        }
    }

    /* ── Тикающие часы: доказательство жизни ────────────────────────────────
       Жалоба #1482 «зависает на середине». Замер прода: полоса стоит без
       ЕДИНОГО изменения 11–30 секунд — на файле 12 с это «Распознавание речи
       55%», на файле 270 с «Загрузка модели 76%». Прогресса в эти секунды
       действительно нет: мел-спектрограмма считается по всему файлу до первого
       токена, а загрузка весов не шлёт событий между кусками.

       Врать процентом нельзя, а молчать — это и есть «зависло». Поэтому к
       метке приписывается ПРОШЕДШЕЕ время, раз в секунду. Часы — информация,
       а не украшение: они отвечают ровно на вопрос «оно вообще живое?».

       ⚠️ Именно текст, а не бесконечная анимация: замер 2026-08-23 (CLAUDE.md)
       показал, что вечная `transform`-петля композитится примерно в половине
       загрузок и держит главный поток. Здесь и без того идёт WASM-декод. */
    var _clock = { el: null, base: '', at: 0, sig: '' };
    var _clockTimer = null;
    function mmss(ms){
        var t = Math.max(0, Math.round(ms / 1000));
        return Math.floor(t / 60) + ':' + String(t % 60).padStart(2, '0');
    }
    function startProgClock(){
        if (_clockTimer) return;
        _clockTimer = setInterval(function(){
            if (!state.runStartedAt || !_clock.el) return;
            // Пока что-то меняется само — не мешаем. Приписываем только когда
            // экран замер дольше трёх секунд.
            if (Date.now() - _clock.at < 3000) return;
            try { _clock.el.textContent = _clock.base + ' · ' + mmss(Date.now() - state.runStartedAt); } catch(_) {}
        }, 1000);
    }
    function stopProgClock(){
        if (_clockTimer) { clearInterval(_clockTimer); _clockTimer = null; }
        _clock.el = null; _clock.base = ''; _clock.at = 0; _clock.sig = '';
    }
    function disable(el, flag){ if (el) el.disabled = !!flag; }

    /* ── Transcription progress: smooth bar + self-correcting ETA ──────────
       The worker reports `chunk_done` in coarse ~29 s audio chunks. On WASM
       each chunk takes ~10 s wall, and the old code only bumped the bar on
       chunk_done (~1.9 % per step) with NO ETA — so it sat visibly frozen at
       e.g. 61 % for 10 s at a time. (The __acETA module was wired into setProg
       but never armed via beginStage, and it was fed the GLOBAL bar fraction
       instead of a stage-local one, so it always returned null.)

       Fix: a 300 ms ticker creeps the bar between chunks (paced by the measured
       per-chunk rate) and feeds __acETA a stage-local 0→1 fraction so it emits
       a real "· ~2m 15s left" suffix that decreases smoothly. */
    var transTicker = null, transStartMs = 0, transChunksDone = 0,
        transLastChunkMs = 0, transTotalChunksEst = 0, transSlowHintShown = false;
    // After this much wall-time still recognizing (but PROGRESSING — the stall
    // watchdog only catches a frozen decode, not an agonizingly slow one), show
    // a one-time, non-blocking hint. This is the 4 GB-laptop + music case
    // (valeriitaglin): tiny WASM on a weak device chews through a song for
    // minutes with a scary "~29 min left" ETA. We can't make it fast, but we
    // can tell the user it's normal and offer the faster levers.
    var TRANS_SLOW_HINT_MS = 240000;   // 4 min

    function paintTransProgress(){
        if (!transTotalChunksEst) return;
        var nowMs = Date.now();
        if (!transSlowHintShown && (nowMs - transStartMs) > TRANS_SLOW_HINT_MS) {
            transSlowHintShown = true;
            try { toast(I18N.transcribe_slow_hint || 'Still working — recognizing music or songs on this device is slow. You can keep waiting, or cancel and try a shorter clip, a smaller model, or turn off word-level timing.', 'info'); } catch(_){}
            reportFeature('transcribe_slow_hint', { elapsedSec: Math.round((nowMs - transStartMs)/1000), chunksDone: transChunksDone, model: els.model && els.model.value });
        }
        var base     = Math.min(1, transChunksDone / transTotalChunksEst);
        var nextSlot = Math.min(1, (transChunksDone + 1) / transTotalChunksEst);
        var avgChunkMs = transChunksDone > 0 ? (nowMs - transStartMs) / transChunksDone : 9000;
        var sinceChunk = nowMs - transLastChunkMs;
        // Creep toward the next chunk's slot, capped at 92 % of it so the bar
        // never reaches/overshoots a slot the worker hasn't actually finished.
        var creep = Math.max(0, Math.min(0.92, sinceChunk / Math.max(1, avgChunkMs)));
        var frac  = Math.min(0.999, base + (nextSlot - base) * creep);

        // Honest ETA from COMPLETED chunks (transLastChunkMs), NOT the creep-
        // inflated `frac`. The old update(frac) ballooned "~N min left" upward
        // while a slow chunk was in flight (frac creep-capped, elapsed growing)
        // — the valeriitaglin "осталось ~29 мин" on a 50 s clip that made him
        // quit. updateChunks uses avg-per-finished-chunk × chunks-left and ticks
        // DOWN; before the first chunk finishes it returns null (no scary number,
        // just "Распознавание речи").
        var etaSuffix = '';
        try {
            if (window.__acETA && window.__acETA.updateChunks && transChunksDone > 0) {
                var etaAvg = (transLastChunkMs - transStartMs) / transChunksDone;
                var s = window.__acETA.updateChunks('ac_transcribe', etaAvg,
                    Math.max(0, transTotalChunksEst - transChunksDone), nowMs - transLastChunkMs);
                if (s) etaSuffix = s;
            }
        } catch (_) {}

        var globalFrac = 0.55 + Math.min(1, frac) * 0.37;  // recognize phase owns 0.55..0.92
        if (globalFrac > 0.915) globalFrac = 0.915;        // hold ceiling until result arrives
        var label = (I18N.transcribe_recognizing || 'Recognizing speech') + etaSuffix;
        setProg(els.tFill, els.tText, els.tPct, globalFrac, label, Math.round(globalFrac * 100));
    }

    function startTransProgress(audioSec){
        transStartMs = Date.now();
        transChunksDone = 0;
        transSlowHintShown = false;
        transLastChunkMs = transStartMs;
        // Estimate chunk count from audio seconds. transformers.js advances ~19 s
        // per chunk (chunk_length 29 − 2×stride 5); divide by 18 to slightly
        // OVER-estimate so the bar never pins at the ceiling before the real
        // last chunk, and the ETA stays mildly pessimistic rather than optimistic.
        transTotalChunksEst = Math.max(1, Math.round((audioSec || state.duration || 60) / 18));
        try { if (window.__acETA && window.__acETA.beginStage) window.__acETA.beginStage('ac_transcribe'); } catch (_) {}
        if (transTicker) clearInterval(transTicker);
        transTicker = setInterval(paintTransProgress, 300);
        paintTransProgress();
    }

    function stopTransProgress(){
        if (transTicker){ clearInterval(transTicker); transTicker = null; }
        try { if (window.__acETA && window.__acETA.endStage) window.__acETA.endStage('ac_transcribe'); } catch (_) {}
    }

    /* Centralised busy-state machine.
       phase: null | 'transcribe' | 'translate' | 'burn'
       During transcribe: lock inputs that shape the transcription (lang, model,
       word-level, fast-mode) + the Generate button + everything downstream.
       During translate: lock translate-only inputs + exports (state.words is
       being mutated). Customization (font/color/position/preset) stays free —
       it is render-only and applies immediately to the in-flight preview.
       During burn: lock all run-triggers; let the user keep editing customise
       inputs but the result is the export already in flight. */
    /* Скобка прогона на ВСЕ фазы, а не только на прожиг. До 12.09.2026 её
       открывал один `__toolLimit.begin` в прожиге, поэтому расшифровка —
       самая долгая работа тула (медиана 122 с, максимум 78 мин) — шла для
       общего слоя как «файл выбран, ничего не нажато»: ни записи в леджере
       (значит ни объяснения после смерти вкладки), ни вопроса «точно уйти?»,
       ни честной стадии в уходе. Токен капа при этом не трогаем: платит
       по-прежнему только выданный прожиг. */
    var runBracket = null;
    function setBusy(phase){
        state.busy = phase || null;
        if (!phase) {
            releaseScreenAwake();
            if (runBracket) { try { runBracket(); } catch (_) {} runBracket = null; }
        } else {
            holdScreenAwake(phase);
            if (!runBracket) { try { runBracket = window.__runMark && window.__runMark.begin(); } catch (_) {} }
        }
        // Mirror busy state to the Cancel button visibility (blade exposes
        // window.__acSyncCancel which toggles transcribe-btn vs cancel-btn).
        if (typeof window.__acSyncCancel === 'function') window.__acSyncCancel(phase);
        var isTranscribe = phase === 'transcribe';
        var isTranslate  = phase === 'translate';
        var isBurn       = phase === 'burn';
        var anyPhase     = !!phase;

        // Transcription parameters — useless to change while it's running
        disable(els.lang,        isTranscribe);
        disable(els.model,       isTranscribe);
        disable(els.wordLevel,   isTranscribe);
        disable(els.fastMode,    isTranscribe);

        // The Generate button — disable during ANY phase (re-trigger mid-flight
        // would orphan the worker).
        disable(els.transcribeBtn, anyPhase);

        // Translate inputs — lock during transcribe (no captions yet) and
        // during translate itself (mutation in flight).
        disable(els.translateToggle, isTranscribe || isTranslate);
        disable(els.translateTarget, isTranscribe || isTranslate);
        disable(els.translateKeep,   isTranscribe || isTranslate);

        // Auto-emoji + speakers toggles — render-only effects but they mutate
        // state.words, which would race with translate. Lock only during translate.
        disable(els.emojiToggle,    isTranslate);
        disable(els.speakersToggle, isTranslate);

        // Export buttons — only valid when there are captions and no phase running.
        var canExport = !anyPhase && state.words && state.words.length > 0;
        setExportsEnabled(canExport);

        /* Preset / style race-guard. Clicking a preset chip during transcribe
           kicks the renderer mid-stream and re-orders state.preset writes against
           the editor render driven by the worker callback — visually jarring +
           occasionally drops the active-word color swap. Lock styling controls
           for the duration of any phase that mutates words. */
        var lockStyle = isTranscribe || isTranslate || isBurn;
        var presetButtons = document.querySelectorAll('.ac-preset');
        for (var pi = 0; pi < presetButtons.length; pi++) {
            presetButtons[pi].disabled = lockStyle;
            presetButtons[pi].setAttribute('aria-disabled', lockStyle ? 'true' : 'false');
        }
        /* Also lock the SRT/VTT import button — applying an SRT mid-transcribe
           overwrites state.words just as Whisper is filling them. */
        if (els.importSubsBtn) disable(els.importSubsBtn, anyPhase);
    }

    function reportToolError(stage, err){
        try {
            if (typeof window.__aeToolError === 'function') {
                window.__aeToolError({ stage: stage, reason: stage, message: (err && err.message) || String(err) });
            }
        } catch(e){}
    }
    function reportToolComplete(payload){
        try {
            if (typeof window.__aeToolComplete === 'function') window.__aeToolComplete(payload || {});
        } catch(e){}
    }
    function reportFeature(name, payload){
        try {
            if (typeof window.__aeFeature === 'function') window.__aeFeature(name, payload || {});
        } catch(e){}
    }
    function reportBlocked(reason, payload){
        try {
            if (typeof window.__aeBlocked === 'function') window.__aeBlocked(Object.assign({ reason: reason }, payload || {}));
        } catch(e){}
    }

    /* WORKERFS-first FFmpeg wrappers. Streaming mounts the input File so RAM
       is bounded by codec state, not file size (the legacy process() path
       copies the whole input into the WASM heap — ~2× file size peak, the
       reason this tool used to carry a 500 MB cap). Only MOUNT-layer failures
       fall back to the in-memory path; real FFmpeg errors (codec, FILE_LOST,
       OOM) re-throw so they keep their classification. */
    function isMountErr(e){
        var m = ((e && e.message) || '').toLowerCase();
        return m.indexOf('mount') !== -1 || m.indexOf('workerfs') !== -1;
    }
    async function ffStream(file, args, outName, preInputArgs){
        if (typeof window.FFmpegProcessor.processStreaming !== 'function') {
            return await window.FFmpegProcessor.process(file, args, outName, null, preInputArgs || null);
        }
        try {
            return await window.FFmpegProcessor.processStreaming(file, args, outName, null, preInputArgs || null);
        } catch (e) {
            if (!isMountErr(e)) throw e;
            reportFeature('ffmpeg_streaming_fallback', { op: 'process', reason: ((e && e.message) || '').slice(0, 120) });
            return await window.FFmpegProcessor.process(file, args, outName, null, preInputArgs || null);
        }
    }
    async function ffStreamMulti(files, args, outName){
        if (typeof window.FFmpegProcessor.processMultiStreaming !== 'function') {
            return await window.FFmpegProcessor.processMulti(files, args, outName);
        }
        try {
            return await window.FFmpegProcessor.processMultiStreaming(files, args, outName);
        } catch (e) {
            if (!isMountErr(e)) throw e;
            reportFeature('ffmpeg_streaming_fallback', { op: 'processMulti', reason: ((e && e.message) || '').slice(0, 120) });
            return await window.FFmpegProcessor.processMulti(files, args, outName);
        }
    }


    /* ════════════════════════════════════════════════════════════
       СОХРАНЁННАЯ РАСШИФРОВКА — самая долгая работа переживает вкладку
       ════════════════════════════════════════════════════════════
       ЗАЧЕМ (обращение Vladique, 12.09.2026): «в разделе Субтитры к видео всё
       сделал — и оставил видео с субтитрами создаваться. Включил комп заново —
       и не могу найти итоговую работу». Работа шла в его вкладке и умерла
       вместе с ней; вернуть прожиг нельзя, но главное в нём — не прожиг, а
       расшифровка: медиана 122 с, максимум за 90 дней 78 минут, и до сих пор
       она не сохранялась НИГДЕ. Замер RU за 90 дней: 2236 сессий довели
       расшифровку до конца и 1562 из них (70%) ушли, не унеся ничего.

       Храним в общем `TranscriptCheckpointStore` (IndexedDB), а не своей
       копией: он для того и заведён, и его шапка прямо называет auto-captions
       среди потребителей — потребителем тул за четыре месяца так и не стал.
       Ключ — отпечаток файла, поэтому тот же файл узнаётся при повторном
       выборе; `origin` отделяет нашу работу от диктовки. */
    var CKPT_ORIGIN  = 'auto-captions';
    var CKPT_MAX_AGE = 7 * 24 * 3600 * 1000;
    function ckptStore(){ var S = window.TranscriptCheckpointStore; return (S && S.isSupported && S.isSupported()) ? S : null; }
    function ckptKeyFor(file){ var S = ckptStore(); return (S && file) ? S.fingerprint(file, CKPT_ORIGIN) : ''; }
    function ckptPlainText(words){
        var out = [];
        for (var i = 0; i < words.length; i++) if (!words[i].isEmoji) out.push(words[i].text);
        return out.join(' ');
    }
    /* Слепок слов — ТОЛЬКО поля, которые переживают перезагрузку: в IndexedDB
       нельзя класть живые объекты со ссылками на узлы и буферы. */
    function ckptWords(words){
        var out = [];
        for (var i = 0; i < words.length; i++) {
            var w = words[i], o = { text: w.text, start: +w.start || 0, end: +w.end || 0 };
            if (w.speaker) o.speaker = w.speaker;
            if (w.isEmoji) o.isEmoji = 1;
            if (typeof w.confidence === 'number') o.confidence = w.confidence;
            if (w.emphasis) o.emphasis = w.emphasis;
            out.push(o);
        }
        return out;
    }
    function saveTranscriptCheckpoint(why){
        var S = ckptStore();
        if (!S || !state.file || !state.words || !state.words.length) return;
        var key = ckptKeyFor(state.file);
        if (!key) return;
        state.ckptKey = key;
        try {
            S.save(key, {
                fileName:      state.file.name || '',
                fileSize:      state.file.size || 0,
                audioDuration: state.duration || 0,
                lang:          state.sourceLang || '',
                model:         (els.model && els.model.value) || '',
                words:         ckptWords(state.words),
                fullText:      ckptPlainText(state.words),
                status:        'done',
                origin:        CKPT_ORIGIN,
                startedAt:     Date.now()
            }).then(function(){
                reportFeature('transcript_checkpoint_saved', { why: why || '', words: state.words.length });
            }).catch(function(){});
        } catch (_) {}
    }
    /* Работу унесли. Дальше она остаётся в хранилище (тот же файл завтра не
       придётся распознавать заново), но баннер «у вас осталась работа» для неё
       больше не поднимается: напоминать о том, что человек уже скачал, —
       навязчивость, а не забота. */
    function markCheckpointDelivered(){
        var S = ckptStore(); if (!S) return;
        var key = ckptKeyFor(state.file) || state.ckptKey || '';
        if (!key) return;
        S.get(key).then(function(rec){
            if (!rec || rec.delivered) return;
            rec.delivered = 1;
            S.save(key, rec).catch(function(){});
        }).catch(function(){});
    }
    function discardTranscriptCheckpoint(file){
        var S = ckptStore(), key = ckptKeyFor(file || state.file);
        if (S && key) S.discard(key).catch(function(){});
    }

    function ckptApply(rec){
        if (!rec || !rec.words || !rec.words.length) return false;
        state.words = ckptWords(rec.words);
        state.wordsOriginal = state.words.slice();
        state.segments = [];
        state.cleanSnapshot = null;
        state.sourceLang = rec.lang || state.sourceLang || 'auto';
        state.ckptFileName = rec.fileName || '';
        state.ckptKey = rec.key || state.ckptKey || '';
        state.captionsGenerated = true;
        if (typeof rebuildLines === 'function') rebuildLines();
        revealCaptionCards();
        return true;
    }

    /* «2 часа назад» на языке страницы, без своей таблицы склонений. */
    function ckptAgo(ts){
        var mins = Math.max(1, Math.round((Date.now() - (ts || 0)) / 60000));
        try {
            var rtf = new Intl.RelativeTimeFormat(window.__LOCALE || 'en', { numeric: 'auto' });
            if (mins < 60) return rtf.format(-mins, 'minute');
            if (mins < 60 * 24) return rtf.format(-Math.round(mins / 60), 'hour');
            return rtf.format(-Math.round(mins / 1440), 'day');
        } catch (_) { return mins + ' min'; }
    }
    function ckptFill(tpl, vals){
        return String(tpl || '').replace(/:(name|words|ago)\b/g, function(_, k){ return String(vals[k]); });
    }
    function ckptCard(title, body){
        var box = document.createElement('div');
        box.className = 'ac-recovery';
        var h = document.createElement('div'); h.className = 'ac-recovery-title'; h.textContent = title;
        var p = document.createElement('div'); p.className = 'ac-recovery-body';  p.textContent = body;
        var row = document.createElement('div'); row.className = 'ac-recovery-row';
        box.appendChild(h); box.appendChild(p); box.appendChild(row);
        box.setAttribute('role', 'status');
        box._row = row;
        return box;
    }
    function ckptBtn(row, label, primary, fn){
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'ac-recovery-btn' + (primary ? ' ac-recovery-btn--primary' : '');
        b.textContent = label;
        b.addEventListener('click', fn);
        row.appendChild(b);
        return b;
    }
    function ckptDropCard(){
        var old = document.querySelector('.ac-recovery');
        if (old && old.parentNode) old.parentNode.removeChild(old);
    }

    /* Файл выбран и он ТОТ САМЫЙ: предлагаем подставить готовую работу вместо
       второго распознавания. Молча не подставляем — человек мог прийти именно
       за новой расшифровкой (другая модель, другой язык). */
    window.__acOfferSavedTranscript = function(file){
        var S = ckptStore(); if (!S || !file) return;
        ckptDropCard();               // предложение «до выбора файла» устарело
        var key = S.fingerprint(file, CKPT_ORIGIN);
        S.get(key).then(function(rec){
            if (!rec || !rec.words || !rec.words.length) return;
            if (Date.now() - (rec.updatedAt || 0) > CKPT_MAX_AGE) { S.discard(key).catch(function(){}); return; }
            if (state.file !== file) return;                  // пока читали, файл сменили
            if (state.words && state.words.length) return;    // работа уже на экране
            var host = document.getElementById('ac-card-transcribe') || els.workspace || document.body;
            ckptDropCard();
            var vals = { name: rec.fileName || '', words: (rec.words || []).length, ago: ckptAgo(rec.updatedAt) };
            var box = ckptCard(I18N.recovery_match_title || 'Saved transcript found',
                               ckptFill(I18N.recovery_match_body || ':words words, :ago.', vals));
            ckptBtn(box._row, I18N.recovery_apply || 'Use it', true, function(){
                if (ckptApply(rec)) {
                    reportFeature('transcript_checkpoint_restored', { where: 'file', words: (rec.words || []).length, age_min: Math.round((Date.now() - (rec.updatedAt || 0)) / 60000) });
                    ckptDropCard();
                    toast(I18N.recovery_applied || 'Transcript restored', 'success');
                }
            });
            ckptBtn(box._row, I18N.recovery_again || 'Transcribe again', false, function(){
                reportFeature('transcript_checkpoint_declined', {});
                ckptDropCard();
            });
            host.insertBefore(box, host.firstChild);
            reportFeature('transcript_checkpoint_offered', { where: 'file', words: (rec.words || []).length });
        }).catch(function(){});
    };

    /* Файл ещё не выбран, а сохранённая работа есть. Именно этот экран видит
       человек, который вчера закрыл вкладку: до сих пор он видел пустой тул и
       шёл в поддержку. Субтитры отсюда скачиваются БЕЗ файла — расшифровка
       самодостаточна, видео нужно только для прожига. */
    function ckptPendingBanner(){
        var S = ckptStore(); if (!S) return;
        S.purgeStale(CKPT_MAX_AGE).catch(function(){});
        S.listPending().then(function(items){
            var mine = (items || []).filter(function(i){
                /* `delivered` — работу уже унесли; напоминать о скачанном не надо.
                   Запись при этом остаётся: тот же файл завтра не придётся
                   распознавать заново (см. __acOfferSavedTranscript). */
                return i.origin === CKPT_ORIGIN && i.wordCount > 0 && !i.delivered;
            });
            if (!mine.length || state.file) return;
            mine.sort(function(a, b){ return (b.updatedAt || 0) - (a.updatedAt || 0); });
            var meta = mine[0];
            /* Запись читаем СЕЙЧАС, а не по нажатию: между кликом и выдачей
               файла не должно быть ни одного await — пользовательский жест до
               него не доживает, и общая дверь загрузки честно придерживает
               файл (via:held_gesture). Стоило одного прогона стенда. */
            return S.get(meta.key).then(function(rec){ renderPendingCard(meta, rec); });
        }).catch(function(){});
    }
    function renderPendingCard(meta, rec){
        if (!rec || !rec.words || !rec.words.length || state.file) return;
        var zone = document.querySelector('.tool-uploader');
        if (!zone || !zone.parentNode) return;
        var vals = { name: meta.fileName || '', words: meta.wordCount, ago: ckptAgo(meta.updatedAt) };
        var box = ckptCard(I18N.recovery_title || 'Your transcript was saved',
                           ckptFill(I18N.recovery_body || '«:name» — :words words, :ago.', vals));
        ckptBtn(box._row, I18N.recovery_srt || 'Download SRT', true, function(){
            if (!ckptApply(rec)) return;
            reportFeature('transcript_checkpoint_restored', { where: 'banner_srt', words: rec.words.length });
            if (typeof window.__acDownloadSrt === 'function') window.__acDownloadSrt();
        });
        ckptBtn(box._row, I18N.recovery_pick || 'Choose the file', false, function(){
            var input = zone.querySelector('input[type=file]') || document.querySelector('input[data-uploader-for="' + zone.id + '"]');
            if (input) input.click();
        });
        ckptBtn(box._row, I18N.recovery_discard || 'Dismiss', false, function(){
            var S = ckptStore(); if (S) S.discard(meta.key).catch(function(){});
            reportFeature('transcript_checkpoint_dismissed', {});
            ckptDropCard();
        });
        /* Если общий леджер уже объяснил, ПОЧЕМУ работа оборвалась, наше
           предложение встаёт под его заметкой: сначала причина, потом спасение. */
        var after = zone.nextSibling;
        var lost = zone.parentNode.querySelector('.tu-run-lost');
        if (lost && lost.parentNode === zone.parentNode) after = lost.nextSibling;
        zone.parentNode.insertBefore(box, after);
        reportFeature('transcript_checkpoint_offered', { where: 'banner', words: meta.wordCount });
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ckptPendingBanner);
    else setTimeout(ckptPendingBanner, 0);
    window.__acSaveTranscriptCheckpoint = saveTranscriptCheckpoint;
    window.__acDiscardTranscriptCheckpoint = discardTranscriptCheckpoint;

    // Expose helpers so blade can call them when toggles change etc.
    window.__acReportFeature = reportFeature;
    window.__acReportComplete = reportToolComplete;
    window.__acReportBlocked = reportBlocked;
})();
